/* Iter 258 — Modul Stok Barang. Iter 263 — Split Polosan vs Kain.
   Tab kategori (Polosan · Kain) di atas 6 tab sub: Ringkasan · Daftar · BM · BK · Riwayat · Opname.
   Reuse: axios instance, lucide icons, shared CSS. */
import React, { useEffect, useMemo, useRef, useState, createContext, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import {
  Package, PackageOpen, PlusCircle, MinusCircle, ClipboardList, History, ClipboardCheck,
  Search, AlertTriangle, CheckCircle2, Circle, Boxes, Archive, Layers, Scissors,
  FileUp, Download, X, Check, ChevronDown, Info, ShoppingCart, Minus, Plus, Trash2,
  SlidersHorizontal, Target, User, Calendar, Send
} from "lucide-react";
import * as XLSX from "xlsx";
import { notify } from "./modal_center";
import { SkuMapPanel } from "./sku_map_panel";
import { VariantManageButtons, KainColorManageButton, colorKey as svmColorKey, colorLabel } from "./stock_variant_manage";
import { JahitPosition, showJahitPosition } from "./jahit_position";
import "./jahit_position.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => Number(n||0).toLocaleString("id-ID");
// Kunci gulir halaman belakang (aman untuk Safari iPhone & bertumpuk); posisi gulir dipulihkan saat dibuka kembali.
let _scrollLocks = 0;
let _scrollSaved = null;
export function lockBodyScroll() {
  if (_scrollLocks++ === 0) {
    const b = document.body, y = window.scrollY || window.pageYOffset || 0;
    _scrollSaved = { y, style: b.getAttribute("style"), htmlOs: document.documentElement.style.overscrollBehavior };
    Object.assign(b.style, { position: "fixed", top: `-${y}px`, left: "0", right: "0", width: "100%", overflow: "hidden" });
    document.documentElement.style.overscrollBehavior = "none";
  }
  return () => {
    if (--_scrollLocks > 0 || !_scrollSaved) return;
    const s = _scrollSaved; _scrollSaved = null;
    if (s.style === null) document.body.removeAttribute("style"); else document.body.setAttribute("style", s.style);
    document.documentElement.style.overscrollBehavior = s.htmlOs;
    window.scrollTo(0, s.y);
  };
}

// Iter 271 — deteksi layar sempit untuk switch tabel → daftar dua tingkat.
function useIsNarrow(bp = 640) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(`(max-width: ${bp}px)`).matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(`(max-width: ${bp}px)`);
    const on = (e) => setNarrow(e.matches);
    setNarrow(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", on);
    else mql.addListener(on);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", on);
      else mql.removeListener(on);
    };
  }, [bp]);
  return narrow;
}

// ============================================================
// Iter 386 — Mode Belanja: Context + UI components bersama (reuse Polosan & Kain).
//   - State: shopMode (bool), shopItems ({ [variant_id]: { qty, variant } })
//   - Persisten di sessionStorage utk tahan navigasi dalam sesi
//   - Komponen: ShopControls (per-row input - qty +), ShopRekapPanel (bottom panel)
//   - Tidak mengubah logic stok/mutasi: panel hanya daftar belanja offline.
// ============================================================
const SHOP_CTX = createContext(null);
const SHOP_SS_KEY = "stock_shop_mode_v1";
const SHOP_SEL_SS_KEY = "stock_shop_selected_v1";

function ShopProvider({ children }) {
  const [shopMode, setShopMode] = useState(() => {
    try { return sessionStorage.getItem(SHOP_SS_KEY + "_mode") === "1"; } catch { return false; }
  });
  const [shopItems, setShopItems] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SHOP_SS_KEY) || "{}") || {}; } catch { return {}; }
  });
  // Iter 525 — pilihan item untuk Kirim ke Produksi (Cutting & SPK Jahit).
  // Key = variant.id (string). Value = boolean. Default: tidak dipilih (false).
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SHOP_SEL_SS_KEY) || "{}") || {}; } catch { return {}; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(SHOP_SS_KEY + "_mode", shopMode ? "1" : "0"); } catch {}
  }, [shopMode]);
  // Iter 526 — debounce persist shopItems (hemat JSON.stringify saat ketik cepat).
  useEffect(() => {
    const t = setTimeout(() => {
      try { sessionStorage.setItem(SHOP_SS_KEY, JSON.stringify(shopItems)); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [shopItems]);
  // Iter 526 — debounce persist selected (sama alasan).
  useEffect(() => {
    const t = setTimeout(() => {
      try { sessionStorage.setItem(SHOP_SEL_SS_KEY, JSON.stringify(selected)); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [selected]);
  const setQty = useCallback((variant, qty) => {
    const safe = Math.max(0, Number(qty) || 0);
    setShopItems(prev => {
      const next = { ...prev };
      if (safe <= 0) {
        delete next[variant.id];
      } else {
        next[variant.id] = {
          qty: safe,
          variant: {
            id: variant.id,
            category: variant.category,
            product_name: variant.product_name || null,
            fabric_name: variant.fabric_name || null,
            brand: variant.brand || null,
            color: variant.color || null,
            size: variant.size || null,
            sleeve: variant.sleeve || null,
            unit: variant.unit || (variant.category === "polosan" ? "pcs" : ""),
          },
        };
      }
      return next;
    });
    // Jika qty 0 → hapus juga dari selected.
    if (safe <= 0) setSelected(prev => { const n = {...prev}; delete n[variant.id]; return n; });
  }, []);
  const resetAll = useCallback(() => { setShopItems({}); setSelected({}); }, []);
  const toggleSelect = useCallback((variantId, on) => {
    setSelected(prev => {
      const n = {...prev};
      if (on) n[variantId] = true; else delete n[variantId];
      return n;
    });
  }, []);
  const selectAll = useCallback((variantIds) => {
    setSelected(prev => {
      const n = {...prev};
      for (const id of variantIds) n[id] = true;
      return n;
    });
  }, []);
  const unselectAll = useCallback(() => setSelected({}), []);
  const value = useMemo(() => ({
    shopMode, setShopMode, shopItems, setQty, resetAll,
    selected, toggleSelect, selectAll, unselectAll,
  }), [shopMode, shopItems, setQty, resetAll, selected, toggleSelect, selectAll, unselectAll]);
  return <SHOP_CTX.Provider value={value}>{children}</SHOP_CTX.Provider>;
}

function useShop() { return useContext(SHOP_CTX); }

// Iter 526 — Optimasi performa input Mode Belanja:
// • Local state `localQty` untuk display → responsif instan tanpa delay (termasuk HP).
// • Commit ke context di-debounce 90 ms → cegah N re-render per keystroke.
// • React.memo + custom comparator → cegah re-render saat baris lain berubah.
// • Tombol +/- commit langsung (tanpa debounce) → tetap responsif + rekap akurat.
const ShopControls = React.memo(function ShopControls({ variant, unit }) {
  const ctx = useShop();
  const contextQty = ctx?.shopItems?.[variant.id]?.qty || 0;
  const [localQty, setLocalQty] = useState(contextQty);
  const dirtyRef = useRef(false);
  const commitTimer = useRef(null);

  // Sinkron dari context ke lokal HANYA jika operator tidak sedang mengetik.
  // Mencegah "lompatan" angka ketika context update dari tempat lain (misal Reset).
  useEffect(() => {
    if (!dirtyRef.current) setLocalQty(contextQty);
  }, [contextQty]);

  // Bersihkan timer jika unmount.
  useEffect(() => () => { if (commitTimer.current) clearTimeout(commitTimer.current); }, []);

  if (!ctx?.shopMode) return null;
  const u = unit || variant.unit || (variant.category === "polosan" ? "pcs" : "");

  const commit = (n) => {
    dirtyRef.current = false;
    ctx.setQty(variant, n);
  };
  const commitDebounced = (n) => {
    dirtyRef.current = true;
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => commit(n), 90);
  };
  const dec = () => {
    const n = Math.max(0, (Number(localQty) || 0) - 1);
    setLocalQty(n);
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commit(n);
  };
  const inc = () => {
    const n = (Number(localQty) || 0) + 1;
    setLocalQty(n);
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commit(n);
  };
  const onInput = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    const n = raw === "" ? 0 : Number(raw);
    setLocalQty(n);         // UI update instan
    commitDebounced(n);     // Commit ke context setelah 90 ms idle
  };
  // Flush saat blur agar rekap akurat sebelum operator pindah fokus.
  const onBlur = () => {
    if (dirtyRef.current) {
      if (commitTimer.current) clearTimeout(commitTimer.current);
      commit(Number(localQty) || 0);
    }
  };
  return (
    <div className="shop-ctrl-row" data-testid={`shop-ctrl-${variant.id}`}>
      <span className="shop-ctrl-label">Beli ({u})</span>
      <div className="shop-ctrl-stepper">
        <button type="button" className="shop-ctrl-btn" onClick={dec}
          disabled={(Number(localQty) || 0) <= 0} aria-label="Kurangi" data-testid={`shop-ctrl-dec-${variant.id}`}>
          <Minus size={14}/>
        </button>
        <input type="text" inputMode="numeric" pattern="[0-9]*"
          className="shop-ctrl-input"
          value={localQty === 0 ? "" : localQty}
          placeholder="0"
          onChange={onInput}
          onBlur={onBlur}
          aria-label={`Jumlah beli ${variant.color || variant.size || ""}`}
          data-testid={`shop-ctrl-input-${variant.id}`}/>
        <button type="button" className="shop-ctrl-btn" onClick={inc}
          aria-label="Tambah" data-testid={`shop-ctrl-inc-${variant.id}`}>
          <Plus size={14}/>
        </button>
      </div>
    </div>
  );
}, (prev, next) => {
  // Re-render hanya bila variant.id atau unit berubah.
  // Perubahan qty di baris lain TIDAK memicu re-render baris ini.
  return prev.variant?.id === next.variant?.id && prev.unit === next.unit;
});

function ShopModeToggle() {
  const ctx = useShop();
  if (!ctx) return null;
  const total = Object.keys(ctx.shopItems).length;
  const on = ctx.shopMode;
  return (
    <button type="button"
      className={`shop-toggle-btn ${on ? "shop-toggle-on" : ""}`}
      onClick={() => ctx.setShopMode(!on)}
      data-testid="stock-shop-mode-toggle"
      title={on ? "Nonaktifkan Mode Belanja" : "Aktifkan Mode Belanja"}>
      <ShoppingCart size={14}/>
      <span>Mode Belanja {on ? "ON" : "OFF"}</span>
      {total > 0 && <span className="shop-toggle-count">{total}</span>}
    </button>
  );
}

// Iter 525 — Tombol "Riwayat Kiriman ke Produksi" untuk Rido & Owner.
// Modal menampilkan daftar permintaan yang pernah dikirim + status Sudah Dilihat.
function ShopHistoryButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="shop-toggle-btn"
        onClick={() => setOpen(true)}
        data-testid="shop-history-btn"
        title="Lihat riwayat kiriman ke Produksi (Cutting & SPK Jahit)">
        <History size={14}/>
        <span>Riwayat Kiriman</span>
      </button>
      {open && <ShopHistoryModal onClose={() => setOpen(false)} />}
    </>
  );
}

function ShopHistoryModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/stock/production-requests?limit=200`, { withCredentials: true });
      setRows(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Gagal memuat");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doResend = async (id) => {
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/stock/production-requests/${id}/resend`, {}, { withCredentials: true });
      const d = r.data || {};
      if (d.status === "sent") toast.success("Terkirim ke Jahit.", { duration: 3000 });
      else if (d.status === "not_sent") toast.warning("Masih belum terkirim. Coba lagi nanti.");
      else toast.info(`Status: ${d.status}`);
      await load();
    } catch (e) {
      toast.error("Gagal kirim ulang", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusyId(null);
    }
  };

  const doSync = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/stock/production-requests/${id}/sync-seen`, {}, { withCredentials: true });
      toast.success("Status baca diperbarui.", { duration: 2500 });
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.detail || e?.message || "Gagal");
      toast.error("Gagal sync status", { description: String(msg).slice(0, 200) });
    } finally {
      setBusyId(null);
    }
  };

  const doCancel = async (id) => {
    const reason = window.prompt("Alasan pembatalan? (opsional)", "") ?? null;
    if (reason === null) return; // user cancel the prompt
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/stock/production-requests/${id}/cancel`, { reason }, { withCredentials: true });
      if (r.data?.status === "cancelled") toast.success("Permintaan dibatalkan.", { duration: 3000 });
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.status === "already_processed") {
        toast.error("Tidak bisa dibatalkan — Jahit sudah memproses.");
      } else {
        toast.error("Gagal batalkan", { description: typeof detail === "string" ? detail : (detail?.detail || e?.message) });
      }
    } finally {
      setBusyId(null);
    }
  };

  const statusChip = (s) => {
    const map = {
      sent: { label: "Terkirim", cls: "chip-safe" },
      not_sent: { label: "Belum Terkirim", cls: "chip-amber" },
      conflict: { label: "Konflik", cls: "chip-danger" },
      cancelled: { label: "Dibatalkan", cls: "chip-muted" },
      draft: { label: "Draft", cls: "chip-muted" },
    };
    const m = map[s] || { label: s || "—", cls: "chip-muted" };
    return <span className={`chip ${m.cls}`}>{m.label}</span>;
  };

  const describeItem = (v) => {
    const name = v.category === "kain" ? (v.fabric_name || "Kain") : (v.product_name || "Polosan");
    const extras = [v.color, v.brand, v.sleeve && v.sleeve !== "tidak_berlaku" ? `L ${v.sleeve}` : "", v.size].filter(Boolean);
    return `${name}${extras.length ? " · " + extras.join(" · ") : ""}`;
  };

  return (
    <div className="shop-send-modal-backdrop" data-testid="shop-history-modal"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="shop-send-modal shop-history-modal">
        <div className="shop-send-modal-head">
          <History size={16}/>
          <h3>Riwayat Kiriman ke Produksi</h3>
          <button type="button" className="icon-btn small-btn" onClick={load}
            title="Muat ulang" data-testid="shop-history-reload">
            <SlidersHorizontal size={14}/>
          </button>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Tutup"
            data-testid="shop-history-close">
            <X size={16}/>
          </button>
        </div>
        <div className="shop-send-modal-body shop-history-body">
          {loading && <p className="shop-send-hint">Memuat…</p>}
          {error && <p className="shop-send-hint error">{error}</p>}
          {!loading && !error && rows.length === 0 && (
            <p className="shop-send-hint">Belum ada kiriman tercatat.</p>
          )}
          {!loading && rows.map(row => {
            const isOpen = expandedId === row.id;
            return (
              <article key={row.id} className="shop-history-row" data-testid={`shop-history-row-${row.id}`}>
                <header className="shop-history-row-head" onClick={() => setExpandedId(isOpen ? null : row.id)}>
                  <div className="shop-history-left">
                    <strong>{fmtDT(row.created_at)}</strong>
                    <span className="shop-history-by">oleh {row.requested_by}</span>
                  </div>
                  <div className="shop-history-right">
                    {statusChip(row.status)}
                    <span className="shop-history-count">{row.items_count} varian</span>
                    <ChevronDown size={14} style={{transform: isOpen ? "rotate(180deg)" : ""}}/>
                  </div>
                </header>
                <div className="shop-history-seen">
                  <span className={row.seen_by_cutting_at ? "seen-on" : "seen-off"}>
                    {row.seen_by_cutting_at ? `✓ Cutting dilihat ${fmtDT(row.seen_by_cutting_at)}${row.seen_by_cutting_user ? ` (${row.seen_by_cutting_user})` : ""}` : "○ Cutting belum lihat"}
                  </span>
                  <span className={row.seen_by_spk_at ? "seen-on" : "seen-off"}>
                    {row.seen_by_spk_at ? `✓ SPK Jahit dilihat ${fmtDT(row.seen_by_spk_at)}${row.seen_by_spk_user ? ` (${row.seen_by_spk_user})` : ""}` : "○ SPK Jahit belum lihat"}
                  </span>
                </div>
                {isOpen && (
                  <>
                    {row.note && <p className="shop-history-note">“{row.note}”</p>}
                    <ul className="shop-send-list">
                      {(row.items || []).map(it => {
                        const prog = (row.line_progress || {})[it.line_id] || null;
                        return (
                          <li key={it.line_id} className="shop-history-item-row">
                            <div className="shop-history-item-main">
                              <span className="shop-send-name">{describeItem(it)}</span>
                              <span className="shop-send-qty"><strong>{it.qty}</strong> {it.unit}</span>
                            </div>
                            {prog && <ItemProgressBlock item={it} prog={prog}/>}
                          </li>
                        );
                      })}
                    </ul>
                    {row.line_progress_synced_at && (
                      <p className="muted small" style={{margin: "4px 0 0", fontSize: 11.5}}>
                        Terakhir diperbarui: {fmtDT(row.line_progress_synced_at)} (dari Nota)
                      </p>
                    )}
                    <div className="shop-history-actions">
                      {(row.status === "not_sent" || row.status === "conflict") && (
                        <button type="button" className="soft-btn small-btn"
                          disabled={busyId === row.id}
                          onClick={() => doResend(row.id)}
                          data-testid={`shop-history-resend-${row.id}`}>
                          <Send size={13}/> Kirim Ulang
                        </button>
                      )}
                      {row.status === "sent" && (
                        <button type="button" className="soft-btn small-btn"
                          disabled={busyId === row.id}
                          onClick={() => doSync(row.id)}
                          data-testid={`shop-history-sync-${row.id}`}
                          title="Tarik status Sudah Dilihat & progres terbaru dari Jahit">
                          <SlidersHorizontal size={13}/> Sync Status
                        </button>
                      )}
                      {row.status !== "cancelled" && (
                        <button type="button" className="soft-btn small-btn"
                          disabled={busyId === row.id}
                          onClick={() => doCancel(row.id)}
                          data-testid={`shop-history-cancel-${row.id}`}>
                          <X size={13}/> Batalkan
                        </button>
                      )}
                    </div>
                    <div className="shop-history-meta">
                      <code>source_id: {row.source_id}</code>
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ShopRekapPanel() {
  const ctx = useShop();
  const [open, setOpen] = useState(true);
  // Iter 526 — memo aggregasi agar tidak dihitung ulang di setiap keystroke
  // di baris manapun. Hooks WAJIB dipanggil sebelum early-return.
  const items = useMemo(
    () => Object.values(ctx?.shopItems || {}).filter(e => e && e.qty > 0),
    [ctx?.shopItems],
  );
  // Kelompokkan per bahan (fabric_name untuk Kain, product_name untuk Polosan)
  const groups = useMemo(() => {
    const by = new Map();
    for (const e of items) {
      const v = e.variant;
      const label = v.category === "kain"
        ? (v.fabric_name || "Bahan Kain")
        : (v.product_name || "Produk Polosan");
      if (!by.has(label)) by.set(label, []);
      by.get(label).push(e);
    }
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, arr]) => ({ label, items: arr }));
  }, [items]);
  // Total per satuan (jangan gabung roll + kg + pcs).
  const totalsByUnit = useMemo(() => {
    const map = new Map();
    for (const e of items) {
      const u = (e.variant.unit || "").trim() || "pcs";
      map.set(u, (map.get(u) || 0) + Number(e.qty || 0));
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  if (!ctx?.shopMode) return null;
  if (items.length === 0) return null;

  const describeItem = (v) => {
    if (v.category === "kain") {
      return [v.color, v.brand].filter(Boolean).join(" · ") || "—";
    }
    const sleeveLbl = v.sleeve && v.sleeve !== "tidak_berlaku" ? ` · Lengan ${v.sleeve}` : "";
    const sizeLbl = v.size ? ` · ${v.size}` : "";
    return `${v.color || "—"}${sleeveLbl}${sizeLbl}`;
  };

  const handleReset = () => {
    if (window.confirm("Kosongkan semua jumlah beli?")) ctx.resetAll();
  };
  const handleSave = () => {
    try {
      // Iter 389 — Buat PDF Rekap Belanja. Tidak menyentuh stok/backend/
      //   sessionStorage. jsPDF men-trigger download otomatis via save().
      const doc = new jsPDF({ unit: "mm", format: "a5", orientation: "portrait" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 10;
      let y = 12;

      // Header: nama + judul
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("TAMFARIS", marginX, y);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text("Rekap Belanja", pageW - marginX, y, { align: "right" });
      y += 5;
      doc.setDrawColor(180, 160, 90);
      doc.setLineWidth(0.6);
      doc.line(marginX, y, pageW - marginX, y);
      y += 5;

      // Judul besar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("REKAP BELANJA", marginX, y);
      y += 5;

      // Tanggal & waktu
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const now = new Date();
      const stampStr = now.toLocaleString("id-ID", {
        day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      doc.setTextColor(90);
      doc.text(`Dibuat: ${stampStr}`, marginX, y);
      doc.setTextColor(0);
      y += 6;

      // Util hal baru bila mendekati bawah halaman
      const ensureSpace = (needed = 8) => {
        if (y + needed > pageH - 15) {
          doc.addPage();
          y = 12;
        }
      };

      // Loop groups
      for (const g of groups) {
        ensureSpace(10);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(180, 140, 40);
        doc.text(g.label.toUpperCase(), marginX, y);
        doc.setTextColor(0);
        y += 5;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        for (const e of g.items) {
          ensureSpace(6);
          const name = describeItem(e.variant);
          const unit = e.variant.unit || "pcs";
          const qtyText = `${e.qty} ${unit}`;
          // Nama kiri (wrap bila panjang), qty kanan bold
          const maxNameW = pageW - marginX * 2 - 25;
          const nameLines = doc.splitTextToSize(`• ${name}`, maxNameW);
          doc.setFont("helvetica", "normal");
          doc.text(nameLines, marginX + 2, y);
          doc.setFont("helvetica", "bold");
          doc.text(qtyText, pageW - marginX, y, { align: "right" });
          y += nameLines.length * 4.5;
        }
        // Subtotal per bahan (per satuan di dalam group)
        const subUnits = new Map();
        for (const e of g.items) {
          const u = (e.variant.unit || "pcs").trim() || "pcs";
          subUnits.set(u, (subUnits.get(u) || 0) + Number(e.qty || 0));
        }
        ensureSpace(6);
        const subTxt = [...subUnits.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([u, q]) => `${q} ${u}`).join("  ·  ");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(`Sub-total: ${subTxt}`, pageW - marginX, y, { align: "right" });
        y += 6;
      }

      // Garis + grand total
      ensureSpace(14);
      doc.setDrawColor(140);
      doc.setLineWidth(0.3);
      doc.line(marginX, y, pageW - marginX, y);
      y += 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("TOTAL:", marginX, y);
      const totalTxt = totalsByUnit.map(([u, q]) => `${q} ${u}`).join("   ·   ");
      doc.text(totalTxt.toUpperCase(), pageW - marginX, y, { align: "right" });
      y += 6;

      // Footer kecil
      ensureSpace(6);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text("Stok tidak berubah. Penambahan stok tetap melalui Stok Masuk.",
        marginX, pageH - 10);

      // Simpan dengan nama file beridentifikasi
      const fileStamp = now.toISOString().replace(/[-:T]/g, "").slice(0, 12);
      doc.save(`rekap-belanja-${fileStamp}.pdf`);

      toast.success("PDF Rekap Belanja berhasil dibuat.", { duration: 3000 });
    } catch (err) {
      toast.error("Gagal membuat PDF Rekap Belanja.", {
        description: err?.message || "Coba muat ulang halaman lalu coba lagi.",
        duration: 3500,
      });
    }
  };

  return (
    <div className={`shop-rekap-panel ${open ? "" : "shop-rekap-collapsed"}`} data-testid="shop-rekap-panel">
      <div className="shop-rekap-head">
        <ShoppingCart size={14}/>
        <h4>Rekap Belanja</h4>
        <span className="shop-rekap-badge">{items.length} varian</span>
        <button type="button" className="shop-rekap-toggle"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-label={open ? "Sembunyikan rekap" : "Tampilkan rekap"}
          data-testid="shop-rekap-toggle">
          <ChevronDown size={16} style={{transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform .15s"}}/>
        </button>
      </div>
      {open && <>
        <ShopSelectionControls allIds={items.map(e => e.variant.id)} />
        <div className="shop-rekap-body">
          {groups.map(g => (
            <section key={g.label} className="shop-rekap-group">
              <h5 className="shop-rekap-group-head">{g.label}</h5>
              <ul>
                {g.items.map(e => (
                  <li key={e.variant.id} data-testid={`shop-rekap-item-${e.variant.id}`}>
                    <label className="shop-rekap-check" title="Centang untuk sertakan dalam pengiriman ke Produksi">
                      <input type="checkbox"
                        checked={!!ctx.selected?.[e.variant.id]}
                        onChange={ev => ctx.toggleSelect(e.variant.id, ev.target.checked)}
                        data-testid={`shop-rekap-check-${e.variant.id}`}/>
                    </label>
                    <span className="shop-rekap-item-name">{describeItem(e.variant)}</span>
                    <span className="shop-rekap-item-qty">
                      <strong>{e.qty}</strong> {e.variant.unit || "pcs"}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <div className="shop-rekap-totals" data-testid="shop-rekap-totals">
          <span>TOTAL:</span>
          <span className="shop-rekap-totals-values">
            {totalsByUnit.map(([u, q]) => (
              <strong key={u}>{q} {u}</strong>
            ))}
          </span>
        </div>
        <div className="shop-rekap-actions">
          <button type="button" className="soft-btn small-btn" onClick={handleReset}
            data-testid="shop-rekap-reset">
            <Trash2 size={14}/> Reset
          </button>
          <button type="button" className="soft-btn small-btn" onClick={handleSave}
            data-testid="shop-rekap-save">
            <Check size={14}/> Simpan PDF
          </button>
          <ShopSendToProductionButton items={items} />
        </div>
      </>}
    </div>
  );
}

// ============================================================
// Iter 525 — Kontrol pilihan & kirim ke Produksi (Cutting & SPK Jahit).
// Permintaan dikirim via POST /api/stock/production-requests.
// Payload mengikuti kontrak Jahit yang sudah di-dokumentasi.
// ============================================================
function ShopSelectionControls({ allIds }) {
  const ctx = useShop();
  const total = allIds.length;
  const selectedCount = allIds.filter(id => !!ctx.selected?.[id]).length;
  const allOn = selectedCount === total && total > 0;
  return (
    <div className="shop-sel-controls" data-testid="shop-sel-controls">
      <label className="shop-sel-all">
        <input type="checkbox" checked={allOn}
          onChange={e => e.target.checked ? ctx.selectAll(allIds) : ctx.unselectAll()}
          data-testid="shop-sel-all"/>
        <span>Pilih semua</span>
      </label>
      <span className="shop-sel-count" data-testid="shop-sel-count">
        {selectedCount} / {total} dipilih
      </span>
    </div>
  );
}

function ShopSendToProductionButton({ items }) {
  const ctx = useShop();
  const [showConfirm, setShowConfirm] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const selectedItems = items.filter(e => !!ctx.selected?.[e.variant.id]);
  const disabled = selectedItems.length === 0;

  const describeFull = (v) => {
    if (v.category === "kain") {
      return [v.fabric_name || "Bahan Kain", v.color, v.brand].filter(Boolean).join(" · ");
    }
    const parts = [v.product_name || "Produk Polosan", v.color];
    if (v.sleeve && v.sleeve !== "tidak_berlaku") parts.push(`Lengan ${v.sleeve}`);
    if (v.size) parts.push(v.size);
    return parts.join(" · ");
  };

  const uuidv4 = () => (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const doSend = async () => {
    if (sending || disabled) return;
    setSending(true);
    const payload = {
      note: note.trim(),
      items: selectedItems.map(e => ({
        line_id: uuidv4(),
        category: e.variant.category,
        product_name: e.variant.category === "polosan" ? (e.variant.product_name || "") : null,
        fabric_name: e.variant.category === "kain" ? (e.variant.fabric_name || "") : null,
        color: e.variant.color || "",
        brand: e.variant.brand || null,
        size: e.variant.size || null,
        sleeve: e.variant.sleeve || null,
        qty: Number(e.qty),
        unit: e.variant.unit || (e.variant.category === "polosan" ? "pcs" : "roll"),
        note: "",
      })),
    };
    try {
      const r = await axios.post(`${API}/stock/production-requests`, payload, { withCredentials: true });
      const d = r.data || {};
      if (d.status === "sent") {
        toast.success("Kebutuhan terkirim ke Produksi (Cutting & SPK Jahit).", {
          description: `${selectedItems.length} varian · ID ${d.source_id?.slice(0, 8)}…`,
          duration: 4000,
        });
      } else if (d.status === "not_sent") {
        toast.warning("Tersimpan di Nota tapi belum terkirim ke Jahit.", {
          description: "Buka Riwayat untuk Kirim Ulang bila sudah terhubung.",
          duration: 5000,
        });
      } else if (d.status === "conflict") {
        toast.error("Jahit menolak permintaan (konflik ID).", {
          description: "Hubungi admin TAMFARIS Jahit.",
          duration: 5000,
        });
      } else {
        toast.info("Permintaan tersimpan.", { description: `Status: ${d.status}` });
      }
      setShowConfirm(false);
      setNote("");
      // Hilangkan pilihan setelah kirim (biar tidak dobel kirim)
      ctx.unselectAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Tidak dapat terhubung.";
      toast.error("Gagal kirim ke Produksi", { description: String(msg).slice(0, 200), duration: 5000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" className="primary small-btn"
        disabled={disabled}
        onClick={() => setShowConfirm(true)}
        data-testid="shop-send-production-btn"
        title={disabled ? "Pilih minimal 1 item" : "Kirim pilihan ke Cutting & SPK Jahit"}>
        <Send size={14}/> Kirim ke Produksi {selectedItems.length > 0 && `(${selectedItems.length})`}
      </button>
      {showConfirm && (
        <div className="shop-send-modal-backdrop" data-testid="shop-send-modal"
          onClick={e => { if (e.target === e.currentTarget && !sending) setShowConfirm(false); }}>
          <div className="shop-send-modal">
            <div className="shop-send-modal-head">
              <Send size={16}/>
              <h3>Kirim ke Produksi (Cutting & SPK Jahit)</h3>
              <button type="button" className="icon-btn" disabled={sending}
                onClick={() => setShowConfirm(false)} aria-label="Tutup"
                data-testid="shop-send-close">
                <X size={16}/>
              </button>
            </div>
            <div className="shop-send-modal-body">
              <p className="shop-send-hint">
                {selectedItems.length} varian akan dikirim ke aplikasi TAMFARIS Jahit. Daftar ini bersifat <b>read-only</b> — <b>tidak</b> memotong stok, <b>tidak</b> otomatis membuat SPK atau Hasil Cutting.
              </p>
              <ul className="shop-send-list">
                {selectedItems.map(e => (
                  <li key={e.variant.id} data-testid={`shop-send-row-${e.variant.id}`}>
                    <span className="shop-send-name">{describeFull(e.variant)}</span>
                    <span className="shop-send-qty"><strong>{e.qty}</strong> {e.variant.unit || "pcs"}</span>
                  </li>
                ))}
              </ul>
              <label className="shop-send-note-label">
                Catatan opsional (akan dilihat Cutting & Pembuat SPK)
                <textarea rows={2} value={note} maxLength={300} disabled={sending}
                  placeholder="mis. Prioritas untuk pesanan XYZ"
                  onChange={e => setNote(e.target.value)}
                  data-testid="shop-send-note"/>
              </label>
            </div>
            <div className="shop-send-modal-actions">
              <button type="button" className="soft-btn" disabled={sending}
                onClick={() => setShowConfirm(false)} data-testid="shop-send-cancel">
                Batal
              </button>
              <button type="button" className="primary" disabled={sending}
                onClick={doSend} data-testid="shop-send-confirm">
                {sending ? "Mengirim…" : (<><Send size={14}/> Kirim Sekarang</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


const fmtQty = (n, unit) => {
  const num = Number(n||0);
  const u = (unit || "").toLowerCase();
  const shown = (u === "meter" || u === "kg")
    ? num.toLocaleString("id-ID", { maximumFractionDigits: 3 })
    : Math.round(num).toLocaleString("id-ID");
  return `${shown} ${unit || ""}`.trim();
};
const localISO = () => new Date().toISOString();
const uuid = () => (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
};

const STATUS_LABEL = {
  belum_dihitung: { label: "Belum Dihitung", cls: "chip-muted" },
  habis: { label: "Habis", cls: "chip-danger" },
  menipis: { label: "Menipis", cls: "chip-amber" },
  aman: { label: "Aman", cls: "chip-safe" },
  min_belum_diatur: { label: "Minimum belum diatur", cls: "chip-muted" },
};

// ============================================================
// Iter 392 — Color Swatch Preview untuk Daftar Stok Polosan.
// Map nama warna (bahasa Indonesia) → hex base; simulasi tekstur kain
// pakai CSS gradient (tidak butuh asset). Fallback neutral jika tidak dikenali.
// Pencocokan: UPPER + trim + strip trailing code (digit/digit-huruf), coba
// exact → prefix fuzzy → kata pertama. Satu sumber kebenaran, tidak ubah data.
// ============================================================
const COLOR_SWATCH_MAP = {
  // Monotone
  HITAM: "#121214", PUTIH: "#f3efe3", CREAM: "#e5d6b1", KREM: "#e5d6b1",
  "BROKEN WHITE": "#ebe3d1", "OFF WHITE": "#ebe3d1",
  "ABU TUA": "#44474d", "ABU MUDA": "#b1b6bf", "ABU ARANG": "#2b2d33",
  "ABU MISTY": "#9aa0ac", ABU: "#7d828c", MISTY: "#9aa0ac", GREY: "#7d828c",
  // Merah
  MERAH: "#c22029", "MERAH CABE": "#cf2a2a", "MERAH BATA": "#9b3a2a",
  "MERAH MARUN": "#5c1a1f", MAROON: "#6c1e26", MARUN: "#6c1e26",
  "MERAH HATI": "#4e1217", "MERAH JAMBU": "#e95982", PINK: "#f08dab",
  SALEM: "#eaa391", PEACH: "#f4bfa0",
  // Biru
  BIRU: "#1e5bb8", "BIRU TUA": "#143a73", "BIRU MUDA": "#6ea4e0",
  "BIRU DONGKER": "#17264a", "BIRU ELEKTRIK": "#1a4ed0", "BIRU LAUT": "#1b6a9b",
  "BIRU BENHUR": "#1d4ed8", BENHUR: "#1a4bca", NAVY: "#0f2447",
  DONGKER: "#17264a", "DUSTY BLUE": "#6e86a3", TURKIS: "#2fa093", TOSCA: "#2b9b8f",
  // Hijau
  HIJAU: "#2a7a3a", "HIJAU TUA": "#1e4a2a", "HIJAU MUDA": "#6cb979",
  "HIJAU BOTOL": "#1e5537", "HIJAU TOSCA": "#2b9b8f", "HIJAU DAUN": "#2f8a3b",
  "HIJAU LUMUT": "#4e5a2e", "HIJAU MINT": "#8dd3b5", MINT: "#8dd3b5",
  ARMY: "#5e6f3b", "ARMY GREEN": "#5e6f3b", OLIVE: "#6a6a2d", LUMUT: "#4e5a2e",
  // Kuning / Oranye
  KUNING: "#e4c23b", "KUNING MUDA": "#f0d771", "KUNING KUNYIT": "#d9a018",
  "KUNING MUSTARD": "#c08a12", MUSTARD: "#c08a12",
  ORANGE: "#e67a1f", ORANYE: "#e67a1f", "ORANGE TUA": "#c46118",
  // Earth
  COKLAT: "#6b4a2b", "COKLAT MUDA": "#a07a4e", "COKLAT TUA": "#4a3220",
  "COKLAT SUSU": "#b58867", MOKA: "#73523a", TAN: "#c89a68", KHAKI: "#9b8d5e",
  BEIGE: "#d9c6a2", BONE: "#dcc9a4",
  // Ungu
  UNGU: "#6a3c91", "UNGU TUA": "#4a2466", "UNGU MUDA": "#a98cc9",
  LAVENDER: "#b4a0d9", LILAC: "#c4a7dc",
  // Spesial
  GOLD: "#c8a047", EMAS: "#c8a047", SILVER: "#b8bcc3", PERAK: "#b8bcc3",
};

// Normalisasi nama warna: upper, trim, hilangkan kode trailing (0212, H020, dsb),
// gabungkan spasi berlebih. Tidak mengubah string sumber (hanya untuk lookup).
function normalizeColorName(raw) {
  if (!raw) return "";
  let s = String(raw).toUpperCase().trim();
  s = s.replace(/\s+/g, " ");
  // Buang token kode (digit atau digit-huruf, min 2 char) di belakang/di tengah.
  s = s.split(" ").filter(t => !/^[A-Z]?\d{2,}[A-Z]?\d*$/.test(t)).join(" ").trim();
  return s;
}

// Resolve hex dari nama warna. Return { hex, source: "known" | "fallback" }.
function resolveColorHex(raw) {
  const key = normalizeColorName(raw);
  if (!key) return { hex: "#9aa0ac", source: "fallback" };
  if (COLOR_SWATCH_MAP[key]) return { hex: COLOR_SWATCH_MAP[key], source: "known" };
  // Coba fuzzy: cocokkan kata kunci utama (contoh "MERAH ANGGUR" → "MERAH").
  for (const k of Object.keys(COLOR_SWATCH_MAP)) {
    if (key.startsWith(k + " ") || key.endsWith(" " + k) || key.includes(" " + k + " ")) {
      return { hex: COLOR_SWATCH_MAP[k], source: "known" };
    }
  }
  // Fallback: kata pertama
  const first = key.split(" ")[0];
  if (COLOR_SWATCH_MAP[first]) return { hex: COLOR_SWATCH_MAP[first], source: "known" };
  return { hex: "#9aa0ac", source: "fallback" };
}

function ColorSwatch({ color, hexOverride, size, testid }) {
  // Iter 393 — jika varian punya `color_hex` tersimpan, pakai itu sbg override.
  let hex, source;
  const raw = (hexOverride || "").trim();
  if (/^#?[0-9a-fA-F]{6}$/.test(raw) || /^#?[0-9a-fA-F]{3}$/.test(raw)) {
    const s = raw.replace("#","");
    hex = s.length === 3 ? "#" + s.split("").map(c=>c+c).join("") : "#" + s;
    source = "known";
  } else {
    const r = resolveColorHex(color);
    hex = r.hex; source = r.source;
  }
  const sz = size || 56;
  return <span
    className={`color-swatch ${source === "fallback" ? "is-fallback" : ""}`}
    data-testid={testid}
    aria-hidden="true"
    title={color || "Tanpa warna"}
    style={{ width: sz, height: sz, "--swatch-color": hex }}
  >
    {source === "fallback" && <span className="color-swatch-fallback-mark">?</span>}
  </span>;
}


const SIZE_ORDER = ["XS","S","M","L","XL"];
const sizeRank = (s) => {
  const up = (s||"").toUpperCase().trim();
  const i = SIZE_ORDER.indexOf(up);
  if (i >= 0) return i;
  if (up === "XXL") return 5;
  if (up === "XXXL") return 6;
  const m = up.match(/^(\d+)XL$/);
  if (m) return 3 + parseInt(m[1], 10);  // 2XL=5, 3XL=6, 4XL=7, 5XL=8...
  return 900 + up.charCodeAt(0);  // fallback alphabetical
};

const emptyFilter = () => ({ status: "", q: "", material:"", color:"", size:"", sleeve:"", fabric_name:"", brand:"", unit:"" });
const SLEEVE_LABEL = { pendek: "Pendek", panjang: "Panjang", tidak_berlaku: "Tidak Berlaku" };
export const CORRECT_CTX = createContext(false); // boleh Koreksi Stok Langsung (Owner/Admin/Gudang)

// Koreksi Stok Langsung — memakai mekanisme opname (POST /stock/opname/quick → txn opname_adjust), bukan ubah saldo langsung.
function StockCorrectButton({ variant, onChanged, compact }) {
  const can = useContext(CORRECT_CTX);
  const [open, setOpen] = useState(false);
  const [phys, setPhys] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!can) return null;
  const unit = variant.unit || "pcs";
  const rec = Number(variant.current_qty || 0);
  const delta = phys === "" ? null : Number(phys) - rec;
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/stock/opname/quick`, { variant_id: variant.id, recorded_qty: rec, physical_qty: Number(phys), reason },
        { withCredentials: true });
      toast.success(`Stok dikoreksi: ${fmtQty(r.data.before, unit)} → ${fmtQty(r.data.after, unit)} (${r.data.delta > 0 ? "+" : ""}${fmtQty(r.data.delta, unit)}) · ${r.data.session_number}`);
      setOpen(false); setPhys(""); setReason("");
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Koreksi gagal"); }
    finally { setBusy(false); }
  };
  const invalid = phys === "" || Number.isNaN(Number(phys)) || Number(phys) < 0 || !reason.trim() || Math.abs(delta) < 1e-6;
  return <>
    <button type="button" className={compact ? "stock-koreksi-btn" : "soft-btn"} onClick={() => setOpen(true)} data-testid={`stock-correct-open-${variant.id}`}
      style={compact ? undefined : {padding:"6px 12px", fontSize:13, minHeight:36, marginRight:6, whiteSpace:"nowrap"}}>{compact ? "Koreksi" : "Koreksi Stok"}</button>
    {open && <div className="modal-backdrop" onClick={() => !busy && setOpen(false)}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:420}} data-testid={`stock-correct-modal-${variant.id}`}>
        <h3 style={{margin:"0 0 6px", fontSize:16}}>Koreksi Stok</h3>
        <p className="small" style={{margin:"0 0 8px"}} data-testid="stock-correct-variant">
          <b>{variant.product_name || variant.fabric_name || "—"}</b> · {variant.color || "—"}
          {variant.sleeve ? ` · Lengan ${SLEEVE_LABEL[variant.sleeve] || variant.sleeve}` : ""}{variant.size ? ` · ${variant.size}` : ""}</p>
        <p style={{margin:"0 0 10px"}}>Stok tercatat saat ini: <b data-testid="stock-correct-recorded">{fmtQty(rec, unit)}</b></p>
        <label className="modal-field">Stok fisik sebenarnya ({unit})
          <input type="number" min="0" step="any" inputMode="decimal" value={phys} onChange={e => setPhys(e.target.value)} data-testid="stock-correct-physical" style={{width:"100%", minHeight:44, fontSize:16}}/></label>
        {delta !== null && !Number.isNaN(delta) && <p className="small" style={{margin:"4px 0 8px", fontWeight:700, color: delta >= 0 ? "#166534" : "#b91c1c"}} data-testid="stock-correct-delta">
          Stok sesudah: {fmtQty(Number(phys), unit)} · Selisih: {delta > 0 ? "+" : ""}{fmtQty(delta, unit)}</p>}
        <label className="modal-field">Alasan koreksi (wajib)
          <input value={reason} onChange={e => setReason(e.target.value)} maxLength={300} placeholder="mis. hasil hitung fisik gudang" data-testid="stock-correct-reason" style={{width:"100%", minHeight:44, fontSize:16}}/></label>
        <div style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:12}}>
          <button type="button" className="soft-btn" disabled={busy} onClick={() => setOpen(false)} data-testid="stock-correct-cancel">Batal</button>
          <button type="button" className="primary" disabled={busy || invalid} onClick={save} data-testid="stock-correct-save">{busy ? "Menyimpan…" : "Simpan Koreksi"}</button>
        </div>
      </div>
    </div>}
  </>;
}
const variantLabel = (v) => {
  if (v.category === "kain") {
    return [v.fabric_name, v.brand, v.color, v.unit].filter(Boolean).join(" · ");
  }
  const sleeveLbl = v.sleeve && v.sleeve !== "tidak_berlaku" ? `Lengan ${SLEEVE_LABEL[v.sleeve] || v.sleeve}` : "";
  return [v.product_name, sleeveLbl, v.color, v.size].filter(Boolean).join(" · ");
};

// ============================================================
export function StockModule({ user, operator, onOpenOrder, initialTab }) {
  const role = String(user?.role || "").toLowerCase();
  const team = String(operator?.team || "");
  const isOwner = role === "owner";
  const canMutasi = isOwner || (!!operator && team === "Gudang");
  const canView = isOwner || role === "admin" || canMutasi || !!operator?.is_admin_toko;
  const [category, setCategory] = useState("polosan"); // polosan | kain
  const [tab, setTab] = useState(initialTab || "summary");
  const [summary, setSummary] = useState({ polosan:{}, kain:{} });
  const [variants, setVariants] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState(emptyFilter());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Iter 287ay — Hook navigasi ringkas (harus di atas early return `canView`).
  const [moreOpen, setMoreOpen] = useState(false);
  const [mutasiPage, setMutasiPage] = useState("list"); // "list" | "in" | "out"
  // Reset moreOpen jika user ganti kategori/tab.
  useEffect(() => { setMoreOpen(false); }, [category, tab]);

  const loadAll = async (cat = category) => {
    setLoading(true); setErr("");
    try {
      const [s, v, p] = await Promise.all([
        axios.get(`${API}/stock/summary`),
        axios.get(`${API}/stock/variants`, { params: { include_inactive: false, category: cat } }),
        axios.get(`${API}/master/products`).catch(() => ({data: []})),
      ]);
      setSummary(s.data || {});
      setVariants(v.data || []);
      setProducts(p.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Gagal memuat data stok");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) loadAll(category); /* eslint-disable-next-line */ }, [canView, category]);

  const filtered = useMemo(() => {
    return (variants || []).filter(v => {
      if (filter.status && v.status !== filter.status) return false;
      if (category === "polosan") {
        if (filter.color && v.color !== filter.color) return false;
        if (filter.size && v.size !== filter.size) return false;
        if (filter.sleeve && (v.sleeve || "") !== filter.sleeve) return false;
      } else {
        if (filter.fabric_name && v.fabric_name !== filter.fabric_name) return false;
        if (filter.brand && v.brand !== filter.brand) return false;
        if (filter.color && v.color !== filter.color) return false;
        if (filter.unit && v.unit !== filter.unit) return false;
      }
      if (filter.q) {
        const s = filter.q.toLowerCase();
        const hay = category === "polosan"
          ? `${v.product_name} ${v.sleeve||""} ${v.color} ${v.size} ${v.sku}`.toLowerCase()
          : `${v.fabric_name} ${v.brand} ${v.color} ${v.unit} ${v.sku}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [variants, filter, category]);

  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
  const colorOpts = useMemo(() => uniq(variants.map(v => v.color)), [variants]);
  const sizeOpts = useMemo(() => uniq(variants.map(v => v.size)), [variants]);
  const fabricOpts = useMemo(() => uniq(variants.map(v => v.fabric_name)), [variants]);
  const brandOpts = useMemo(() => uniq(variants.map(v => v.brand)), [variants]);

  if (!canView) return <section className="section"><h3>Stok</h3><p className="muted">Anda tidak memiliki akses ke modul Stok.</p></section>;

  const goDaftarWithStatus = (st) => {
    // Iter 269: "__all__" → reset semua filter (dipakai kartu Total Stok/Kain Fisik).
    if (st === "__all__") setFilter(emptyFilter());
    else setFilter({ ...emptyFilter(), status: st });
    setTab("daftar");
  };
  const catSummary = summary?.[category] || {};

  const switchCategory = (c) => {
    if (c === category) return;
    setCategory(c);
    setFilter(emptyFilter());  // reset filter yang tidak relevan
  };

  // Iter 287ay — Navigasi ringkas Rido/Gudang: 4 pil (Daftar, Mutasi, Ke Potong, Lainnya).
  //   • "Mutasi" halaman gabungan (Barang Masuk + Barang Keluar + Riwayat) — sub-state lokal.
  //   • "Lainnya" popover: Ringkasan + Stok Opname + Permintaan Kain (kain only).
  //   • Tab lama (summary/in/out/mutasi/opname/fabreq) tetap valid; nav baru hanya memetakan ulang.
  const goTab = (k) => { setTab(k); setMoreOpen(false); };
  const isMoreActive = ["summary","opname","fabreq","skumap"].includes(tab);
  const isMutasiActive = ["mutasi","in","out"].includes(tab);

  return <CORRECT_CTX.Provider value={(canMutasi && !operator?.is_acc_only) || role === "admin"}><ShopProvider>
    <section className="stock-wrap" data-testid="stock-wrap" style={{display:"flex", flexDirection:"column", gap:14}}>
    <header className="stock-header" style={{display:"flex", flexDirection:"column", gap:4}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, flexWrap:"wrap"}}>
        <h2 style={{margin:0}}>Stok Barang</h2>
        {/* Iter 387 — Toggle Mode Belanja di header stok (muncul di SEMUA tab,
            termasuk Ringkasan). Terlihat di mobile narrow karena di baris
            yang sama dengan judul (flex-wrap aktif, mobile → turun baris). */}
        <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
          <ShopHistoryButton/>
          <ShopModeToggle/>
        </div>
      </div>
      <details className="stock-note-collapsible" data-testid="stock-note-collapsible">
        <summary className="stock-note-summary">Informasi stok</summary>
        <p className="muted small" style={{margin:"6px 0 0"}}>
          Nota belum mengurangi stok otomatis. Stok berubah melalui transaksi masuk dan keluar.
        </p>
      </details>
    </header>

    {/* Iter 287ay — Segmented control compact Polosan/Kain (bukan dua tombol besar). */}
    <nav className="stock-cat-segment" role="tablist" aria-label="Kategori stok" data-testid="stock-category-tabs">
      <button type="button" role="tab" aria-selected={category==="polosan"}
        className={`stock-cat-segment-btn ${category==="polosan"?"active":""}`}
        data-testid="stock-category-polosan"
        onClick={()=>switchCategory("polosan")}>
        <Package size={13}/> <span>Polosan</span>
      </button>
      <button type="button" role="tab" aria-selected={category==="kain"}
        className={`stock-cat-segment-btn ${category==="kain"?"active":""}`}
        data-testid="stock-category-kain"
        onClick={()=>switchCategory("kain")}>
        <Layers size={13}/> <span>Kain</span>
      </button>
    </nav>

    {/* Iter 287ay — Navigasi ringkas 4 pil (Daftar · Mutasi · Ke Potong · Lainnya).
        Menu "Ke Potong" hanya tampil pada kategori Kain. */}
    <nav className="stock-pill-nav" role="tablist" aria-label="Navigasi stok" data-testid="stock-tabs">
      <button type="button" role="tab" aria-selected={tab==="daftar"}
        className={`stock-pill ${tab==="daftar"?"active":""}`}
        data-testid="stock-tab-daftar" onClick={()=>goTab("daftar")}>
        <Package size={14}/> <span>Daftar Stok</span>
      </button>
      <button type="button" role="tab" aria-selected={isMutasiActive}
        className={`stock-pill ${isMutasiActive?"active":""}`}
        data-testid="stock-tab-mutasi"
        onClick={()=>{ setMutasiPage("list"); goTab("mutasi"); }}>
        <History size={14}/> <span>Mutasi</span>
      </button>
      {category === "kain" && (
        <button type="button" role="tab" aria-selected={tab==="potong"}
          className={`stock-pill ${tab==="potong"?"active":""}`}
          data-testid="stock-tab-potong" onClick={()=>goTab("potong")}>
          <Scissors size={14}/> <span>Ke Potong</span>
        </button>
      )}
      <div className="stock-more-wrap">
        <button type="button" aria-haspopup="menu" aria-expanded={moreOpen}
          className={`stock-pill ${isMoreActive?"active":""}`}
          data-testid="stock-tab-more" onClick={()=>setMoreOpen(v=>!v)}>
          <Boxes size={14}/> <span>Lainnya</span>
          <ChevronDown size={13} style={{opacity:.7}}/>
        </button>
        {moreOpen && (
          <>
            <div className="stock-more-overlay" onClick={()=>setMoreOpen(false)} data-testid="stock-more-overlay"/>
            <div className="stock-more-menu" role="menu" data-testid="stock-more-menu">
              <button type="button" role="menuitem" className={`stock-more-item ${tab==="summary"?"active":""}`}
                data-testid="stock-tab-summary" onClick={()=>goTab("summary")}>
                <Boxes size={14}/> <span>Ringkasan</span>
              </button>
              <button type="button" role="menuitem" className={`stock-more-item ${tab==="opname"?"active":""}`}
                data-testid="stock-tab-opname" onClick={()=>goTab("opname")}>
                <ClipboardCheck size={14}/> <span>Stok Opname</span>
              </button>
              {(isOwner || role === "admin") && (
                <button type="button" role="menuitem" className={`stock-more-item ${tab==="skumap"?"active":""}`}
                  data-testid="stock-tab-skumap" onClick={()=>goTab("skumap")}>
                  <ClipboardCheck size={14}/> <span>Verifikasi Pemetaan SKU</span>
                </button>
              )}
              {category === "kain" && (
                <button type="button" role="menuitem" className={`stock-more-item ${tab==="fabreq"?"active":""}`}
                  data-testid="stock-tab-fabreq" onClick={()=>goTab("fabreq")}>
                  <PackageOpen size={14}/> <span>Permintaan Kain</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </nav>

    {err && <div className="warn-box" data-testid="stock-error">{err}</div>}
    {summary?.ambigu > 0 && <div className="warn-box small" data-testid="stock-ambigu-notice">
      ⚠️ {summary.ambigu} varian lama perlu ditinjau Owner: pastikan kategorinya (Polosan/Kain) sudah benar setelah migrasi Iter 263.
    </div>}

    {tab === "summary" && <>
      <TabSummary category={category} summary={catSummary} onCardClick={goDaftarWithStatus} loading={loading} onManageVariants={isOwner?()=>setTab("daftar"):null}/>
      {isOwner && <InventoryValuePanel category={category}/>}
      {!loading && (catSummary.varian_aktif||0) === 0 && (
        <article className="warn-box" data-testid="stock-empty-onboarding" style={{marginTop:12}}>
          <b>📦 Belum ada varian {category === "kain" ? "kain" : "polosan"}.</b>
          <p className="muted small" style={{marginTop:6}}>
            {isOwner
              ? <>Mulai dengan menambah varian di tab <b>Daftar Stok</b>{category==="polosan"?" (produk × warna × ukuran)":" (jenis kain × merek × warna × satuan)"}, lalu buka panel <b>Konfirmasi Saldo Awal</b>.</>
              : (canMutasi
                ? <>Menunggu Owner menambahkan varian & mengkonfirmasi saldo awal. Setelah itu Anda bisa mencatat <b>Barang Masuk</b>/<b>Barang Keluar</b> dan mengajukan <b>Stok Opname</b>.</>
                : <>Menunggu Owner & tim Gudang menyiapkan data stok awal.</>)}
          </p>
          {isOwner && <button type="button" className="primary small-btn" style={{marginTop:6}} onClick={()=>setTab("daftar")} data-testid="stock-empty-cta">Buka Daftar Stok</button>}
        </article>
      )}
    </>}
    {tab === "daftar" && <TabDaftar
      category={category}
      variants={filtered} products={products} filter={filter} setFilter={setFilter}
      isOwner={isOwner} loading={loading} onChanged={()=>loadAll(category)}
      colorOpts={colorOpts} sizeOpts={sizeOpts} fabricOpts={fabricOpts} brandOpts={brandOpts}
      onOpenOrder={onOpenOrder}
    />}
    {/* Iter 287ay — Halaman Mutasi gabungan: tombol Barang Masuk/Keluar + Riwayat. */}
    {tab === "mutasi" && (
      <div className="stock-mutasi-page" data-testid="stock-mutasi-page">
        {mutasiPage === "list" && <>
          <div className="stock-mutasi-cta-row">
            {canMutasi && <button type="button" className="primary small-btn stock-mutasi-cta stock-mutasi-cta-in"
              data-testid="stock-mutasi-cta-in" onClick={()=>setMutasiPage("in")}>
              <PlusCircle size={14}/> Barang Masuk
            </button>}
            {canMutasi && <button type="button" className="primary small-btn stock-mutasi-cta stock-mutasi-cta-out"
              data-testid="stock-mutasi-cta-out" onClick={()=>setMutasiPage("out")}>
              <MinusCircle size={14}/> Barang Keluar
            </button>}
          </div>
          {canMutasi && <AccPendingCuts onOpenOrder={onOpenOrder}/>}
          <TabMutasi category={category} variants={variants} isOwner={isOwner} onDone={()=>loadAll(category)}/>
        </>}
        {mutasiPage === "in" && <>
          <button type="button" className="stock-sub-back" onClick={()=>setMutasiPage("list")}
            data-testid="stock-mutasi-back-in" aria-label="Kembali ke daftar mutasi">
            ← Kembali ke Riwayat Mutasi
          </button>
          <TabIn category={category} variants={variants} canMutasi={canMutasi}
            onDone={()=>{ loadAll(category); setMutasiPage("list"); }}
            onOpenOrder={onOpenOrder}/>
        </>}
        {mutasiPage === "out" && <>
          <button type="button" className="stock-sub-back" onClick={()=>setMutasiPage("list")}
            data-testid="stock-mutasi-back-out" aria-label="Kembali ke daftar mutasi">
            ← Kembali ke Riwayat Mutasi
          </button>
          <TabOut category={category} variants={variants} canMutasi={canMutasi}
            onDone={()=>{ loadAll(category); setMutasiPage("list"); }}
            onOpenOrder={onOpenOrder}/>
        </>}
      </div>
    )}
    {tab === "skumap" && (isOwner || role === "admin") && <SkuMapPanel isOwner={isOwner}/>}
    {tab === "opname" && <TabOpname category={category} variants={variants} canMutasi={canMutasi} isOwner={isOwner} onDone={()=>loadAll(category)}/>}
    {tab === "potong" && category === "kain" && <TabPotong variants={variants} canMutasi={canMutasi} isOwner={isOwner} onDone={()=>loadAll(category)} onOpenOrder={onOpenOrder}/>}
    {tab === "fabreq" && category === "kain" && <TabFabricRequests variants={variants} canMutasi={canMutasi} isOwner={isOwner} onDone={()=>loadAll(category)} onOpenOrder={onOpenOrder}/>}
    {/* Iter 387 — Rekap Belanja di level StockModule → persist antar tab. */}
    <ShopRekapPanel/>
    </section>
  </ShopProvider></CORRECT_CTX.Provider>;
}

// ============================================================
function TabSummary({ category, summary, onCardClick, loading, onManageVariants }) {
  const isKain = category === "kain";
  const totalValue = isKain
    ? <span>
        <span data-testid="stock-sum-total-roll">{money(summary.total_roll)} roll</span>{" · "}
        <span data-testid="stock-sum-total-meter">{money(summary.total_meter)} m</span>{" · "}
        <span data-testid="stock-sum-total-kg">{money(summary.total_kg)} kg</span>
      </span>
    : `${money(summary.total_pcs)} pcs`;
  const totalLabel = isKain ? "Total Kain Fisik" : "Total Stok Fisik";
  const cards = [
    { key: "total", label: totalLabel, value: totalValue, tone: "ink", filter: "__all__", icon: isKain ? <Layers size={18}/> : <Boxes size={18}/>, hint: "Lihat stok →" },
    { key: "habis", label: "Varian Habis", value: summary.habis||0, tone: "red", filter: "habis", icon: <AlertTriangle size={18}/> },
    { key: "menipis", label: "Varian Menipis", value: summary.menipis||0, tone: "amber", filter: "menipis", icon: <AlertTriangle size={18}/> },
    { key: "belum", label: "Belum Saldo Awal", value: summary.belum_saldo_awal||0, tone: "blue", filter: "belum_dihitung", icon: <Circle size={18}/> },
  ];
  return <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:10}}>
    {cards.map(c => {
      const clickable = !!c.filter;
      const cls = `at-prod-sum at-prod-sum-${c.tone}${clickable?" clickable":""}`;
      const testid = `stock-sum-${c.key}`;
      const inner = <>
        <span className="at-prod-sum-icon">{c.icon}</span>
        <span className="at-prod-sum-value">{loading?"…":c.value}</span>
        <span className="at-prod-sum-label">{c.label}</span>
        {c.hint && <span className="at-prod-sum-hint" data-testid={`${testid}-hint`}
          style={{fontSize:11, opacity:0.75, marginTop:4, fontWeight:500}}>{c.hint}</span>}
      </>;
      if (!clickable) return <article key={c.key} className={cls} data-testid={testid}>{inner}</article>;
      return <button key={c.key} type="button" className={cls} data-testid={testid} onClick={()=>onCardClick(c.filter)}>{inner}</button>;
    })}
  </div>;
}

// ============================================================
// Iter 280 — Panel Nilai Persediaan (Owner-only).
// 3 kartu clickable: Nilai Polosan / Nilai Kain / Total Nilai Persediaan.
// Klik → modal drill-down dengan produk × jumlah × harga modal × subtotal.
// ============================================================
function InventoryValuePanel({ category }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // "polosan"|"kain"|"total"|null
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock/inventory-value`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/stock/inventory-value`);
        if (mounted) setData(r.data);
      } catch (e) {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [category]);

  const triggerImport = async () => {
    if (importing) return;
    if (!window.confirm("Terapkan harga modal dari spreadsheet ke DB? Harga yang sudah diedit manual Owner akan dipertahankan.")) return;
    setImporting(true);
    setImportReport(null);
    try {
      const r = await axios.post(`${API}/stock/import-cost-prices`);
      setImportReport(r.data);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Gagal jalankan import harga modal.");
    } finally { setImporting(false); }
  };

  if (loading) {
    return <article className="at-prod-card" style={{marginTop:12, padding:10}} data-testid="stock-inventory-value-loading">
      <span className="muted small">Memuat nilai persediaan…</span>
    </article>;
  }
  if (!data) return null;
  const bp = data.by_category?.polosan || {};
  const bk = data.by_category?.kain || {};
  const cards = [
    { key:"polosan", tone:"ink", label:"Nilai Stok Polosan", value:bp.value||0, mapped:bp.mapped||0, unmapped:bp.unmapped||0, total:bp.total_variants||0 },
    { key:"kain",    tone:"ink", label:"Nilai Stok Kain",    value:bk.value||0, mapped:bk.mapped||0, unmapped:bk.unmapped||0, total:bk.total_variants||0 },
    { key:"total",   tone:"amber", label:"Total Nilai Persediaan", value:data.total_value||0, mapped:(bp.mapped||0)+(bk.mapped||0), unmapped:data.total_unmapped||0, total:(bp.total_variants||0)+(bk.total_variants||0) },
  ];

  return <>
    <section style={{marginTop:14}} data-testid="stock-inventory-value">
      <div style={{display:"flex", alignItems:"baseline", gap:8, marginBottom:6, flexWrap:"wrap"}}>
        <b style={{fontSize:14}}>Nilai Persediaan · Owner</b>
        <span className="muted small">Harga modal dihitung dari master harga Owner. Klik kartu untuk rincian.</span>
        <button type="button" className="soft-btn small-btn" style={{marginLeft:"auto"}}
          onClick={triggerImport} disabled={importing}
          data-testid="stock-inv-value-import"
          title="Terapkan ulang harga modal dari spreadsheet ke DB (mis. setelah deploy Live). Harga yang sudah diedit manual Owner dipertahankan.">
          {importing ? "Menerapkan…" : "Terapkan Harga dari Spreadsheet"}
        </button>
      </div>
      {importReport && <div style={{background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, padding:"8px 12px", marginBottom:8, fontSize:12}}
        data-testid="stock-inv-value-import-report">
        Ter-map: <b>{importReport.mapped_total}</b> (polosan {importReport.mapped_polosan} · kain {importReport.mapped_kain}) ·
        Skip manual: <b>{importReport.skipped_manual}</b> ·
        Belum harga: <b>{importReport.unmapped_total}</b> ·
        Total nilai: <b>Rp {money(Math.round(importReport.total_inventory_value))}</b>
      </div>}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(210px, 1fr))", gap:10}}>
        {cards.map(c => {
          const partial = c.unmapped > 0;
          return <button key={c.key} type="button"
            className={`at-prod-sum at-prod-sum-${c.tone} clickable`}
            data-testid={`stock-inv-value-${c.key}`}
            onClick={()=>setDrill(c.key)}
            style={{alignItems:"flex-start"}}>
            <span className="at-prod-sum-label" style={{fontSize:12}}>{c.label}</span>
            <span className="at-prod-sum-value" style={{fontSize:20, fontVariantNumeric:"tabular-nums"}}>Rp {money(Math.round(c.value))}</span>
            {partial && <span className="chip chip-amber small" style={{marginTop:4}} data-testid={`stock-inv-value-${c.key}-partial`}>Nilai sementara · {c.unmapped} varian belum harga</span>}
            <span className="muted small" style={{fontSize:11, marginTop:2}}>{c.mapped}/{c.total} varian ter-map · Lihat rincian →</span>
          </button>;
        })}
      </div>
    </section>
    {drill && <InventoryValueDrill data={data} kind={drill} onClose={()=>setDrill(null)}/>}
  </>;
}

function InventoryValueDrill({ data, kind, onClose }) {
  const items = (data.items || []).filter(it => {
    if (kind === "total") return true;
    return it.category === kind;
  });
  const withCost = items.filter(it => it.has_cost);
  const withoutCost = items.filter(it => !it.has_cost);
  // Group by product+sleeve+color (polosan) atau fabric+brand+unit (kain) untuk ringkas
  const rows = [...withCost, ...withoutCost].sort((a,b) => (b.subtotal||0) - (a.subtotal||0));
  const totalValue = withCost.reduce((s, it) => s + (it.subtotal || 0), 0);
  const title = kind === "polosan" ? "Nilai Stok Polosan" : kind === "kain" ? "Nilai Stok Kain" : "Total Nilai Persediaan";
  return <div className="modal-backdrop" data-testid="stock-inv-value-drill" onClick={onClose}>
    <div className="modal-panel" onClick={e=>e.stopPropagation()} style={{maxWidth:720, maxHeight:"85vh", display:"flex", flexDirection:"column"}}>
      <h3 style={{margin:"0 0 8px"}}>{title}</h3>
      <div className="muted small" style={{marginBottom:6}}>
        {withCost.length} varian dengan harga · {withoutCost.length} belum harga · Rp {money(Math.round(totalValue))}
      </div>
      <div style={{overflow:"auto", flex:1, border:"1px solid var(--line,#e2e8f0)", borderRadius:8}}>
        <table style={{width:"100%", fontSize:12, borderCollapse:"collapse"}}>
          <thead style={{background:"var(--wash-2,#f4efdf)", position:"sticky", top:0}}>
            <tr>
              <th style={{textAlign:"left", padding:"6px 8px"}}>Produk / Bahan</th>
              <th style={{textAlign:"left", padding:"6px 8px"}}>Detail</th>
              <th style={{textAlign:"right", padding:"6px 8px"}}>Qty</th>
              <th style={{textAlign:"right", padding:"6px 8px"}}>Harga Modal</th>
              <th style={{textAlign:"right", padding:"6px 8px"}}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(it => {
              const label = it.category === "polosan"
                ? it.product_name
                : `${it.fabric_name}${it.brand ? " · " + it.brand : ""}`;
              const detail = it.category === "polosan"
                ? [it.sleeve && `Lengan ${SLEEVE_LABEL[it.sleeve] || it.sleeve}`, it.color, `Ukuran ${it.size}`].filter(Boolean).join(" · ")
                : [it.color, it.unit].filter(Boolean).join(" · ");
              return <tr key={it.id} style={{borderTop:"1px solid var(--line,#e2e8f0)"}}
                data-testid={`stock-inv-value-row-${it.id}`}>
                <td style={{padding:"6px 8px"}}>{label}</td>
                <td style={{padding:"6px 8px"}} className="muted">{detail}</td>
                <td style={{padding:"6px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums"}}>{money(it.qty)} {it.unit}</td>
                <td style={{padding:"6px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", color: it.has_cost ? "" : "var(--muted,#94a3b8)"}}>
                  {it.has_cost ? `Rp ${money(it.cost_price)}` : "—"}
                </td>
                <td style={{padding:"6px 8px", textAlign:"right", fontVariantNumeric:"tabular-nums", fontWeight:600}}>
                  {it.has_cost ? `Rp ${money(Math.round(it.subtotal))}` : "—"}
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      <div className="modal-actions" style={{marginTop:10}}>
        <button type="button" className="primary" onClick={onClose}>Tutup</button>
      </div>
    </div>
  </div>;
}

// ============================================================
function TabDaftar({ category, variants, products, filter, setFilter, isOwner, loading, onChanged, colorOpts, sizeOpts, fabricOpts, brandOpts, onOpenOrder }) {
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [detail, setDetail] = useState(null);
  const [obPending, setObPending] = useState([]); // varian pending saldo awal
  const [obTs, setObTs] = useState(localISO());
  const [obNote, setObNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showAdvFilter, setShowAdvFilter] = useState(false); // Iter 390 — Filter lainnya collapse
  const isKain = category === "kain";
  // Iter 390 — Hitung filter aktif pada "Filter lainnya" (kain) utk badge
  const advActiveCount = isKain
    ? [filter.fabric_name, filter.brand, filter.color, filter.unit].filter(Boolean).length
    : 0;

  const submitOB = async () => {
    if (!obPending.length) return;
    setBusy(true); setMsg("");
    try {
      const client_key = uuid();
      await axios.post(`${API}/stock/opening-balance`, {
        counted_at: obTs, note: obNote, client_key,
        lines: obPending.map(l => ({ variant_id: l.variant_id, qty: Number(l.qty||0), note: l.note||"" })),
      });
      setMsg(`Saldo awal ${obPending.length} varian dikonfirmasi.`);
      setObPending([]); setObNote(""); onChanged();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal simpan saldo awal"); }
    finally { setBusy(false); }
  };

  return <div style={{display:"flex", flexDirection:"column", gap:12}} className={isKain ? "stock-daftar-wrap stock-daftar-wrap--kain" : "stock-daftar-wrap"}>
    <div className="stock-filter-bar" data-testid="stock-filter-bar">
      <div className="search stock-filter-search">
        <Search size={14}/>
        <input value={filter.q} onChange={e=>setFilter(f=>({...f,q:e.target.value}))}
          placeholder={isKain ? "Cari kain / merek / warna / satuan / SKU" : "Cari produk / warna / ukuran / SKU"}
          data-testid="stock-daftar-search"/>
      </div>
      <select className="stock-filter-select" value={filter.status} onChange={e=>setFilter(f=>({...f,status:e.target.value}))} data-testid="stock-daftar-status">
        <option value="">Semua Status</option>
        <option value="belum_dihitung">Belum Dihitung</option>
        <option value="habis">Habis</option>
        <option value="menipis">Menipis</option>
        <option value="aman">Aman</option>
        <option value="min_belum_diatur">Min belum diatur</option>
      </select>
      {isKain ? (
        <button type="button"
          className={`stock-filter-adv-btn${showAdvFilter ? " is-open" : ""}${advActiveCount ? " is-active" : ""}`}
          onClick={()=>setShowAdvFilter(s=>!s)}
          aria-expanded={showAdvFilter}
          data-testid="stock-filter-adv-toggle">
          <SlidersHorizontal size={13}/>
          <span>Filter lainnya</span>
          {advActiveCount > 0 && <span className="stock-filter-adv-badge" data-testid="stock-filter-adv-badge">{advActiveCount}</span>}
          <ChevronDown size={13} className="stock-filter-adv-caret"/>
        </button>
      ) : <>
        <select className="stock-filter-select" value={filter.sleeve} onChange={e=>setFilter(f=>({...f,sleeve:e.target.value}))} data-testid="stock-daftar-sleeve">
          <option value="">Semua Lengan</option>
          <option value="pendek">Pendek</option>
          <option value="panjang">Panjang</option>
          <option value="tidak_berlaku">Tidak Berlaku</option>
        </select>
        <select className="stock-filter-select" value={filter.color} onChange={e=>setFilter(f=>({...f,color:e.target.value}))}>
          <option value="">Semua Warna</option>{colorOpts.map(x=><option key={x} value={x}>{x}</option>)}
        </select>
        <select className="stock-filter-select" value={filter.size} onChange={e=>setFilter(f=>({...f,size:e.target.value}))}>
          <option value="">Semua Ukuran</option>{sizeOpts.map(x=><option key={x} value={x}>{x}</option>)}
        </select>
      </>}
      {isOwner && <div className="stock-filter-owner-actions">
        <button type="button" className="soft-btn small-btn" onClick={()=>setShowImport(true)} data-testid="stock-import-btn"><FileUp size={14}/>Impor CSV/Excel</button>
        <button type="button" className="soft-btn small-btn" onClick={()=>setShowReconcile(true)} data-testid="stock-reconcile-btn"><FileUp size={14}/>Rekonsiliasi CSV</button>
        <button type="button" className="primary small-btn" onClick={()=>setShowNew(true)} data-testid="stock-variant-new-btn"><PlusCircle size={14}/>Tambah Varian</button>
      </div>}
    </div>

    {!isKain && isOwner && <MissingStockProducts products={products} variants={variants} onAdd={(pid) => setShowNew(pid)}/>}
    {isKain && showAdvFilter && <div className="stock-filter-adv-panel" data-testid="stock-filter-adv-panel">
      <select className="stock-filter-select" value={filter.fabric_name} onChange={e=>setFilter(f=>({...f,fabric_name:e.target.value}))} data-testid="stock-daftar-fabric">
        <option value="">Semua Jenis Kain</option>{fabricOpts.map(x=><option key={x} value={x}>{x}</option>)}
      </select>
      <select className="stock-filter-select" value={filter.brand} onChange={e=>setFilter(f=>({...f,brand:e.target.value}))} data-testid="stock-daftar-brand">
        <option value="">Semua Merek</option>{brandOpts.map(x=><option key={x} value={x}>{x}</option>)}
      </select>
      <select className="stock-filter-select" value={filter.color} onChange={e=>setFilter(f=>({...f,color:e.target.value}))}>
        <option value="">Semua Warna</option>{colorOpts.map(x=><option key={x} value={x}>{x}</option>)}
      </select>
      <select className="stock-filter-select" value={filter.unit} onChange={e=>setFilter(f=>({...f,unit:e.target.value}))} data-testid="stock-daftar-unit">
        <option value="">Semua Satuan</option>
        <option value="meter">meter</option>
        <option value="kg">kg</option>
        <option value="roll">roll</option>
      </select>
      {advActiveCount > 0 && <button type="button"
        className="stock-filter-adv-reset"
        onClick={()=>setFilter(f=>({...f, fabric_name:"", brand:"", color:"", unit:""}))}
        data-testid="stock-filter-adv-reset">Reset filter lainnya</button>}
    </div>}

    {msg && <div className="warn-box small" data-testid="stock-ob-msg">{msg}</div>}

    {/* Bulk saldo awal — visible untuk Owner. Varian yg belum saldo awal dipilih di sini. */}
    {isOwner && <details style={{border:"1px solid var(--line,#e2e8f0)", borderRadius:10, padding:8}} data-testid="stock-ob-panel">
      <summary style={{cursor:"pointer", fontWeight:600}}>Konfirmasi Saldo Awal ({variants.filter(v=>v.status==="belum_dihitung").length} varian belum)</summary>
      <div style={{display:"flex", flexDirection:"column", gap:8, marginTop:8}}>
        <label className="modal-field">Waktu Hitung
          <input type="datetime-local" value={obTs.slice(0,16)} onChange={e=>setObTs(new Date(e.target.value).toISOString())}/>
        </label>
        <label className="modal-field">Catatan (opsional)
          <input value={obNote} onChange={e=>setObNote(e.target.value)}/>
        </label>
        <div style={{display:"flex", flexDirection:"column", gap:4}}>
          {variants.filter(v=>v.status==="belum_dihitung").map(v => {
            const pend = obPending.find(x=>x.variant_id===v.id);
            const vUnit = (v.unit || "").toLowerCase();
            const isFractional = vUnit === "meter" || vUnit === "kg";
            const step = isFractional ? "0.001" : "1";
            return <div key={v.id} style={{display:"grid", gridTemplateColumns:"1fr 120px 40px", gap:6, alignItems:"center", fontSize:13}}>
              <span>{variantLabel(v)}</span>
              <input type="number" min={0} step={step} placeholder={`0 ${v.unit||""}`}
                value={pend?.qty ?? ""} onChange={e=>{
                  const q = e.target.value; setObPending(cur => {
                    const other = cur.filter(x=>x.variant_id!==v.id);
                    if (q === "" || q === null) return other;
                    const num = isFractional ? Math.max(0, parseFloat(q||"0")) : Math.max(0, parseInt(q||"0",10));
                    return [...other, { variant_id: v.id, qty: num, note: pend?.note||"" }];
                  });
                }}
                data-testid={`stock-ob-qty-${v.id}`}
              />
              {pend && <button type="button" className="soft-btn small-btn" onClick={()=>setObPending(cur=>cur.filter(x=>x.variant_id!==v.id))}>×</button>}
            </div>;
          })}
        </div>
        <button type="button" className="primary" onClick={submitOB} disabled={busy || obPending.length===0} data-testid="stock-ob-submit">
          {busy?"Menyimpan…":`Konfirmasi Saldo Awal (${obPending.length})`}
        </button>
        <p className="muted small">Konfirmasi tidak bisa diulang. Koreksi selanjutnya via Stok Opname.</p>
      </div>
    </details>}

    <div className="stock-list" style={{display:"flex", flexDirection:"column", gap:8}}>
      {loading && <p className="muted">Memuat…</p>}
      {!loading && variants.length === 0 && <p className="muted">Tidak ada varian pada filter ini.</p>}
      {!loading && category === "polosan" && variants.length > 0 && (
        <PolosanAccordionList variants={variants} filter={filter} isOwner={isOwner} onOpenDetail={setDetail} onChanged={onChanged}/>
      )}
      {!loading && category === "kain" && variants.length > 0 && (
        <KainAccordionList variants={variants} filter={filter} isOwner={isOwner} onOpenDetail={setDetail} onChanged={onChanged}/>
      )}
    </div>

    {showNew && isOwner && <VariantForm category={category} products={products} initialPid={typeof showNew === "string" ? showNew : ""}
      onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false); onChanged();}}/>}
    {showImport && isOwner && <ImportModal products={products} onClose={()=>setShowImport(false)} onDone={()=>{setShowImport(false); onChanged();}}/>}
    {showReconcile && isOwner && <ReconcileModal onClose={()=>setShowReconcile(false)} onDone={()=>{setShowReconcile(false); onChanged();}}/>}
    {detail && <VariantDetail variant={detail} onClose={()=>setDetail(null)} onOpenOrder={onOpenOrder}/>}
    {/* Iter 386 — Panel Rekap Belanja bawah (hanya tampil saat ada item).
        Iter 387 — Panel dipindah ke level StockModule agar persist antar tab. */}
  </div>;
}

function VariantEdit({ variant, onSaved, label = "Atur" }) {
  const [open, setOpen] = useState(false);
  const [minStock, setMinStock] = useState(variant.min_stock ?? "");
  const [usesStock, setUsesStock] = useState(!!variant.uses_stock);
  const [isActive, setIsActive] = useState(variant.is_active !== false);
  // Iter 280 — Harga modal per satuan (Owner-only, dilihat dari response API stripping).
  const [costPrice, setCostPrice] = useState(
    variant.cost_price !== null && variant.cost_price !== undefined ? String(variant.cost_price) : ""
  );
  const canEditCost = variant.cost_price !== undefined; // field terlihat = Owner (backend strip untuk non-Owner)
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        min_stock: minStock === "" ? null : Number(minStock),
        uses_stock: usesStock,
        is_active: isActive,
      };
      if (canEditCost) {
        body.cost_price = costPrice === "" ? 0 : Number(costPrice);
      }
      await axios.patch(`${API}/stock/variants/${variant.id}`, body);
      setOpen(false); onSaved();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(false); }
  };
  const unit = variant.unit || (variant.category === "polosan" ? "pcs" : "");
  return <>
    <button type="button" className="soft-btn small-btn" onClick={()=>setOpen(true)} data-testid={`stock-variant-edit-${variant.id}`}>{label}</button>
    {open && <div className="modal-backdrop" data-testid="stock-variant-edit-modal">
      <div className="modal-panel">
        <h3>Atur {variant.product_name || variant.fabric_name}</h3>
        <label className="modal-field">Stok Minimum (kosong = tidak diatur)
          <input type="number" min={0} value={minStock} onChange={e=>setMinStock(e.target.value)}/>
        </label>
        {canEditCost && <label className="modal-field">Harga Modal per {unit || "satuan"} (Rp, kosong = belum diatur)
          <input type="number" min={0} step={100} value={costPrice}
            data-testid={`stock-variant-cost-${variant.id}`}
            onChange={e=>setCostPrice(e.target.value)}
            placeholder="Contoh: 65000"/>
          <span className="muted small" style={{marginTop:2, display:"block"}}>Owner-only. Dipakai untuk menghitung Nilai Persediaan.</span>
        </label>}
        <label className="modal-check"><input type="checkbox" checked={usesStock} onChange={e=>setUsesStock(e.target.checked)}/><span>Produk ini menggunakan stok</span></label>
        <label className="modal-check"><input type="checkbox" checked={isActive} onChange={e=>setIsActive(e.target.checked)}/><span>Varian aktif</span></label>
        <div className="modal-actions">
          <button type="button" className="soft-btn" onClick={()=>setOpen(false)} disabled={busy}>Batal</button>
          <button type="button" className="primary" onClick={save} disabled={busy}>{busy?"…":"Simpan"}</button>
        </div>
      </div>
    </div>}
  </>;
}

// ============================================================
// Iter 393 — Edit Warna Varian (Owner-only).
// Mengubah label `color` + override swatch `color_hex` tanpa menyentuh
// variant_id, SKU, stok, mutasi, atau histori. Backend akan menolak jika
// nama warna baru menyebabkan bentrok identitas (duplicate variant_key).
// ============================================================
export function VariantColorEdit({ variant, onSaved }) {
  const [open, setOpen] = useState(false);
  const [colorName, setColorName] = useState(variant.color || "");
  const [hex, setHex] = useState(variant.color_hex || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const show = () => { setColorName(variant.color || ""); setHex(variant.color_hex || ""); setErr(""); setOpen(true); };
  const previewHex = useMemo(() => {
    const raw = (hex || "").trim();
    if (/^#?[0-9a-fA-F]{6}$/.test(raw)) return raw.startsWith("#") ? raw : "#" + raw;
    if (/^#?[0-9a-fA-F]{3}$/.test(raw)) {
      const s = raw.replace("#","");
      return "#" + s.split("").map(c=>c+c).join("");
    }
    return resolveColorHex(colorName).hex;
  }, [colorName, hex]);

  const save = async () => {
    const name = (colorName || "").trim();
    if (!name) { setErr("Nama warna wajib diisi."); return; }
    setBusy(true); setErr("");
    try {
      const body = { color: name };
      // Hanya kirim color_hex jika user secara eksplisit set (atau kosongkan utk reset).
      const rawHex = (hex || "").trim();
      if (rawHex === "" && variant.color_hex) body.color_hex = ""; // reset ke auto
      else if (rawHex !== "") body.color_hex = rawHex;
      await axios.patch(`${API}/stock/variants/${variant.id}`, body);
      toast.success("Warna varian diperbarui");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : e?.response ? `Gagal menyimpan warna (kode ${e.response.status}).` : "Gagal menyimpan warna — periksa koneksi internet lalu coba lagi.");
    } finally { setBusy(false); }
  };

  return <>
    <button type="button" className="soft-btn small-btn"
      onClick={show}
      data-testid={`stock-variant-color-edit-${variant.id}`}>
      Edit Warna
    </button>
    {open && createPortal(<div className="vce-backdrop" data-testid="stock-variant-color-modal">
      <form className="vce-panel" role="dialog" aria-modal="true" aria-label="Ubah Warna" onClick={e=>e.stopPropagation()}
        onSubmit={e=>{ e.preventDefault(); if (!busy) save(); }}>
        <h3 style={{margin:"0 0 4px", fontSize:16}}>Ubah Warna</h3>
        <p className="muted small" style={{margin:"0 0 12px"}}>
          Hanya nama & kode warna. Stok, ukuran, SKU, dan histori tidak berubah.
        </p>
        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:12}}>
          <span className="color-swatch" aria-hidden="true"
            data-testid="stock-variant-color-preview"
            style={{width:64, height:64, "--swatch-color": previewHex}}/>
          <div style={{display:"flex", flexDirection:"column", gap:2, minWidth:0}}>
            <b style={{fontSize:14, overflowWrap:"anywhere"}}>{(colorName || "").trim() || "—"}</b>
            <span className="muted small" style={{fontFamily:"monospace"}}>{previewHex.toUpperCase()}</span>
          </div>
        </div>
        <label className="modal-field">Nama Warna
          <input type="text" value={colorName}
            maxLength={60}
            onChange={e=>setColorName(e.target.value)}
            placeholder="Contoh: MERAH CABE"
            data-testid="stock-variant-color-name"/>
        </label>
        <label className="modal-field">Kode Warna (opsional)
          <input type="text" value={hex}
            onChange={e=>setHex(e.target.value)}
            placeholder="#1A4BCA · kosongkan utk auto"
            data-testid="stock-variant-color-hex"/>
          <span className="muted small" style={{marginTop:2, display:"block"}}>
            Kosongkan agar preview otomatis mengikuti nama warna.
          </span>
        </label>
        {err && <div className="vce-err" role="alert" data-testid="stock-variant-color-error">{err}</div>}
        <div className="vce-actions">
          <button type="button" className="soft-btn" onClick={()=>setOpen(false)} disabled={busy}
            data-testid="stock-variant-color-cancel">Batal</button>
          <button type="submit" className="primary" disabled={busy}
            data-testid="stock-variant-color-save">{busy ? "Menyimpan…" : "Simpan"}</button>
        </div>
      </form>
    </div>, document.body)}
  </>;
}


// ============================================================
// PolosanGroupedList — Iter 268: kelompokkan kartu per Produk+Lengan+Warna
// Filter status sudah diterapkan di parent, di sini hanya rendering.
// ============================================================
// Satu baris per ukuran: Ukuran | Stok (pcs) | Koreksi (langsung terlihat) + Detail (sekunder).
function PolosanSizeRows({ rows, isOwner, onOpenDetail, onChanged }) {
  const shop = useShop();
  const shopOn = !!shop?.shopMode;
  return <ul className="stock-size-list" style={{listStyle:"none", padding:0, margin:0}}>
    {rows.map(v => <li key={v.id} className="stock-size-row" data-testid={`stock-polosan-row-${v.id}`}>
      <div className="stock-size-line">
        <b className="stock-size-name" data-testid={`stock-polosan-row-size-${v.id}`}>{v.size || "—"}</b>
        <span className={`stock-size-qty is-${v.status || "x"}`} data-testid={`stock-polosan-row-qty-${v.id}`}>{fmtQty(v.current_qty, "pcs")}</span>
        <span className="stock-size-actions" data-testid={`stock-polosan-row-actions-${v.id}`}>
          <StockCorrectButton variant={v} onChanged={onChanged} compact/>
          <RowDetailButton variant={v} onOpenDetail={onOpenDetail} isOwner={isOwner} onChanged={onChanged} compact/>
        </span>
      </div>
      {shopOn && <ShopControls variant={v} unit="pcs"/>}
    </li>)}
  </ul>;
}

function PolosanGroupedList({ variants, isOwner, onOpenDetail, onChanged }) {
  const narrow = useIsNarrow(640);
  // Buat kunci grup deterministik
  const groups = useMemo(() => {
    const byKey = new Map();
    for (const v of variants) {
      const key = `${v.product_name}||${v.sleeve || ""}||${v.color}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          product_name: v.product_name || "—",
          sleeve: v.sleeve || "",
          color: v.color || "—",
          needs_review: false,
          rows: [],
        });
      }
      const g = byKey.get(key);
      g.rows.push(v);
      if (v.needs_review) g.needs_review = true;
    }
    for (const g of byKey.values()) {
      g.rows.sort((a, b) => sizeRank(a.size) - sizeRank(b.size) || (a.size || "").localeCompare(b.size || ""));
    }
    return Array.from(byKey.values()).sort((a, b) =>
      a.product_name.localeCompare(b.product_name) ||
      a.sleeve.localeCompare(b.sleeve) ||
      a.color.localeCompare(b.color)
    );
  }, [variants]);

  return <>{groups.map(g => {
    const title = [
      g.product_name,
      g.sleeve ? `Lengan ${SLEEVE_LABEL[g.sleeve] || g.sleeve}` : "Lengan belum diisi",
      g.color,
    ].join(" · ");
    const testKey = g.key.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
    return <article key={g.key} className="at-prod-card"
      data-testid={`stock-polosan-group-${testKey}`}
      style={{padding: narrow ? 8 : 10, display:"flex", flexDirection:"column", gap:6}}>
      <header style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8}}>
        <b style={{fontSize:14, lineHeight:1.35, color:"var(--ink,#0f0f0f)"}}>{title}</b>
        {g.needs_review && <span className="chip chip-amber small" title="Salah satu varian ditandai perlu ditinjau">Perlu Ditinjau</span>}
      </header>
      <PolosanSizeRows rows={g.rows} isOwner={isOwner} onOpenDetail={onOpenDetail} onChanged={onChanged} narrow={narrow}/>
    </article>;
  })}</>;
}

// ============================================================
// PolosanAccordionList — Iter 275: kelompok buka-tutup untuk Daftar Stok Polosan.
// Level 1 = product_name. Level 2 (opsional) = Lengan Pendek/Panjang bila produk
//   punya keduanya. Level 3 = warna. Leaf = PolosanSizeRows (ukuran + Detail).
// sleeve="tidak_berlaku" atau produk hanya 1 lengan → level sleeve dilewati,
//   label lengan disematkan di header warna. Search/filter aktif → semua open.
// Identitas varian, pemisahan pendek/panjang, saldo, minimum, riwayat TIDAK diubah.
// ============================================================
function PolosanAccordionList({ variants, filter, isOwner, onOpenDetail, onChanged }) {
  const narrow = useIsNarrow(640);
  const filterActive = !!(
    filter && (
      filter.q || filter.status || filter.color || filter.size || filter.sleeve
    )
  );

  // Level 1: per product_name
  const products = useMemo(() => {
    const byProduct = new Map();
    for (const v of variants) {
      const p = v.product_name || "—";
      if (!byProduct.has(p)) byProduct.set(p, []);
      byProduct.get(p).push(v);
    }
    return Array.from(byProduct.entries())
      .map(([name, rows]) => ({ name, rows }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [variants]);

  const countAlerts = (rows) => {
    let habis = 0, menipis = 0, totalPcs = 0;
    for (const v of rows) {
      if (v.status === "habis") habis++;
      else if (v.status === "menipis") menipis++;
      totalPcs += Number(v.current_qty || 0);
    }
    return { habis, menipis, totalPcs };
  };

  const sleeveLabel = (s) => s === "tidak_berlaku" ? "" : (SLEEVE_LABEL[s] || s || "");
  const [openProd, setOpenProd] = useState(null);
  const openP = openProd ? products.find(p => p.name === openProd) : null;  // data terbaru setelah Koreksi/refresh

  return <>{products.map(p => {
    const prodTestKey = p.name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
    // Cek lengan yang ada di produk ini
    const sleevesPresent = Array.from(new Set(p.rows.map(v => v.sleeve || ""))); // include "" or "tidak_berlaku"
    // Anggap "tidak_berlaku" / "" → tidak butuh level sleeve
    const realSleeves = sleevesPresent.filter(s => s && s !== "tidak_berlaku");
    const useSleeveLevel = realSleeves.length >= 2;

    const alerts = countAlerts(p.rows);
    const uniqueColors = new Set(p.rows.map(v => `${v.sleeve || ""}|${v.color}`)).size;
    const singleSleeveLbl = !useSleeveLevel && realSleeves.length === 1 ? sleeveLabel(realSleeves[0]) : "";
    const prodSubtitle = [
      `${money(alerts.totalPcs)} pcs`,
      `${uniqueColors} varian warna`,
      singleSleeveLbl ? `Lengan ${singleSleeveLbl}` : "",
    ].filter(Boolean).join(" · ");

    // Bila ada sleeve level → group by sleeve
    const bySleeve = useSleeveLevel
      ? realSleeves.slice().sort().map(s => ({
          sleeve: s,
          rows: p.rows.filter(v => (v.sleeve || "") === s),
        }))
      : null;

    // Ketuk produk → panel khusus (satu area scroll); daftar utama, filter & posisi tidak berubah.
    return <button type="button" key={p.name} className="stock-prod-row" onClick={() => setOpenProd(p.name)}
      data-testid={`stock-polosan-acc1-${prodTestKey}`}>
      <span style={{display:"flex", flexDirection:"column", gap:2, minWidth:0, textAlign:"left"}}>
        <b className="stock-item-name" style={{color:"var(--ink,#0f0f0f)", overflowWrap:"anywhere"}} data-testid={`stock-polosan-name-${prodTestKey}`}>{p.name}</b>
        <span className="muted small" style={{fontSize:11}}>{prodSubtitle}</span>
      </span>
      <span style={{display:"flex", gap:4, flexWrap:"wrap", alignItems:"center", flex:"none"}}>
        {alerts.habis > 0 && <span className="chip chip-red small" data-testid={`stock-polosan-acc1-habis-${prodTestKey}`}>Habis {alerts.habis}</span>}
        {alerts.menipis > 0 && <span className="chip chip-amber small" data-testid={`stock-polosan-acc1-menipis-${prodTestKey}`}>Menipis {alerts.menipis}</span>}
        <span aria-hidden="true" style={{fontSize:18, marginLeft:2, color:"var(--muted,#94a3b8)"}}>›</span>
      </span>
    </button>;
  })}
  {openP && <StockProductPanel product={openP} filterActive={filterActive} isOwner={isOwner} onOpenDetail={onOpenDetail}
    onChanged={onChanged} narrow={narrow} countAlerts={countAlerts} sleeveLabel={sleeveLabel} onClose={() => setOpenProd(null)}/>}
  </>;
}

// Tab lengan panel: muncul bila ≥2 lengan; PDH Nagata Drill selalu Panjang | Pendek (tanpa membuat varian).
const validSleeve = (s) => s && s !== "tidak_berlaku";
const inTabOf = (v, t) => t === "__lain" ? !validSleeve(v.sleeve) : (v.sleeve || "") === t;
export function sleeveTabsFor(name, rows) {
  const real = Array.from(new Set(rows.map(v => v.sleeve || ""))).filter(validSleeve);
  const forced = /pdh/i.test(name || "") && /nagata/i.test(name || "");
  const sleeves = Array.from(new Set([...real, ...(forced ? ["panjang", "pendek"] : [])])).sort();
  const hasOther = sleeves.length >= 2 && rows.some(v => !validSleeve(v.sleeve));
  return sleeves.length >= 2 ? [...sleeves, ...(hasOther ? ["__lain"] : [])] : [];
}

// Panel produk: judul tetap di atas (sticky), pencarian warna/ukuran, satu area scroll. Memakai daftar warna/ukuran yang sama.
export function StockProductPanel({ product, filterActive, isOwner, onOpenDetail, onChanged, narrow, countAlerts, sleeveLabel, onClose }) {
  const [q, setQ] = useState("");
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  React.useEffect(() => lockBodyScroll(), []);
  const qq = q.trim().toLowerCase();
  const toks = qq.split(/\s+/).filter(Boolean);
  const rows = toks.length ? product.rows.filter(v => toks.every(t => (v.color || "").toLowerCase().includes(t) || (v.size || "").toLowerCase().startsWith(t))) : product.rows;
  const validSl = (s) => s && s !== "tidak_berlaku";
  const sleeves = Array.from(new Set(product.rows.map(v => v.sleeve || ""))).filter(validSl).sort();
  const tabs = sleeveTabsFor(product.name, product.rows);
  const [tab, setTab] = useState(tabs.find(t => product.rows.some(v => inTabOf(v, t))) || tabs[0] || "");
  const inTab = (v, t) => t === "__lain" ? !validSl(v.sleeve) : (v.sleeve || "") === t;
  const nColors = new Set(product.rows.map(v => `${v.sleeve || ""}|${svmColorKey(v.color)}`)).size;
  const sub = [`${nColors} warna`, `${product.rows.length} varian`, sleeves.length ? `Lengan ${sleeves.map(sleeveLabel).join(" / ")}` : ""].filter(Boolean).join(" · ");
  const expandAll = filterActive || !!qq;
  const shown = tabs.length ? rows.filter(v => inTab(v, tab)) : rows;
  return <div className="stock-prod-backdrop" onClick={onClose} data-testid="stock-product-panel-backdrop">
  <div className="stock-prod-panel" role="dialog" aria-modal="true" aria-label={product.name} data-testid="stock-product-panel" onClick={e => e.stopPropagation()}>
    <div className="stock-prod-panel-head">
      <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8}}>
        <div style={{minWidth:0}}>
          <h2 className="stock-prod-panel-title" data-testid="stock-product-panel-title">{product.name}</h2>
          <div className="muted stock-prod-panel-sub" data-testid="stock-product-panel-sub">{sub}</div>
          {isOwner && <VariantManageButtons product={product} onChanged={onChanged}/>}
        </div>
        <button type="button" className="stock-prod-close" onClick={onClose} aria-label="Tutup" data-testid="stock-product-panel-close">×</button>
      </div>
      <input className="stock-prod-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Cari warna atau ukuran" aria-label="Cari warna atau ukuran"
        data-testid="stock-product-panel-search"/>
      {tabs.length > 0 && <div className="stock-sleeve-tabs" role="tablist" data-testid="stock-sleeve-tabs">
        {tabs.map(t => { const n = rows.filter(v => inTab(v, t)).length; return <button key={t} type="button" role="tab" aria-selected={tab === t}
          className={`stock-sleeve-tab${tab === t ? " is-active" : ""}`} onClick={() => setTab(t)} data-testid={`stock-sleeve-tab-${t}`}>
          {t === "__lain" ? "Lainnya" : sleeveLabel(t)}{qq ? ` (${n})` : ""}
        </button>; })}
      </div>}
    </div>
    <div className="stock-prod-panel-body" data-testid="stock-product-panel-body">
      {shown.length === 0 ? <p className="muted" data-testid="stock-product-panel-empty">
          {tab && tab !== "__lain" && !product.rows.some(v => inTab(v, tab)) ? `Belum ada varian ${sleeveLabel(tab)}` : "Tidak ada warna/ukuran yang cocok."}</p>
        : <PolosanColorList key={tab} productName={product.name} sleeveLbl={tab} rows={shown} expandAll={expandAll} isOwner={isOwner}
            onOpenDetail={onOpenDetail} onChanged={onChanged} countAlerts={countAlerts}/>}
    </div>
  </div>
  </div>;
}

// Kartu warna ringkas: semua tertutup di awal, satu terbuka sekaligus; saat cari/filter semua hasil terbuka & bisa dibandingkan.
function PolosanColorList({ productName, sleeveLbl, rows, expandAll, isOwner, onOpenDetail, onChanged, countAlerts }) {
  const colors = useMemo(() => {
    const by = new Map();
    for (const v of rows) {
      const c = svmColorKey(v.color) || "—";
      if (!by.has(c)) by.set(c, []);
      by.get(c).push(v);
    }
    const arr = Array.from(by.values()).map((r) => {
      r.sort((a, b) => sizeRank(a.size) - sizeRank(b.size) || (a.size || "").localeCompare(b.size || ""));
      return { color: colorLabel(r) || "—", rows: r };
    });
    arr.sort((a, b) => a.color.localeCompare(b.color));
    return arr;
  }, [rows]);
  const colorKey = colors.map(c => c.color).join("|");
  const [openSet, setOpenSet] = useState(() => new Set(expandAll ? colors.map(c => c.color) : []));
  useEffect(() => { setOpenSet(new Set(expandAll ? colorKey.split("|") : [])); }, [expandAll, colorKey]);
  const toggle = (c) => setOpenSet(prev => {
    if (expandAll) { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; }
    return prev.has(c) ? new Set() : new Set([c]);
  });

  return <>{colors.map(c => {
    const alerts = countAlerts(c.rows);
    const testKey = `${productName}-${sleeveLbl || "x"}-${c.color}`.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
    const needsReview = c.rows.some(v => v.needs_review);
    const isOpen = openSet.has(c.color);
    return <div key={c.color} className={`stock-color-card${isOpen ? " is-open" : ""}`} data-testid={`stock-polosan-acc3-${testKey}`}>
      <button type="button" className="stock-color-head" aria-expanded={isOpen} onClick={() => toggle(c.color)} data-testid={`stock-polosan-acc3-summary-${testKey}`}>
        <span className="stock-color-bar" aria-hidden="true"/>
        <span style={{display:"flex", flexDirection:"column", gap:2, minWidth:0, flex:"1 1 auto", textAlign:"left"}}>
          <b className="stock-color-name">{c.color}</b>
          <span className="muted stock-color-meta">{money(alerts.totalPcs)} pcs · {c.rows.length} ukuran</span>
        </span>
        <span style={{display:"flex", gap:4, flexWrap:"wrap", alignItems:"center", justifyContent:"flex-end", flex:"none"}}>
          {needsReview && <span className="chip chip-amber small" title="Salah satu varian ditandai perlu ditinjau">Perlu Ditinjau</span>}
          {alerts.habis > 0 && <span className="chip chip-red small">Habis {alerts.habis}</span>}
          {alerts.menipis > 0 && <span className="chip chip-amber small">Menipis {alerts.menipis}</span>}
          <ChevronDown aria-hidden="true" className="stock-color-caret" size={20}/>
        </span>
      </button>
      {isOpen && <div className="stock-color-body">
        <PolosanSizeRows rows={c.rows} isOwner={isOwner} onOpenDetail={onOpenDetail} onChanged={onChanged}/>
      </div>}
    </div>;
  })}</>;
}



export function RowDetailButton({ variant, onOpenDetail, isOwner, onChanged, compact }) {
  const [open, setOpen] = useState(false);
  const unit = "pcs";
  const minText = (variant.min_stock === null || variant.min_stock === undefined)
    ? "Minimum belum diatur"
    : `Minimum: ${variant.min_stock} ${unit}`;
  return <>
    <button type="button"
      className={compact ? "stock-detail-btn" : "soft-btn"}
      onClick={() => setOpen(true)}
      data-testid={`stock-polosan-row-detail-${variant.id}`}
      aria-label={compact ? `Detail ${variant.size || ""}` : undefined}
      style={compact ? undefined : {padding:"6px 10px", fontSize:12, minHeight:32}}>
      Detail
    </button>
    {open && <StockDetailModal onClose={()=>setOpen(false)} testid={`stock-polosan-detail-modal-${variant.id}`}>
        <h3 style={{margin:"0 0 6px", fontSize:16}}>{variant.product_name} · {variant.size}</h3>
        <p className="muted small" style={{margin:"0 0 4px"}}>
          {variant.sleeve ? `Lengan ${SLEEVE_LABEL[variant.sleeve] || variant.sleeve}` : "Lengan belum diisi"} · {variant.color}
        </p>
        <p className="muted small" style={{margin:"0 0 4px"}}>SKU: <span style={{fontFamily:"monospace"}}>{variant.sku}</span></p>
        <div className="sdm-saldo">
          <p className="muted small" style={{margin:0}}>Saldo saat ini: <b>{fmtQty(variant.current_qty, unit)}</b> tersedia di Gudang · {minText}</p>
          {isOwner && <VariantEdit variant={variant} label="Atur Minimum" onSaved={()=>{ setOpen(false); onChanged(); }}/>}
        </div>
        {showJahitPosition(variant) && <JahitPosition variantId={variant.id}/>}
        {isOwner && <div style={{display:"flex", gap:6, flexWrap:"wrap", marginTop:8}}>
          <VariantColorEdit variant={variant} onSaved={()=>{ setOpen(false); onChanged(); }}/>
        </div>}
        <div className="sdm-actions">
          <button type="button" className="soft-btn"
            onClick={()=>{ setOpen(false); onOpenDetail(variant); }}
            data-testid={`stock-polosan-row-mutasi-${variant.id}`}>
            Lihat Mutasi
          </button>
          <button type="button" className="primary" onClick={()=>setOpen(false)} data-testid={`stock-polosan-detail-close-${variant.id}`}>Tutup</button>
        </div>
    </StockDetailModal>}
  </>;
}

function StockDetailModal({ onClose, testid, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(<div className="modal-backdrop sdm-backdrop" onClick={onClose}>
    <div className="sdm-panel" onClick={e=>e.stopPropagation()} data-testid={testid}>{children}</div>
  </div>, document.body);
}



// ============================================================
// KainGroupedList — Iter 270: kelompokkan kartu per Nama Kain + Merek + Satuan
// Baris di dalam kartu = per Warna. Roll/meter/kg tidak digabung (unit ikut kunci).
// Pencarian & filter sudah diterapkan di parent; jangan tampilkan kelompok kosong.
// Pengelompokan hanya untuk tampilan — ID varian, saldo, satuan, mutasi tetap terpisah.
// ============================================================
function KainGroupedList({ variants, isOwner, onOpenDetail, onChanged }) {
  const narrow = useIsNarrow(640);
  const shop = useShop();
  const shopOn = !!shop?.shopMode;
  const groups = useMemo(() => {
    const byKey = new Map();
    for (const v of variants) {
      const fabric = v.fabric_name || "—";
      const brand = v.brand || "";
      const unit = v.unit || "";
      const key = `${fabric}||${brand}||${unit}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          fabric_name: fabric,
          brand,
          unit,
          needs_review: false,
          rows: [],
        });
      }
      const g = byKey.get(key);
      g.rows.push(v);
      if (v.needs_review) g.needs_review = true;
    }
    for (const g of byKey.values()) {
      g.rows.sort((a, b) => (a.color || "").localeCompare(b.color || ""));
    }
    return Array.from(byKey.values()).sort((a, b) =>
      a.fabric_name.localeCompare(b.fabric_name) ||
      a.brand.localeCompare(b.brand) ||
      a.unit.localeCompare(b.unit)
    );
  }, [variants]);

  return <>{groups.map(g => {
    const titleParts = [g.fabric_name];
    if (g.brand) titleParts.push(g.brand);
    titleParts.push(`Satuan ${g.unit || "—"}`);
    const title = titleParts.join(" · ");
    const testKey = g.key.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
    const unitShort = g.unit || "";
    return <article key={g.key} className="at-prod-card stock-kain-card"
      data-testid={`stock-kain-group-${testKey}`}>
      <header className="stock-kain-card-head">
        <b className="stock-kain-card-title">{title}</b>
        {g.needs_review && <span className="chip chip-amber small" title="Salah satu varian ditandai perlu ditinjau">Perlu Ditinjau</span>}
        {isOwner && <KainColorManageButton group={g} onChanged={onChanged}/>}
      </header>
      {narrow ? (
        <ul className="stock-kain-rows" style={{listStyle:"none", padding:0, margin:0, display:"flex", flexDirection:"column"}}>
          {g.rows.map(v => {
            const sMeta = STATUS_LABEL[v.status] || STATUS_LABEL.min_belum_diatur;
            const minSet = v.min_stock !== null && v.min_stock !== undefined;
            return <li key={v.id} className="stock-kain-row" data-testid={`stock-kain-row-${v.id}`}>
              <div className="stock-kain-row-name">
                {v.color || "—"}
              </div>
              <div className="stock-kain-row-meta">
                <span className="stock-kain-row-stat" style={{fontVariantNumeric:"tabular-nums"}}><span className="stock-kain-row-key">Stok</span><b>{fmtQty(v.current_qty, unitShort)}</b></span>
                <span className={`stock-kain-row-stat${minSet ? "" : " is-empty"}`} style={{fontVariantNumeric:"tabular-nums"}}
                  data-testid={`stock-kain-row-min-${v.id}`}>
                  <span className="stock-kain-row-key">Min</span>{minSet ? `${v.min_stock} ${unitShort}` : "—"}
                </span>
                <span className={`chip ${sMeta.cls} small stock-kain-row-chip`}>{sMeta.label}</span>
                <span className="stock-kain-row-cta">
                  <StockCorrectButton variant={v} onChanged={onChanged}/><KainRowDetailButton variant={v} onOpenDetail={onOpenDetail} isOwner={isOwner} onChanged={onChanged}/>
                </span>
              </div>
              {shopOn && <ShopControls variant={v} unit={unitShort || v.unit}/>}
            </li>;
          })}
        </ul>
      ) : (
      <div className="stock-kain-table-wrap">
        <table className="stock-kain-table" style={{width:"100%", fontSize:13, borderCollapse:"collapse", tableLayout:"fixed"}}>
          <colgroup>
            <col style={{width:"34%"}}/>
            <col style={{width:"20%"}}/>
            <col style={{width:"18%"}}/>
            <col style={{width:"18%"}}/>
            <col style={{width:"10%"}}/>
          </colgroup>
          <thead>
            <tr>
              <th style={{textAlign:"left"}}>Warna</th>
              <th style={{textAlign:"right"}}>Stok</th>
              <th style={{textAlign:"right"}}>Minimum</th>
              <th style={{textAlign:"center"}}>Status</th>
              <th style={{textAlign:"right"}}></th>
            </tr>
          </thead>
          <tbody>
            {g.rows.map(v => {
              const sMeta = STATUS_LABEL[v.status] || STATUS_LABEL.min_belum_diatur;
              const minSet = v.min_stock !== null && v.min_stock !== undefined;
              return <React.Fragment key={v.id}>
                <tr data-testid={`stock-kain-row-${v.id}`} className="stock-kain-tr">
                <td className="stock-kain-td-name" style={{overflowWrap:"anywhere", wordBreak:"break-word"}}>{v.color || "—"}</td>
                <td className="stock-kain-td-num" style={{textAlign:"right", fontVariantNumeric:"tabular-nums"}}>
                  {fmtQty(v.current_qty, unitShort)}
                </td>
                <td className={`stock-kain-td-num${minSet ? "" : " is-empty"}`} style={{textAlign:"right", fontVariantNumeric:"tabular-nums"}}
                  data-testid={`stock-kain-row-min-${v.id}`}>
                  {minSet ? `${v.min_stock} ${unitShort}` : "Belum diatur"}
                </td>
                <td style={{textAlign:"center"}}>
                  <span className={`chip ${sMeta.cls} small`}>{sMeta.label}</span>
                </td>
                <td style={{textAlign:"right"}}>
                  <StockCorrectButton variant={v} onChanged={onChanged}/><KainRowDetailButton variant={v} onOpenDetail={onOpenDetail} isOwner={isOwner} onChanged={onChanged}/>
                </td>
              </tr>
              {shopOn && <tr className="stock-kain-tr-shop">
                <td colSpan={5}>
                  <ShopControls variant={v} unit={unitShort || v.unit}/>
                </td>
              </tr>}
              </React.Fragment>;
            })}
          </tbody>
        </table>
      </div>
      )}
    </article>;
  })}</>;
}

function KainRowDetailButton({ variant, onOpenDetail, isOwner, onChanged }) {
  const [open, setOpen] = useState(false);
  const unit = variant.unit || "";
  const minText = (variant.min_stock === null || variant.min_stock === undefined)
    ? "Minimum: Belum diatur"
    : `Minimum: ${variant.min_stock} ${unit}`;
  return <>
    <button type="button"
      className="soft-btn stock-kain-row-detail-btn"
      onClick={() => setOpen(true)}
      data-testid={`stock-kain-row-detail-${variant.id}`}>
      Detail
    </button>
    {open && <div className="modal-backdrop" onClick={()=>setOpen(false)}>
      <div className="modal-panel" onClick={e=>e.stopPropagation()} style={{maxWidth:420}}>
        <h3 style={{margin:"0 0 6px", fontSize:16}}>{variant.fabric_name || "—"} · {variant.color || "—"}</h3>
        <p className="muted small" style={{margin:"0 0 4px"}}>
          {[variant.brand, `Satuan ${unit || "—"}`].filter(Boolean).join(" · ")}
        </p>
        <p className="muted small" style={{margin:"0 0 4px"}}>SKU: <span style={{fontFamily:"monospace"}}>{variant.sku}</span></p>
        <p className="muted small" style={{margin:"0 0 12px"}}>Saldo saat ini: <b>{fmtQty(variant.current_qty, unit)}</b> · {minText}</p>
        <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
          <button type="button" className="soft-btn small-btn"
            onClick={()=>{ setOpen(false); onOpenDetail(variant); }}
            data-testid={`stock-kain-row-mutasi-${variant.id}`}>
            Lihat Mutasi
          </button>
          {isOwner && <VariantEdit variant={variant} onSaved={()=>{ setOpen(false); onChanged(); }}/>}
          {isOwner && <VariantColorEdit variant={variant} onSaved={()=>{ setOpen(false); onChanged(); }}/>}
        </div>
        <div className="modal-actions" style={{marginTop:12}}>
          <button type="button" className="primary" onClick={()=>setOpen(false)}>Tutup</button>
        </div>
      </div>
    </div>}
  </>;
}


// ============================================================
// KainAccordionList — Iter 273: kelompok buka-tutup untuk Daftar Stok Kain.
// Level 1 = fabric_name (atau super-grup "Bahan Jersey" untuk anggota Jersey).
// Level 2 (khusus super Jersey) = per fabric_name (Brazil, Milano, dst).
// Leaf = KainGroupedList existing (per fabric+brand+unit, tiap warna baris).
// Merek/satuan tetap dipisah — tidak ada penjumlahan roll+meter+kg.
// Saat filter aktif, semua group auto-open agar hasil pencarian langsung terlihat.
// ============================================================
const JERSEY_MEMBERS_NORM = new Set([
  "BRAZIL",
  "MILANO",
  "MILANO PREMIUM",
  "EMBOS",
  "WAFFLE",
  "DRIFIT",
  "AKTIF AIR",
  "AKTIF DRY MARTHON",
  // Iter 274 — tambahan (identitas masing-masing tetap; hanya pengelompokan tampilan).
  "KOLOR BRAZIL",
  "KOLOR EMBOS",
  "KOLOR MILANO",
  "MILENIAL",
]);
const isJerseyFabric = (name) => JERSEY_MEMBERS_NORM.has(String(name || "").trim().toUpperCase());

function KainAccordionList({ variants, filter, isOwner, onOpenDetail, onChanged }) {
  const filterActive = !!(
    filter && (
      filter.q || filter.status || filter.fabric_name || filter.brand || filter.color || filter.unit
    )
  );

  // Level-1 buckets
  const level1 = useMemo(() => {
    const byKey = new Map();
    for (const v of variants) {
      const fabric = v.fabric_name || "—";
      const isJer = isJerseyFabric(fabric);
      const key = isJer ? "__jersey__" : `fabric::${fabric}`;
      const label = isJer ? "Bahan Jersey" : fabric;
      if (!byKey.has(key)) byKey.set(key, { key, label, isJersey: isJer, rows: [] });
      byKey.get(key).rows.push(v);
    }
    const arr = Array.from(byKey.values());
    arr.sort((a, b) => {
      // Bahan Jersey selalu di paling bawah agar bahan utama tampil dulu — kecuali user filter jersey saja.
      if (a.isJersey !== b.isJersey) return a.isJersey ? 1 : -1;
      return a.label.localeCompare(b.label);
    });
    return arr;
  }, [variants]);

  const countAlerts = (rows) => {
    let habis = 0, menipis = 0;
    for (const v of rows) {
      if (v.status === "habis") habis++;
      else if (v.status === "menipis") menipis++;
    }
    return { habis, menipis };
  };

  return <>{level1.map(g => {
    const testKey = g.key.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
    const alerts = countAlerts(g.rows);
    // level-2 (khusus jersey super): per fabric_name
    let subgroups = null;
    if (g.isJersey) {
      const byFabric = new Map();
      for (const v of g.rows) {
        const f = v.fabric_name || "—";
        if (!byFabric.has(f)) byFabric.set(f, []);
        byFabric.get(f).push(v);
      }
      subgroups = Array.from(byFabric.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([label, rows]) => ({ key: `jersey::${label}`, label, rows }));
    }
    // Level-1 header ringkas
    const uniqueColors = new Set(g.rows.map(v => `${v.fabric_name}|${v.color}|${v.unit}`)).size;
    const summaryText = g.isJersey
      ? `${subgroups?.length || 0} jenis · ${uniqueColors} varian warna`
      : `${uniqueColors} varian warna`;

    return <details key={g.key}
      className="stock-accordion"
      data-testid={`stock-kain-acc1-${testKey}`}
      open={filterActive || undefined}
      style={{
        background:"var(--wash-1,#fffbf1)",
        border:"1px solid var(--line,#e2e8f0)",
        borderRadius:12,
        overflow:"hidden",
      }}>
      <summary style={{
        listStyle:"none", cursor:"pointer",
        padding:"10px 12px",
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
        background:"var(--wash-2,#f4efdf)",
      }} data-testid={`stock-kain-acc1-summary-${testKey}`}>
        <span style={{display:"flex", flexDirection:"column", gap:2, minWidth:0}}>
          <b className="stock-item-name" style={{color:"var(--ink,#0f0f0f)", overflowWrap:"anywhere"}} data-testid={`stock-kain-name-${testKey}`}>{g.label}</b>
          <span className="muted small" style={{fontSize:11}}>{summaryText}</span>
        </span>
        <span style={{display:"flex", gap:4, flexWrap:"wrap", alignItems:"center", flex:"none"}}>
          {alerts.habis > 0 && <span className="chip chip-red small" data-testid={`stock-kain-acc1-habis-${testKey}`}>Habis {alerts.habis}</span>}
          {alerts.menipis > 0 && <span className="chip chip-amber small" data-testid={`stock-kain-acc1-menipis-${testKey}`}>Menipis {alerts.menipis}</span>}
          <span aria-hidden="true" className="stock-accordion-caret" style={{fontSize:14, marginLeft:2, color:"var(--muted,#94a3b8)"}}>▾</span>
        </span>
      </summary>
      <div style={{padding:8, display:"flex", flexDirection:"column", gap:8}}>
        {g.isJersey && subgroups
          ? subgroups.map(sg => {
              const subKey = sg.key.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
              const subAlerts = countAlerts(sg.rows);
              const subColors = new Set(sg.rows.map(v => `${v.color}|${v.unit}`)).size;
              return <details key={sg.key}
                className="stock-accordion stock-accordion-sub"
                data-testid={`stock-kain-acc2-${subKey}`}
                open={filterActive || undefined}
                style={{
                  background:"var(--wash-1,#fffbf1)",
                  border:"1px solid var(--line,#e2e8f0)",
                  borderRadius:10,
                  overflow:"hidden",
                }}>
                <summary style={{
                  listStyle:"none", cursor:"pointer",
                  padding:"8px 10px",
                  display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
                }} data-testid={`stock-kain-acc2-summary-${subKey}`}>
                  <span style={{display:"flex", flexDirection:"column", gap:2, minWidth:0}}>
                    <b style={{fontSize:13, color:"var(--ink,#0f0f0f)", overflowWrap:"anywhere"}}>{sg.label}</b>
                    <span className="muted small" style={{fontSize:11}}>{subColors} varian warna</span>
                  </span>
                  <span style={{display:"flex", gap:4, flexWrap:"wrap", alignItems:"center", flex:"none"}}>
                    {subAlerts.habis > 0 && <span className="chip chip-red small">Habis {subAlerts.habis}</span>}
                    {subAlerts.menipis > 0 && <span className="chip chip-amber small">Menipis {subAlerts.menipis}</span>}
                    <span aria-hidden="true" className="stock-accordion-caret" style={{fontSize:13, marginLeft:2, color:"var(--muted,#94a3b8)"}}>▾</span>
                  </span>
                </summary>
                <div style={{padding:8, display:"flex", flexDirection:"column", gap:8}}>
                  <KainGroupedList variants={sg.rows} isOwner={isOwner} onOpenDetail={onOpenDetail} onChanged={onChanged}/>
                </div>
              </details>;
            })
          : <KainGroupedList variants={g.rows} isOwner={isOwner} onOpenDetail={onOpenDetail} onChanged={onChanged}/>
        }
      </div>
    </details>;
  })}</>;
}




// Produk aktif di Master Produk yang belum punya varian stok → Owner bisa langsung Tambah Varian (stok kosong; saldo diisi lewat alur biasa).
export function missingStockProducts(products, variants) {
  const has = new Set((variants || []).map(v => v.product_id));
  return (products || []).filter(p => p.is_active !== false && !p.is_customer_material && !has.has(p.id || p._id));
}

function MissingStockProducts({ products, variants, onAdd }) {
  const list = missingStockProducts(products, variants);
  if (!list.length) return null;
  return <details className="stock-missing" data-testid="stock-missing-products">
    <summary>Produk belum ada di Stok ({list.length})</summary>
    <p className="muted small" style={{margin:"6px 0"}}>Produk aktif di Master Produk yang belum punya varian stok. Tambahkan variannya agar bisa terpotong otomatis saat ACC.</p>
    <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
      {list.map(p => <button key={p.id} type="button" className="soft-btn small-btn" onClick={() => onAdd(p.id)} data-testid={`stock-missing-add-${p.id}`}>
        <PlusCircle size={13}/>{p.name}
      </button>)}
    </div>
  </details>;
}

function VariantForm({ category, products, onClose, onSaved, initialPid }) {
  const isKain = category === "kain";
  // Polosan states
  const [pid, setPid] = useState(initialPid || products[0]?.id || products[0]?._id || "");
  const [sleeve, setSleeve] = useState("pendek");
  const [size, setSize] = useState("");
  // Kain states
  const [fabricName, setFabricName] = useState("");
  const [brand, setBrand] = useState("");
  const [unit, setUnit] = useState("meter");
  // Shared
  const [color, setColor] = useState("");
  const [minStock, setMinStock] = useState("");
  const [usesStock, setUsesStock] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      let body;
      if (isKain) {
        body = {
          category: "kain",
          fabric_name: fabricName, brand, color, unit,
          min_stock: minStock === "" ? null : Number(minStock),
          uses_stock: usesStock,
        };
      } else {
        const chosen = products.find(p=>p.id===pid || p._id===pid) || {};
        body = {
          category: "polosan",
          product_id: pid, product_name: chosen.name || "",
          sleeve, color, size,
          min_stock: minStock === "" ? null : Number(minStock),
          uses_stock: usesStock,
        };
      }
      await axios.post(`${API}/stock/variants`, body);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Gagal buat varian"); }
    finally { setBusy(false); }
  };
  const canSubmit = isKain ? !!fabricName && !!color && !!unit : !!pid && !!color && !!size && !!sleeve;
  return <div className="modal-backdrop" data-testid="stock-variant-form">
    <div className="modal-panel">
      <h3>Tambah Varian {isKain ? "Kain" : "Polosan"}</h3>
      {err && <div className="warn-box small">{err}</div>}
      {isKain ? <>
        <label className="modal-field">Nama / Jenis Kain
          <input value={fabricName} onChange={e=>setFabricName(e.target.value)} placeholder="Cotton Combed 24s / Drifit / …" data-testid="stock-variant-fabric-name"/>
        </label>
        <label className="modal-field">Merek / Seri (opsional)
          <input value={brand} onChange={e=>setBrand(e.target.value)} placeholder="misal Nagata / lokal / impor" data-testid="stock-variant-brand"/>
        </label>
        <label className="modal-field">Warna
          <input value={color} onChange={e=>setColor(e.target.value)} data-testid="stock-variant-color"/>
        </label>
        <label className="modal-field">Satuan Utama
          <select value={unit} onChange={e=>setUnit(e.target.value)} data-testid="stock-variant-unit">
            <option value="meter">meter</option>
            <option value="kg">kg</option>
            <option value="roll">roll</option>
          </select>
        </label>
        <label className="modal-field">Stok Minimum (opsional, dalam {unit})
          <input type="number" min={0} step={unit === "roll" ? 1 : "0.001"} value={minStock} onChange={e=>setMinStock(e.target.value)}/>
        </label>
      </> : <>
        <label className="modal-field">Produk
          <select value={pid} onChange={e=>setPid(e.target.value)} data-testid="stock-variant-product">
            <option value="">— pilih —</option>
            {products.map(p => <option key={p.id||p._id} value={p.id||p._id}>{p.name}</option>)}
          </select>
        </label>
        <label className="modal-field">Lengan
          <select value={sleeve} onChange={e=>setSleeve(e.target.value)} data-testid="stock-variant-sleeve">
            <option value="pendek">Pendek</option>
            <option value="panjang">Panjang</option>
            <option value="tidak_berlaku">Tidak Berlaku (produk tanpa pembeda lengan)</option>
          </select>
        </label>
        <label className="modal-field">Warna
          <input value={color} onChange={e=>setColor(e.target.value)} data-testid="stock-variant-color"/>
        </label>
        <label className="modal-field">Ukuran
          <input value={size} onChange={e=>setSize(e.target.value)} placeholder="S / M / L / XL / …" data-testid="stock-variant-size"/>
        </label>
        <label className="modal-field">Stok Minimum (opsional, dalam pcs)
          <input type="number" min={0} step={1} value={minStock} onChange={e=>setMinStock(e.target.value)}/>
        </label>
      </>}
      <label className="modal-check"><input type="checkbox" checked={usesStock} onChange={e=>setUsesStock(e.target.checked)}/><span>Varian ini menggunakan stok</span></label>
      <div className="modal-actions">
        <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
        <button type="button" className="primary" onClick={submit} disabled={busy || !canSubmit} data-testid="stock-variant-form-submit">{busy?"…":"Simpan Varian"}</button>
      </div>
    </div>
  </div>;
}

// ============================================================
// ImportModal — Owner impor CSV/Excel massal (varian + saldo awal)
// ============================================================
const IMPORT_HEADERS = ["category","product_name","sleeve","fabric_name","brand","color","size","unit","min_stock","saldo_awal","note"];
const IMPORT_HINT = [
  "Kolom (case-insensitive): category, product_name, sleeve, fabric_name, brand, color, size, unit, min_stock, saldo_awal, note",
  "• category: 'polosan' atau 'kain'",
  "• Polosan wajib: category, product_name (harus persis nama di Master Produk), sleeve ('pendek'|'panjang'|'tidak_berlaku'), color, size",
  "• Kain wajib: category, fabric_name, color, unit ('meter'|'kg'|'roll'). brand opsional.",
  "• min_stock & saldo_awal opsional (JANGAN diisi angka buatan jika data asli tidak ada). Saldo awal roll/pcs dibulatkan integer; meter/kg boleh desimal (titik atau koma).",
  "• Baris kosong / hanya '-' akan dilewati sebelum upload. Duplikat (varian sudah ada / duplikat dalam file) akan ditandai.",
].join("\n");

function parseCsvText(text) {
  const rows = [];
  let cur = ""; let inQ = false; const line = [];
  const commitCell = () => { line.push(cur); cur = ""; };
  const commitLine = () => { rows.push([...line]); line.length = 0; };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i+1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQ = false; }
      else { cur += ch; }
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") commitCell();
      else if (ch === "\r") { /* skip */ }
      else if (ch === "\n") { commitCell(); commitLine(); }
      else cur += ch;
    }
  }
  if (cur.length || line.length) { commitCell(); commitLine(); }
  return rows.filter(r => r.some(c => (c||"").trim() !== ""));
}

function ImportModal({ products, onClose, onDone }) {
  const [rawRows, setRawRows] = useState([]);  // parsed rows [{...raw fields}]
  const [validation, setValidation] = useState([]);  // per-row {ok, errors[], warn[]}
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [result, setResult] = useState(null);
  const [countedAt, setCountedAt] = useState(localISO());
  const [fileName, setFileName] = useState("");
  const [clientKey] = useState(() => uuid());

  const productNames = useMemo(() => new Set(products.map(p => (p.name||"").toLowerCase())), [products]);

  const handleFile = async (file) => {
    setMsg(""); setResult(null); setRawRows([]); setValidation([]);
    if (!file) return;
    setFileName(file.name);
    try {
      const ext = file.name.split(".").pop().toLowerCase();
      let rows;
      if (ext === "csv" || ext === "txt") {
        const text = await file.text();
        const grid = parseCsvText(text);
        if (grid.length < 2) { setMsg("File kosong atau hanya header"); return; }
        const headers = grid[0].map(h => (h||"").trim().toLowerCase());
        rows = grid.slice(1).map(r => {
          const obj = {};
          headers.forEach((h,i) => { obj[h] = (r[i]||"").trim(); });
          return obj;
        });
      } else if (ext === "xlsx" || ext === "xls") {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const grid = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
        rows = grid.map(o => {
          const lc = {};
          Object.entries(o).forEach(([k,v]) => { lc[String(k).trim().toLowerCase()] = String(v||"").trim(); });
          return lc;
        });
      } else {
        setMsg("Format tidak didukung. Gunakan .csv, .xlsx, atau .xls");
        return;
      }
      // Validate per row
      const val = rows.map(r => validateRow(r, productNames));
      setRawRows(rows); setValidation(val);
      if (rows.length === 0) setMsg("Tidak ada baris data setelah header");
    } catch (e) {
      setMsg("Gagal membaca file: " + (e.message || String(e)));
    }
  };

  const validRows = validation.map((v,i) => v.ok ? i : null).filter(i => i !== null);
  const errCount = validation.filter(v => !v.ok).length;

  const submit = async () => {
    if (!validRows.length) { setMsg("Tidak ada baris valid untuk diimpor"); return; }
    setBusy(true); setMsg("");
    try {
      const rowsPayload = validRows.map(i => normalizeRow(rawRows[i]));
      const r = await axios.post(`${API}/stock/import`, {
        rows: rowsPayload,
        counted_at: countedAt,
        client_key: clientKey,
      });
      setResult(r.data);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal impor");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const rows = [
      IMPORT_HEADERS,
      ["polosan","Polo PE","pendek","","","Maroon","L","","5","25","stok awal"],
      ["polosan","Polo PE","panjang","","","Maroon","L","","","","varian lengan panjang"],
      ["polosan","Kaos Combed 30s","tidak_berlaku","","","Hitam","XL","","10","","produk tanpa pembeda lengan"],
      ["kain","","","Cotton Combed 24s","Nagata","Hitam","","meter","20","150.75","gulung besar"],
      ["kain","","","Drifit","","Putih","","kg","5","12.4",""],
      ["kain","","","Cotton Combed 30s","Lokal","Putih","","roll","2","6","6 gulung standar"],
    ];
    const csv = rows.map(r => r.map(c => /[,"\n]/.test(c) ? `"${String(c).replace(/"/g,'""')}"` : c).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "template-impor-stok.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return <div className="modal-backdrop" data-testid="stock-import-modal">
    <div className="modal-panel" style={{maxWidth: 860, maxHeight: "88vh", overflowY: "auto"}}>
      <h3 style={{margin:"0 0 6px"}}>Impor Stok dari CSV/Excel</h3>
      <p className="muted small" style={{whiteSpace:"pre-line", margin:"0 0 10px"}}>{IMPORT_HINT}</p>
      {msg && <div className="warn-box small" data-testid="stock-import-msg">{msg}</div>}
      {!result && <>
        <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"center"}}>
          <button type="button" className="soft-btn small-btn" onClick={downloadTemplate} data-testid="stock-import-template">
            <Download size={14}/> Unduh Template
          </button>
          <a
            href="/impor-stok-polosan-final-29-sep-2026.csv"
            download="impor-stok-polosan-final-29-sep-2026.csv"
            className="soft-btn small-btn"
            style={{textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6}}
            data-testid="stock-import-final-csv"
          >
            <Download size={14}/> Unduh CSV Final (1.465 baris · 19.865 pcs)
          </a>
          <label className="modal-field" style={{margin:0}}>
            <input type="file" accept=".csv,.xlsx,.xls,.txt"
              onChange={e => handleFile(e.target.files?.[0])} data-testid="stock-import-file"/>
          </label>
          {fileName && <span className="muted small">{fileName}</span>}
        </div>
        <label className="modal-field" style={{marginTop:8}}>Tanggal saldo awal (untuk baris dengan `saldo_awal`)
          <input type="datetime-local" value={countedAt.slice(0,16)} onChange={e=>setCountedAt(new Date(e.target.value).toISOString())}/>
        </label>
        {rawRows.length > 0 && <>
          <div style={{marginTop:10, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
            <b>Pratinjau {rawRows.length} baris</b>
            <span className="muted small" data-testid="stock-import-summary">✓ {validRows.length} valid · ⚠️ {errCount} error</span>
          </div>
          <div style={{maxHeight: 300, overflowY:"auto", border:"1px solid var(--line,#e2e8f0)", borderRadius:8, marginTop:6}}>
            <table style={{width:"100%", fontSize:12, borderCollapse:"collapse"}}>
              <thead style={{position:"sticky", top:0, background:"var(--wash-2,#f4efdf)"}}>
                <tr>
                  <th style={{padding:"6px 8px", textAlign:"left"}}>#</th>
                  <th style={{padding:"6px 8px", textAlign:"left"}}>Status</th>
                  <th style={{padding:"6px 8px", textAlign:"left"}}>Kategori</th>
                  <th style={{padding:"6px 8px", textAlign:"left"}}>Deskripsi</th>
                  <th style={{padding:"6px 8px", textAlign:"right"}}>Min</th>
                  <th style={{padding:"6px 8px", textAlign:"right"}}>Saldo Awal</th>
                  <th style={{padding:"6px 8px", textAlign:"left"}}>Catatan Validasi</th>
                </tr>
              </thead>
              <tbody>
                {rawRows.map((r,i) => {
                  const v = validation[i] || {ok:false, errors:["-"]};
                  const desc = (r.category||"").toLowerCase() === "kain"
                    ? [r.fabric_name, r.brand, r.color, r.unit].filter(Boolean).join(" · ")
                    : [r.product_name, r.color, r.size].filter(Boolean).join(" · ");
                  return <tr key={i} style={{borderTop:"1px solid var(--line,#e2e8f0)"}} data-testid={`stock-import-row-${i}`}>
                    <td style={{padding:"4px 8px"}}>{i+1}</td>
                    <td style={{padding:"4px 8px"}}>
                      {v.ok
                        ? <span className="chip chip-safe small">OK</span>
                        : <span className="chip chip-danger small">Error</span>}
                    </td>
                    <td style={{padding:"4px 8px"}}>{r.category||"—"}</td>
                    <td style={{padding:"4px 8px"}}>{desc || "—"}</td>
                    <td style={{padding:"4px 8px", textAlign:"right"}}>{r.min_stock||"—"}</td>
                    <td style={{padding:"4px 8px", textAlign:"right"}}>{r.saldo_awal||"—"}</td>
                    <td style={{padding:"4px 8px", color: v.ok ? "#166534" : "#b91c1c"}}>{v.ok ? "siap impor" : v.errors.join("; ")}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </>}
        <div className="modal-actions" style={{marginTop:12}}>
          <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
          <button type="button" className="primary" onClick={submit}
            disabled={busy || validRows.length===0}
            data-testid="stock-import-submit">
            {busy ? "Mengimpor…" : `Impor ${validRows.length} baris`}
          </button>
        </div>
      </>}
      {result && <div data-testid="stock-import-result">
        <div className="warn-box" style={{marginTop:10}}>
          <b>Impor selesai.</b>
          <div style={{marginTop:6, display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px,1fr))", gap:6, fontSize:13}}>
            <div>Input: <b>{result.totals?.input||0}</b></div>
            <div>Varian dibuat: <b style={{color:"#166534"}}>{result.totals?.created||0}</b></div>
            <div>Varian dilewati: <b>{result.totals?.skipped_variant||0}</b></div>
            <div>Saldo awal masuk: <b style={{color:"#166534"}}>{result.totals?.opening_balance||0}</b></div>
            <div>Error: <b style={{color:"#b91c1c"}}>{result.totals?.errors||0}</b></div>
          </div>
        </div>
        {result.errors?.length > 0 && <details style={{marginTop:8}}>
          <summary style={{cursor:"pointer", color:"#b91c1c"}}>Detail {result.errors.length} error</summary>
          <ul style={{fontSize:12}}>{result.errors.map((e,i)=><li key={i}>Baris {e.line}: {e.reason}</li>)}</ul>
        </details>}
        {result.skipped_variant?.length > 0 && <details style={{marginTop:6}}>
          <summary style={{cursor:"pointer"}}>Varian dilewati ({result.skipped_variant.length})</summary>
          <ul style={{fontSize:12}}>{result.skipped_variant.map((e,i)=><li key={i}>Baris {e.line}: {e.reason} — SKU {e.sku||"—"}</li>)}</ul>
        </details>}
        {result.opening_balance_skipped?.length > 0 && <details style={{marginTop:6}}>
          <summary style={{cursor:"pointer"}}>Saldo awal dilewati ({result.opening_balance_skipped.length})</summary>
          <ul style={{fontSize:12}}>{result.opening_balance_skipped.map((e,i)=><li key={i}>Baris {e.line}: {e.reason}</li>)}</ul>
        </details>}
        <div className="modal-actions" style={{marginTop:12}}>
          <button type="button" className="primary" onClick={onDone} data-testid="stock-import-done">Selesai</button>
        </div>
      </div>}
    </div>
  </div>;
}

function validateRow(r, productNames) {
  const errors = [];
  const cat = (r.category||"").toLowerCase();
  if (!["polosan","kain"].includes(cat)) errors.push("category harus 'polosan' atau 'kain'");
  if (!(r.color||"").trim()) errors.push("color kosong");
  if (cat === "polosan") {
    const pn = (r.product_name||"").trim();
    if (!pn) errors.push("product_name kosong");
    else if (!productNames.has(pn.toLowerCase())) errors.push(`product_name '${pn}' tidak ada di Master Produk`);
    if (!(r.size||"").trim()) errors.push("size kosong");
    const sl = (r.sleeve||"").trim().toLowerCase();
    if (!["pendek","panjang","tidak_berlaku"].includes(sl)) errors.push("sleeve harus 'pendek', 'panjang', atau 'tidak_berlaku'");
  } else if (cat === "kain") {
    if (!(r.fabric_name||"").trim()) errors.push("fabric_name kosong");
    const u = (r.unit||"").toLowerCase();
    if (!["meter","kg","roll"].includes(u)) errors.push("unit harus 'meter', 'kg', atau 'roll'");
  }
  const num = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(String(v).replace(",", "."));
    return isNaN(n) ? "invalid" : n;
  };
  const mn = num(r.min_stock);
  if (mn === "invalid") errors.push("min_stock bukan angka");
  else if (mn !== null && mn < 0) errors.push("min_stock < 0");
  const sa = num(r.saldo_awal);
  if (sa === "invalid") errors.push("saldo_awal bukan angka");
  else if (sa !== null && sa < 0) errors.push("saldo_awal < 0");
  return { ok: errors.length === 0, errors };
}

function normalizeRow(r) {
  const cat = (r.category||"").toLowerCase();
  const num = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(String(v).replace(",", "."));
    return isNaN(n) ? null : n;
  };
  const base = {
    category: cat,
    color: (r.color||"").trim(),
    min_stock: num(r.min_stock),
    saldo_awal: num(r.saldo_awal),
    note: (r.note||"").trim(),
  };
  if (cat === "polosan") {
    return { ...base,
      product_name: (r.product_name||"").trim(),
      sleeve: (r.sleeve||"").trim().toLowerCase(),
      size: (r.size||"").trim(),
    };
  }
  return { ...base, fabric_name: (r.fabric_name||"").trim(), brand: (r.brand||"").trim(), unit: (r.unit||"").toLowerCase() };
}

// ============================================================
// ReconcileModal — Owner rekonsiliasi stok via CSV
// Fitur: SET final_qty existing (bukan +=), update cost_price, buat varian baru,
// preview old→new, pisahkan minus/belum opname/ambiguous, idempotent via client_key.
// ============================================================
const RECONCILE_HEADERS = ["category","product_name","sleeve","size","fabric_name","brand","unit","color","final_qty","cost_price","min_stock","note"];
const RECONCILE_HINT = [
  "Kolom (case-insensitive): category, product_name, sleeve, size, fabric_name, brand, unit, color, final_qty, cost_price, min_stock, note",
  "• category: 'polosan' (termasuk Jersey) atau 'kain'",
  "• final_qty: stok AKHIR (bukan tambah). Kosong = qty tidak diubah (baris dianggap 'belum opname')",
  "• cost_price: harga modal SET. Kosong = tidak diubah. Harga JUAL tidak akan disentuh",
  "• Varian baru otomatis dibuat jika kombinasi belum ada. Untuk polosan, product_name wajib persis nama Master Produk",
  "• final_qty < 0 → ditolak & masuk kategori 'Stok Minus'. Duplikat & tanpa master produk juga ditolak",
  "• Idempotent: upload file yang sama tidak akan menggandakan stok",
].join("\n");

async function hashFileHex(file) {
  const buf = await file.arrayBuffer();
  const h = await crypto.subtle.digest("SHA-1", buf);
  return Array.from(new Uint8Array(h)).map(b => b.toString(16).padStart(2, "0")).join("");
}

function reconcileNormalizeRow(r) {
  const cat = (r.category||"").toLowerCase();
  const num = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(String(v).replace(",", "."));
    return isNaN(n) ? null : n;
  };
  const base = {
    category: cat,
    color: (r.color||"").trim(),
    final_qty: num(r.final_qty),
    cost_price: num(r.cost_price),
    min_stock: num(r.min_stock),
    note: (r.note||"").trim(),
  };
  if (cat === "polosan") {
    return { ...base,
      product_name: (r.product_name||"").trim(),
      sleeve: (r.sleeve||"").trim().toLowerCase(),
      size: (r.size||"").trim(),
    };
  }
  return { ...base,
    fabric_name: (r.fabric_name||"").trim(),
    brand: (r.brand||"").trim(),
    unit: (r.unit||"").toLowerCase(),
  };
}

function ReconcileModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [applyRes, setApplyRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [countedAt, setCountedAt] = useState(localISO());
  const [clientKey, setClientKey] = useState("");
  const [note, setNote] = useState("");

  const handleFile = async (f) => {
    setFile(f); setRows([]); setPreview(null); setApplyRes(null); setMsg("");
    if (!f) return;
    try {
      const key = await hashFileHex(f);
      setClientKey(`reconcile_${key}_${f.size}`);
      const ext = f.name.split(".").pop().toLowerCase();
      let parsed;
      if (ext === "csv" || ext === "txt") {
        const text = await f.text();
        const grid = parseCsvText(text);
        if (grid.length < 2) { setMsg("File kosong atau hanya header"); return; }
        const headers = grid[0].map(h => (h||"").trim().toLowerCase());
        parsed = grid.slice(1).map(r => {
          const obj = {}; headers.forEach((h,i) => { obj[h] = (r[i]||"").trim(); });
          return obj;
        });
      } else if (ext === "xlsx" || ext === "xls") {
        const buf = await f.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const grid = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
        parsed = grid.map(o => {
          const lc = {};
          Object.entries(o).forEach(([k,v]) => { lc[String(k).trim().toLowerCase()] = String(v||"").trim(); });
          return lc;
        });
      } else {
        setMsg("Format tidak didukung. Gunakan .csv, .xlsx, atau .xls"); return;
      }
      setRows(parsed);
      if (!parsed.length) setMsg("Tidak ada baris data setelah header");
    } catch (e) {
      setMsg("Gagal membaca file: " + (e.message || String(e)));
    }
  };

  const runDryRun = async () => {
    if (!rows.length) { setMsg("Belum ada data untuk dipratinjau"); return; }
    setBusy(true); setMsg("");
    try {
      const payload = { rows: rows.map(reconcileNormalizeRow), counted_at: countedAt, client_key: clientKey, note };
      const r = await axios.post(`${API}/stock/reconcile/dry-run`, payload);
      setPreview(r.data);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal pratinjau rekonsiliasi");
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    if (!preview) return;
    if (!confirm("Terapkan rekonsiliasi ke DB Live? Stok, saldo awal (jika perlu), dan harga modal akan diperbarui. Aksi ini tercatat di riwayat.")) return;
    setBusy(true); setMsg("");
    try {
      const payload = { rows: rows.map(reconcileNormalizeRow), counted_at: countedAt, client_key: clientKey, note };
      const r = await axios.post(`${API}/stock/reconcile/apply`, payload);
      setApplyRes(r.data);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal menerapkan rekonsiliasi");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => { window.open(`${API}/stock/reconcile/template`, "_blank"); };
  const downloadBackup = () => { window.open(`${API}/stock/reconcile/export`, "_blank"); };

  const t = preview?.totals;

  return <div className="modal-backdrop" data-testid="stock-reconcile-modal">
    <div className="modal-panel" style={{maxWidth: 960, maxHeight: "90vh", overflowY: "auto"}}>
      <h3 style={{margin:"0 0 6px"}}>Rekonsiliasi Stok via CSV (Owner)</h3>
      <p className="muted small" style={{whiteSpace:"pre-line", margin:"0 0 10px"}}>{RECONCILE_HINT}</p>
      {msg && <div className="warn-box small" data-testid="stock-reconcile-msg">{msg}</div>}

      {!applyRes && <>
        <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:8}}>
          <button type="button" className="soft-btn small-btn" onClick={downloadTemplate} data-testid="stock-reconcile-template">
            <Download size={14}/> Unduh Template CSV
          </button>
          <button type="button" className="soft-btn small-btn" onClick={downloadBackup} data-testid="stock-reconcile-backup">
            <Download size={14}/> Ekspor Stok Sekarang (Backup)
          </button>
        </div>
        <label className="modal-field" style={{marginTop:4}}>
          <input type="file" accept=".csv,.xlsx,.xls,.txt"
            onChange={e => handleFile(e.target.files?.[0])} data-testid="stock-reconcile-file"/>
        </label>
        {file && <div className="muted small">File: {file.name} · {rows.length} baris terbaca</div>}
        <div style={{display:"grid", gridTemplateColumns:"1fr 2fr", gap:6, marginTop:8}}>
          <label className="modal-field" style={{margin:0}}>Waktu Opname
            <input type="datetime-local" value={countedAt.slice(0,16)} onChange={e=>setCountedAt(new Date(e.target.value).toISOString())}/>
          </label>
          <label className="modal-field" style={{margin:0}}>Catatan (opsional, dipakai di riwayat)
            <input value={note} onChange={e=>setNote(e.target.value)} placeholder="misal: Opname 1 Feb 2026" data-testid="stock-reconcile-note"/>
          </label>
        </div>

        {rows.length > 0 && !preview && <div className="modal-actions" style={{marginTop:12}}>
          <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
          <button type="button" className="primary" onClick={runDryRun} disabled={busy} data-testid="stock-reconcile-preview-btn">
            {busy ? "Menganalisa…" : `Pratinjau Perubahan (${rows.length} baris)`}
          </button>
        </div>}

        {preview && <>
          <div style={{marginTop:12, background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, padding:"10px 12px"}} data-testid="stock-reconcile-preview">
            <div style={{fontWeight:700, marginBottom:6}}>Ringkasan Pratinjau ({t.input} baris)</div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:6, fontSize:12}}>
              <div>Varian baru: <b>{t.new_variants}</b></div>
              <div>Qty berubah: <b>{t.qty_changes}</b></div>
              <div>Qty tidak berubah: <b>{t.qty_unchanged}</b></div>
              <div>Harga modal berubah: <b>{t.cost_changes}</b></div>
              <div>Harga modal tetap: <b>{t.cost_unchanged}</b></div>
              <div style={{color:"#b91c1c"}}>Stok minus (ditolak): <b>{t.rejected_minus}</b></div>
              <div style={{color:"#b91c1c"}}>Tanpa master (ditolak): <b>{t.rejected_ambiguous}</b></div>
              <div style={{color:"#b91c1c"}}>Duplikat: <b>{t.duplicates}</b></div>
              <div style={{color:"#a16207"}}>Belum opname (dilewati): <b>{t.unopened}</b></div>
              {t.manual_cost_will_overwrite > 0 && <div style={{color:"#a16207", gridColumn:"1 / -1"}}>
                ⚠️ Akan menimpa harga modal edit manual Owner: <b>{t.manual_cost_will_overwrite}</b> varian
              </div>}
            </div>
          </div>
          <ReconcileBuckets preview={preview}/>
          <div className="modal-actions" style={{marginTop:12}}>
            <button type="button" className="soft-btn" onClick={()=>setPreview(null)} disabled={busy}>← Ubah File</button>
            <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
            <button type="button" className="primary" onClick={runApply}
              disabled={busy || (t.new_variants + t.qty_changes + t.cost_changes) === 0}
              data-testid="stock-reconcile-apply-btn">
              {busy ? "Menerapkan…" : `Terapkan (${t.new_variants} baru · ${t.qty_changes} qty · ${t.cost_changes} harga)`}
            </button>
          </div>
        </>}
      </>}

      {applyRes && <div data-testid="stock-reconcile-applied">
        <div style={{background:"#dcfce7", border:"1px solid #86efac", borderRadius:8, padding:"10px 12px", marginBottom:8}}>
          <div style={{fontWeight:700}}>Rekonsiliasi Diterapkan{applyRes.idempotent_replay ? " (idempotent replay — file sama, tidak digandakan)" : ""}</div>
          <div style={{fontSize:12, marginTop:6, display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:4}}>
            <div>Varian baru dibuat: <b>{applyRes.totals.created_variants}</b></div>
            <div>Penyesuaian qty (RC): <b>{applyRes.totals.qty_txns}</b></div>
            <div>Harga modal diperbarui: <b>{applyRes.totals.cost_updates}</b></div>
            <div>Saldo awal dicatat: <b>{applyRes.totals.opening_balances}</b></div>
            {applyRes.totals.errors > 0 && <div style={{color:"#b91c1c"}}>Error: <b>{applyRes.totals.errors}</b></div>}
          </div>
        </div>
        {applyRes.applied?.errors?.length > 0 && <div className="warn-box small">
          {applyRes.applied.errors.slice(0,10).map((e,i)=><div key={i}>Baris {e.line}: {e.reason}</div>)}
        </div>}
        <div className="modal-actions" style={{marginTop:12}}>
          <button type="button" className="primary" onClick={onDone}>Selesai</button>
        </div>
      </div>}
    </div>
  </div>;
}

function ReconcileBuckets({ preview }) {
  const [tab, setTab] = useState("qty_changes");
  const buckets = {
    qty_changes: { label: "Qty Berubah", rows: preview.qty_changes, cols: ["desc","old","new","delta"] },
    new_variants: { label: "Varian Baru", rows: preview.new_variants, cols: ["desc","final_qty","cost"] },
    cost_changes: { label: "Harga Modal Berubah", rows: preview.cost_changes, cols: ["desc","old_cost","new_cost","tag"] },
    unopened: { label: "Belum Opname", rows: preview.unopened, cols: ["desc","current"] },
    rejected_minus: { label: "Stok Minus", rows: preview.rejected_minus, cols: ["desc","final_qty"] },
    rejected_ambiguous: { label: "Tanpa Master", rows: preview.rejected_ambiguous, cols: ["reason"] },
    duplicates: { label: "Duplikat File", rows: preview.duplicates, cols: ["dup"] },
    manual_cost_locked: { label: "Timpa Perubahan Manual", rows: preview.manual_cost_locked, cols: ["desc","old_cost","new_cost"] },
  };
  const desc = (r) => {
    if (r.fabric_name) return [r.fabric_name, r.brand, r.color, r.unit].filter(Boolean).join(" · ");
    return [r.product_name, r.sleeve, r.color, r.size].filter(Boolean).join(" · ");
  };
  const cur = buckets[tab];
  return <div style={{marginTop:12}}>
    <div style={{display:"flex", flexWrap:"wrap", gap:4, marginBottom:6}}>
      {Object.entries(buckets).map(([k, b]) => b.rows.length > 0 && (
        <button key={k} type="button"
          className={"soft-btn small-btn"} style={{background: tab===k ? "var(--accent, #2563eb)" : undefined, color: tab===k ? "#fff" : undefined}}
          onClick={()=>setTab(k)} data-testid={`stock-reconcile-tab-${k}`}>
          {b.label} ({b.rows.length})
        </button>
      ))}
    </div>
    {cur && cur.rows.length > 0 && <div style={{maxHeight:260, overflowY:"auto", border:"1px solid var(--line,#e2e8f0)", borderRadius:8}}>
      <table style={{width:"100%", fontSize:12, borderCollapse:"collapse"}}>
        <thead style={{position:"sticky", top:0, background:"var(--wash-2,#f4efdf)"}}>
          <tr>
            <th style={{padding:"6px 8px", textAlign:"left"}}>Baris</th>
            {tab === "rejected_ambiguous" ? <th style={{padding:"6px 8px", textAlign:"left"}}>Alasan</th> :
             tab === "duplicates" ? <th style={{padding:"6px 8px", textAlign:"left"}}>Duplikat</th> : <>
              <th style={{padding:"6px 8px", textAlign:"left"}}>Deskripsi</th>
              {(tab === "qty_changes" || tab === "unopened") && <th style={{padding:"6px 8px", textAlign:"right"}}>Sekarang</th>}
              {tab === "qty_changes" && <><th style={{padding:"6px 8px", textAlign:"right"}}>Baru</th><th style={{padding:"6px 8px", textAlign:"right"}}>Δ</th></>}
              {tab === "new_variants" && <><th style={{padding:"6px 8px", textAlign:"right"}}>Saldo Awal</th><th style={{padding:"6px 8px", textAlign:"right"}}>Cost</th></>}
              {(tab === "cost_changes" || tab === "manual_cost_locked") && <><th style={{padding:"6px 8px", textAlign:"right"}}>Cost Lama</th><th style={{padding:"6px 8px", textAlign:"right"}}>Cost Baru</th><th style={{padding:"6px 8px", textAlign:"left"}}>Sumber</th></>}
              {tab === "rejected_minus" && <th style={{padding:"6px 8px", textAlign:"right"}}>final_qty</th>}
             </>}
          </tr>
        </thead>
        <tbody>
          {cur.rows.map((r, i) => <tr key={i} style={{borderTop:"1px solid var(--line,#e2e8f0)"}}>
            <td style={{padding:"4px 8px"}}>{r.line}</td>
            {tab === "rejected_ambiguous" ? <td style={{padding:"4px 8px", color:"#b91c1c"}}>{r.reason}</td> :
             tab === "duplicates" ? <td style={{padding:"4px 8px"}}>Dup baris {r.first_line}: {r.key}</td> : <>
              <td style={{padding:"4px 8px"}}>{desc(r) || r.sku || "—"}</td>
              {(tab === "qty_changes" || tab === "unopened") && <td style={{padding:"4px 8px", textAlign:"right"}}>{tab==="unopened" ? r.current_qty : r.old_qty} {r.unit||""}</td>}
              {tab === "qty_changes" && <><td style={{padding:"4px 8px", textAlign:"right"}}>{r.new_qty}</td><td style={{padding:"4px 8px", textAlign:"right", color:r.delta>0?"#166534":"#b91c1c"}}>{r.delta>0?"+":""}{r.delta}</td></>}
              {tab === "new_variants" && <><td style={{padding:"4px 8px", textAlign:"right"}}>{r.final_qty ?? "—"}</td><td style={{padding:"4px 8px", textAlign:"right"}}>{r.cost_price ? `Rp ${money(r.cost_price)}` : "—"}</td></>}
              {(tab === "cost_changes" || tab === "manual_cost_locked") && <>
                <td style={{padding:"4px 8px", textAlign:"right"}}>{r.old_cost ? `Rp ${money(r.old_cost)}` : "—"}</td>
                <td style={{padding:"4px 8px", textAlign:"right"}}>Rp {money(r.new_cost)}</td>
                <td style={{padding:"4px 8px", color: r.will_overwrite_manual ? "#a16207" : undefined}}>{r.will_overwrite_manual ? `⚠️ manual (${r.prev_updater})` : (r.prev_updater || "baru")}</td>
              </>}
              {tab === "rejected_minus" && <td style={{padding:"4px 8px", textAlign:"right", color:"#b91c1c"}}>{r.final_qty}</td>}
             </>}
          </tr>)}
        </tbody>
      </table>
    </div>}
  </div>;
}




export function VariantDetail({ variant, onClose, onOpenOrder }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/stock/transactions`, { params: { variant_id: variant.id, limit: 200 } });
        setTxns(r.data || []);
      } catch { setTxns([]); }
      finally { setLoading(false); }
    })();
  }, [variant.id]);
  const unit = variant.unit || (variant.category === "kain" ? "" : "pcs");
  const minText = (variant.min_stock === null || variant.min_stock === undefined) ? "Minimum belum diatur" : `Minimum: ${variant.min_stock} ${unit}`;
  return <StockDetailModal onClose={onClose} testid={`stock-variant-detail-${variant.id}`}>
      <h3 style={{margin:"0 0 6px", fontSize:16}}>{variantLabel(variant)}</h3>
      <p className="muted small" style={{margin:"0 0 6px"}}>SKU: <span style={{fontFamily:"monospace"}}>{variant.sku}</span></p>
      <p className="muted small" style={{margin:"0 0 6px"}}>Saldo saat ini: <b>{fmtQty(variant.current_qty, unit)}</b> tersedia di Gudang · {minText}</p>
      {showJahitPosition(variant) && <JahitPosition variantId={variant.id}/>}
      <div style={{display:"flex", flexDirection:"column", gap:6, marginTop:8}}>
        {loading && <p className="muted small">Memuat…</p>}
        {!loading && txns.length===0 && <p className="muted small">Belum ada mutasi.</p>}
        {txns.map(t => <TxnRow key={t.id} t={t} onOpenOrder={onOpenOrder}/>)}
      </div>
      <div className="sdm-actions">
        <button type="button" className="primary" onClick={onClose} data-testid={`stock-variant-detail-close-${variant.id}`}>Tutup</button>
      </div>
  </StockDetailModal>;
}

function TxnRow({ t, onOpenOrder }) {
  const orderNo = t.ref?.ref_order_number;
  const typeLbl = { opening: "Saldo Awal", in: "Masuk", out: "Keluar", opname_adjust: "Opname", reversal: "Pembalikan" }[t.type] || t.type;
  const deltaColor = t.delta >= 0 ? "#166534" : "#b91c1c";
  const unit = t.unit || "";
  const rollCount = t.ref?.roll_count;
  const rollDetails = t.ref?.roll_details;
  return <div className="stock-txn-row" style={{border:"1px solid var(--line,#e2e8f0)", borderRadius:8, padding:8, fontSize:13, display:"flex", flexDirection:"column", gap:2}}>
    <div style={{display:"flex", justifyContent:"space-between", gap:6}}>
      <span><b>{typeLbl}</b> · {t.doc_number}</span>
      <span style={{color:deltaColor, fontWeight:700}}>{t.delta>=0?"+":""}{fmtQty(t.delta, unit)}</span>
    </div>
    <div className="muted small">{fmtDT(t.recorded_at)} · oleh {t.actor_name}</div>
    <div className="muted small">Saldo: {fmtQty(t.balance_before, unit)} → {fmtQty(t.balance_after, unit)}</div>
    {rollCount && <div className="muted small">Kemasan: {rollCount} roll{rollDetails ? ` (${rollDetails.map(r=>`${r.qty}${unit}`).join(" + ")})` : ""}</div>}
    {t.note && <div className="muted small" style={{fontStyle:"italic"}}>“{t.note}”</div>}
    {orderNo && onOpenOrder && <button type="button" className="soft-btn small-btn" onClick={()=>onOpenOrder(orderNo)}>Buka Nota {orderNo}</button>}
    {t.reversed_by && <span className="chip chip-muted small">Sudah dibalik</span>}
    {t.reversal_of && <span className="chip chip-warn small">Pembalikan</span>}
  </div>;
}

// ============================================================
function TabIn({ category, variants, canMutasi, onDone, onOpenOrder }) {
  return <TxnDocForm category={category} kind="in" title="Barang Masuk" endpoint="/stock/in" variants={variants} canMutasi={canMutasi} onDone={onDone}
    extra={{ source_kind: { label:"Sumber", options:[["pembelian","Pembelian"],["produksi","Produksi Sendiri"],["return","Pengembalian"]], default:"pembelian" } }}
    supplierField
    rollField={category === "kain"}
    prLinkField  // Tahap C — toggle "Tautkan ke Kebutuhan Produksi"
    onOpenOrder={onOpenOrder}
  />;
}
function TabOut({ category, variants, canMutasi, onDone, onOpenOrder }) {
  const outOptions = category === "kain"
    ? [["bordir_sablon","Pengeluaran ke Cutting/Bordir/Sablon"],["rusak","Kain Rusak"],["lain","Lain-lain"]]
    : [["nota","Untuk Pesanan Nota"],["bordir_sablon","Untuk Bordir/Sablon"],["polosan_langsung","Penjualan Polosan Langsung"],["rusak","Barang Rusak"],["lain","Lain-lain"]];
  return <TxnDocForm category={category} kind="out" title="Barang Keluar" endpoint="/stock/out" variants={variants} canMutasi={canMutasi} onDone={onDone}
    extra={{ reason: { label:"Alasan / Tujuan", options: outOptions, default: outOptions[0][0] } }}
    reasonDetailField
    orderRefField
    onOpenOrder={onOpenOrder}
  />;
}

function TxnDocForm({ category, kind, title, endpoint, variants, canMutasi, onDone, extra, supplierField, reasonDetailField, orderRefField, rollField, prLinkField, onOpenOrder }) {
  const isKain = category === "kain";
  const isOut = kind === "out";
  const [extraSel, setExtraSel] = useState(Object.fromEntries(Object.entries(extra||{}).map(([k,v])=>[k,v.default])));
  const [supplier, setSupplier] = useState("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [occurredAt, setOccurredAt] = useState(localISO());
  const [refOrder, setRefOrder] = useState("");
  const [refOrderTxns, setRefOrderTxns] = useState([]);
  const [note, setNote] = useState("");
  // Iter 267: kelompok berisi seleksi bertahap + jumlah per varian
  // Polosan group: { id, product_name, sleeve, color, qtyByVid: {vid: qty} }
  // Kain group:    { id, fabric_name, brand, color, unit, variant_id, qty, roll_count, roll_details }
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [clientKey, setClientKey] = useState(() => uuid());

  // Tahap C — Tautan ke Kebutuhan Produksi (opsional, hanya kind=in).
  const [prLinkOpen, setPrLinkOpen] = useState(false);
  const [prList, setPrList] = useState([]);         // daftar PR aktif
  const [prLoading, setPrLoading] = useState(false);
  const [prSelectedId, setPrSelectedId] = useState(""); // source_id PR yang dipilih
  const [prLineByVid, setPrLineByVid] = useState({}); // {variant_id → line_id} override
  const prSelected = useMemo(() => prList.find(p => p.source_id === prSelectedId) || null, [prList, prSelectedId]);

  useEffect(() => {
    if (!prLinkField || !prLinkOpen || prList.length) return;
    setPrLoading(true);
    axios.get(`${API}/stock/production-requests/active-for-link`, { withCredentials: true })
      .then(r => setPrList(r.data?.items || []))
      .catch(() => setPrList([]))
      .finally(() => setPrLoading(false));
  }, [prLinkField, prLinkOpen, prList.length]);

  // Normalisasi string untuk perbandingan atribut (case-insensitive).
  const _ns = (x) => (x ? String(x).trim().toLowerCase() : "");

  // Cari line_id PR yang match dengan variant tertentu (category + atribut + unit).
  const autoMatchLine = useCallback((variant, pr) => {
    if (!variant || !pr) return "";
    const want_cat = (variant.category || "polosan").toLowerCase();
    for (const it of (pr.items || [])) {
      if ((it.category || "").toLowerCase() !== want_cat) continue;
      if (_ns(it.unit) !== _ns(variant.unit)) continue;
      if (_ns(it.color) !== _ns(variant.color)) continue;
      if (want_cat === "kain") {
        if (_ns(it.fabric_name) !== _ns(variant.fabric_name)) continue;
        if (it.brand && _ns(it.brand) !== _ns(variant.brand)) continue;
      } else {
        if (_ns(it.product_name) !== _ns(variant.product_name)) continue;
        if (it.size && _ns(it.size) !== _ns(variant.size)) continue;
        if (it.sleeve && _ns(it.sleeve) !== _ns(variant.sleeve)) continue;
      }
      return it.line_id;
    }
    return "";
  }, []);

  const eligible = useMemo(() =>
    variants.filter(v => v.uses_stock!==false && v.is_active!==false && v.status!=="belum_dihitung"),
    [variants]
  );
  const pendingOB = useMemo(() => Array.from(new Set(variants.filter(v => v.status === "belum_dihitung" && v.uses_stock !== false && v.is_active !== false
    && (v.category || "polosan") === (isKain ? "kain" : "polosan")).map(v => v.product_name || v.fabric_name).filter(Boolean))).sort(), [variants, isKain]);

  const addGroup = () => setGroups(g => [...g, isKain
    ? { id: uuid(), fabric_name: "", brand: "", color: "", unit: "", variant_id: "", qty: "", roll_count: "", roll_details: [] }
    : { id: uuid(), product_name: "", sleeve: "", color: "", qtyByVid: {} }]);
  const patchGroup = (id, patch) => setGroups(g => g.map(x => x.id===id ? {...x, ...patch} : x));
  const delGroup = (id) => setGroups(g => g.filter(x => x.id!==id));

  const fetchRefTxns = async (n) => {
    setRefOrder(n);
    if (!n || n.length < 3) { setRefOrderTxns([]); return; }
    try {
      const r = await axios.get(`${API}/stock/txns-for-order/${encodeURIComponent(n)}`);
      setRefOrderTxns(r.data || []);
    } catch { setRefOrderTxns([]); }
  };

  // Rangkum semua kelompok jadi daftar line { variant_id, qty }; jumlahkan varian yang muncul di > 1 kelompok
  const linesFromGroups = () => {
    const bag = new Map();  // vid → qty
    const roll = new Map(); // vid → {roll_count, roll_details} (kain-only, hanya kelompok terakhir)
    for (const g of groups) {
      if (isKain) {
        if (!g.variant_id || !g.qty) continue;
        const q = Number(String(g.qty).replace(",","."));
        if (isNaN(q) || q <= 0) continue;
        bag.set(g.variant_id, (bag.get(g.variant_id)||0) + q);
        if (rollField && kind === "in") {
          const v = eligible.find(x => x.id === g.variant_id);
          const vu = (v?.unit || "").toLowerCase();
          if (vu !== "roll" && (g.roll_count || (g.roll_details||[]).length)) {
            roll.set(g.variant_id, { roll_count: g.roll_count, roll_details: g.roll_details });
          }
        }
      } else {
        for (const [vid, raw] of Object.entries(g.qtyByVid||{})) {
          const q = parseInt(String(raw).replace(",","."), 10);
          if (!q || q <= 0) continue;
          bag.set(vid, (bag.get(vid)||0) + q);
        }
      }
    }
    return Array.from(bag.entries()).map(([variant_id, qty]) => {
      const line = { variant_id, qty };
      const r = roll.get(variant_id);
      if (r?.roll_count) line.roll_count = parseInt(r.roll_count, 10);
      if (r?.roll_details?.length) line.roll_details = r.roll_details.map(x => ({ qty: Number(x.qty), label: x.label || "" }));
      // Tahap C — inject tautan PR bila user memilih dan ada line_id match untuk varian ini.
      if (prLinkField && prSelectedId) {
        const lid = prLineByVid[variant_id];
        if (lid) {
          line.pr_source_id = prSelectedId;
          line.pr_line_id = lid;
        }
      }
      return line;
    });
  };

  const submit = async () => {
    const lines = linesFromGroups();
    if (!lines.length) { setMsg("Isi minimal satu jumlah > 0 di salah satu kelompok"); return; }
    // Validasi qty-out ≤ saldo (frontend guard; backend tetap validasi)
    if (isOut) {
      for (const l of lines) {
        const v = eligible.find(x => x.id === l.variant_id);
        if (v && Number(l.qty) > Number(v.current_qty||0) + 1e-6) {
          setMsg(`Stok ${variantLabel(v)} tidak cukup (${l.qty} > ${v.current_qty} ${v.unit||""})`);
          return;
        }
      }
    }
    // Validasi rincian roll kain
    if (isKain && rollField) {
      for (const l of lines) {
        if (l.roll_details && l.roll_details.length > 0) {
          const sum = l.roll_details.reduce((s,r)=>s + Number(r.qty||0), 0);
          if (Math.abs(sum - Number(l.qty)) > 0.005) { setMsg(`Rincian roll (${sum}) tidak sama dengan jumlah kain (${l.qty}). Kosongkan rincian atau perbaiki.`); return; }
        }
      }
    }
    setBusy(true); setMsg("");
    try {
      const body = {
        occurred_at: occurredAt, note, ref_order_number: refOrder || null,
        lines,
        client_key: clientKey,
        ...extraSel,
        ...(supplierField ? { supplier } : {}),
        ...(reasonDetailField ? { reason_detail: reasonDetail } : {}),
      };
      const r = await axios.post(`${API}${endpoint}`, body);
      setMsg(`Berhasil: ${r.data.doc_number}`);
      setGroups([]); setRefOrder(""); setRefOrderTxns([]); setNote(""); setReasonDetail(""); setSupplier("");
      // Tahap C — reset tautan PR setelah submit sukses.
      setPrSelectedId(""); setPrLineByVid({}); setPrLinkOpen(false);
      setClientKey(uuid());
      onDone();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(false); }
  };

  const groupsFilled = groups.some(g => isKain
    ? (g.variant_id && Number(g.qty||0) > 0)
    : Object.values(g.qtyByVid||{}).some(q => Number(String(q).replace(",","."))>0));

  return <div style={{display:"flex", flexDirection:"column", gap:10}}>
    <h3 style={{margin:0}}>{title} — {isKain ? "Kain" : "Polosan"}</h3>
    {!canMutasi && <div className="warn-box small">Anda tidak berwenang mencatat mutasi stok.</div>}
    {msg && <div className="warn-box small" data-testid="stock-txn-msg">{msg}</div>}

    <div style={{display:"grid", gridTemplateColumns:"1fr", gap:8}}>
      {Object.entries(extra||{}).map(([k,v]) => (
        <label key={k} className="modal-field">{v.label}
          <select value={extraSel[k]||""} onChange={e=>setExtraSel(s=>({...s,[k]:e.target.value}))} data-testid={`stock-txn-${k}`}>
            {v.options.map(([val,lab]) => <option key={val} value={val}>{lab}</option>)}
          </select>
        </label>
      ))}
      {supplierField && <label className="modal-field">Supplier<input value={supplier} onChange={e=>setSupplier(e.target.value)}/></label>}
      {reasonDetailField && extraSel.reason==="lain" && <label className="modal-field">Detail Alasan (wajib)<input value={reasonDetail} onChange={e=>setReasonDetail(e.target.value)} data-testid="stock-out-reason-detail"/></label>}
      <label className="modal-field">Tanggal Transaksi<input type="datetime-local" value={occurredAt.slice(0,16)} onChange={e=>setOccurredAt(new Date(e.target.value).toISOString())}/></label>
      {orderRefField && <label className="modal-field">Referensi Nomor Nota (opsional)
        <input value={refOrder} onChange={e=>fetchRefTxns(e.target.value.trim())} placeholder="TF-…" data-testid="stock-out-ref-order"/>
      </label>}
    </div>

    {orderRefField && refOrder && refOrderTxns.length>0 && <div className="warn-box small">
      <b>Pengeluaran terdahulu untuk {refOrder}:</b>
      {refOrderTxns.map(t => <div key={t.id} style={{fontSize:12, marginTop:2}}>· {t.doc_number}: {fmtQty(t.delta, t.unit||"")} · {fmtDT(t.recorded_at)}</div>)}
    </div>}

    <div style={{display:"flex", flexDirection:"column", gap:10}}>
      {groups.map((g, gi) => isKain
        ? <KainGroup key={g.id} idx={gi} group={g} eligible={eligible} kind={kind}
            onPatch={(p)=>patchGroup(g.id, p)} onDelete={()=>delGroup(g.id)}
            rollField={rollField}/>
        : <PolosanGroup key={g.id} idx={gi} group={g} eligible={eligible} kind={kind}
            onPatch={(p)=>patchGroup(g.id, p)} onDelete={()=>delGroup(g.id)}/>
      )}
      <button type="button" className="soft-btn" onClick={addGroup} data-testid="stock-txn-add-group" disabled={!canMutasi}>
        + Tambah Produk
      </button>
      {pendingOB.length > 0 && <div className="muted small" data-testid="stock-txn-pending-ob">
        Belum bisa dipilih karena saldo awal belum dikonfirmasi: <b>{pendingOB.join(", ")}</b>. Owner dapat mengonfirmasi di Daftar Stok → Konfirmasi Saldo Awal.
      </div>}
    </div>

    {/* Tahap C — Toggle "+ Tautkan ke Kebutuhan Produksi" (hanya di Barang Masuk). */}
    {prLinkField && (
      <PRLinkPanel
        open={prLinkOpen}
        onToggle={() => setPrLinkOpen(v => !v)}
        prList={prList} prLoading={prLoading}
        prSelectedId={prSelectedId} setPrSelectedId={(id) => { setPrSelectedId(id); setPrLineByVid({}); }}
        prSelected={prSelected}
        activeVariants={(() => {
          // Rangkum variant_id + qty dari groups yang qty > 0.
          const vids = new Set();
          for (const g of groups) {
            if (isKain) {
              if (g.variant_id && Number(String(g.qty||"").replace(",",".")) > 0) vids.add(g.variant_id);
            } else {
              for (const [vid, raw] of Object.entries(g.qtyByVid||{})) {
                if (parseInt(String(raw).replace(",","."), 10) > 0) vids.add(vid);
              }
            }
          }
          return Array.from(vids).map(id => eligible.find(e => e.id === id)).filter(Boolean);
        })()}
        prLineByVid={prLineByVid}
        setPrLineByVid={setPrLineByVid}
        autoMatchLine={autoMatchLine}
      />
    )}

    <label className="modal-field">Catatan<textarea rows={2} value={note} onChange={e=>setNote(e.target.value)}/></label>

    <button type="button" className="primary" onClick={submit}
      disabled={busy || !canMutasi || !groupsFilled}
      data-testid="stock-txn-submit">
      {busy ? "Menyimpan…" : `Konfirmasi ${title}`}
    </button>
    {busy && <div className="muted small" data-testid="stock-txn-status">⏳ Sedang menyimpan… mohon tunggu, jangan tutup halaman.</div>}
    <p className="muted small">Draf belum mengubah stok. Stok berubah saat tombol Konfirmasi ditekan.</p>
  </div>;
}

// ============================================================
// Tahap C — Blok progres per item di modal Riwayat Kiriman.
// Menampilkan 5 angka: Total, Dipotong, Jahit, Diterima Gudang, Belum Diterima.
// Warning banner saat atribusi_akurat=false/ambiguous_pcs>0/over_received.
// Untuk kain: hanya tampilkan sebagai Material (sudah_dipotong), tidak section Jahit.
// ============================================================
function ItemProgressBlock({ item, prog }) {
  const n = (v) => (v === null || v === undefined ? "—" : v);
  const isKain = (item.category || "").toLowerCase() === "kain";
  const hasAmbiguity = prog.attribusi_akurat === false || Number(prog.ambiguous_pcs || 0) > 0;
  const hasOver = !!prog.over_received;
  const belumDiterima = prog.belum_diterima_gudang_pcs;
  const belumPasti = belumDiterima === null || belumDiterima === undefined;
  const diterima = Number(prog.diterima_gudang_pcs || 0);

  return (
    <div className="pi-progress" data-testid={`pi-progress-${item.line_id}`}>
      {(hasAmbiguity || hasOver) && (
        <div className="pi-progress-warn" data-testid={`pi-progress-warn-${item.line_id}`}>
          {hasOver && (
            <div>⚠ Ketidaksesuaian: diterima gudang ({diterima}) <b>melebihi</b> selesai dijahit ({prog.selesai_dijahit}){prog.over_received_pcs ? ` — selisih ${prog.over_received_pcs} pcs` : ""}.</div>
          )}
          {hasAmbiguity && (
            <div>⚠ Jahit melaporkan atribusi belum pasti{prog.ambiguous_pcs ? ` (${prog.ambiguous_pcs} pcs ambigu)` : ""} — sebagian angka belum dapat dipastikan.</div>
          )}
        </div>
      )}
      <div className="pi-progress-grid">
        <div><span>Total kebutuhan</span><strong>{n(prog.total)} {item.unit}</strong></div>
        <div><span>Sudah dipotong</span><strong>{n(prog.sudah_dipotong)}</strong></div>
        {!isKain && <>
          <div><span>Sedang dijahit</span><strong>{n(prog.sedang_dijahit)}</strong></div>
          <div><span>Selesai jahit</span><strong>{n(prog.selesai_dijahit)}</strong></div>
        </>}
        <div className={!prog.has_nota_link ? "pi-progress-unknown" : ""}>
          <span>Diterima gudang</span>
          <strong>
            {prog.has_nota_link ? diterima : "Belum ada penerimaan tertaut"}
          </strong>
        </div>
        {!isKain && (
          <div className={belumPasti ? "pi-progress-unknown" : ""}>
            <span>Belum diterima gudang</span>
            <strong>
              {belumPasti ? "— belum terverifikasi" : `${belumDiterima}`}
            </strong>
          </div>
        )}
      </div>
      {!prog.has_nota_link && (
        <p className="pi-progress-hint">
          ℹ Belum ada Barang Masuk yang <b>ditautkan</b> ke baris ini. Angka ini bukan bukti barang belum diterima fisik — centang tautan saat input Barang Masuk agar progres terbaca.
        </p>
      )}
      {isKain && (
        <p className="pi-progress-hint">
          Kain ditampilkan sebagai material (tidak melewati proses jahit).
        </p>
      )}
    </div>
  );
}

// ============================================================
// Tahap C — Panel "Tautkan ke Kebutuhan Produksi" (hanya BM).
// Collapsed by default. Open → dropdown PR aktif → mapping variant→line_id.
// Hanya variant yang auto-match atribut yang bisa ditautkan.
// ============================================================
// Collapsed by default. Open → dropdown PR aktif → mapping variant→line_id.
// Hanya variant yang auto-match atribut yang bisa ditautkan.
// ============================================================
function PRLinkPanel({ open, onToggle, prList, prLoading, prSelectedId, setPrSelectedId, prSelected, activeVariants, prLineByVid, setPrLineByVid, autoMatchLine }) {
  // Tahap C — PENGAMANAN: auto-match TIDAK otomatis mengisi prLineByVid.
  // Pemilihan tautan harus eksplisit per baris oleh operator. Hitung saran
  // (daftar line_id yang match atribut) HANYA sebagai hint visual di dropdown.
  const suggestedLineByVid = useMemo(() => {
    if (!prSelected) return {};
    const out = {};
    for (const v of activeVariants) {
      const lid = autoMatchLine(v, prSelected);
      if (lid) out[v.id] = lid;
    }
    return out;
  }, [prSelected, activeVariants, autoMatchLine]);

  const describePrLine = (it) => {
    const name = (it.category === "kain") ? (it.fabric_name || "Kain") : (it.product_name || "Polosan");
    const bits = [it.color, it.brand, (it.size || ""), (it.sleeve && it.sleeve !== "tidak_berlaku" ? `L ${it.sleeve}` : "")].filter(Boolean);
    return `${name}${bits.length ? " · " + bits.join(" · ") : ""} · ${it.qty} ${it.unit}`;
  };

  const describeVariant = (v) => {
    if (v.category === "kain") return [v.fabric_name, v.color, v.brand, v.unit].filter(Boolean).join(" · ");
    return [v.product_name, v.color, v.size, v.sleeve && v.sleeve !== "tidak_berlaku" ? `L ${v.sleeve}` : ""].filter(Boolean).join(" · ");
  };

  const linkedCount = activeVariants.filter(v => prLineByVid[v.id]).length;

  return (
    <section className="pr-link-panel" data-testid="pr-link-panel">
      <button type="button" className="pr-link-toggle" onClick={onToggle}
        data-testid="pr-link-toggle"
        aria-expanded={open}>
        <ChevronDown size={14} style={{transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s"}}/>
        <ShoppingCart size={14}/>
        <span>+ Tautkan ke Kebutuhan Produksi {prSelected && linkedCount > 0 ? `(${linkedCount} baris)` : "(opsional)"}</span>
      </button>
      {open && (
        <div className="pr-link-body">
          {prLoading && <p className="muted small">Memuat kebutuhan aktif…</p>}
          {!prLoading && prList.length === 0 && (
            <p className="muted small">Belum ada kebutuhan Produksi aktif. Kirim dulu dari Mode Belanja.</p>
          )}
          {!prLoading && prList.length > 0 && (
            <>
              <label className="modal-field">
                Pilih Kebutuhan
                <select value={prSelectedId}
                  onChange={e => setPrSelectedId(e.target.value)}
                  data-testid="pr-link-select">
                  <option value="">— tidak ditautkan —</option>
                  {prList.map(p => (
                    <option key={p.source_id} value={p.source_id}>
                      {fmtDT(p.created_at)} · {p.items_count} varian · oleh {p.requested_by}
                      {p.note ? ` · "${p.note.slice(0, 30)}"` : ""}
                    </option>
                  ))}
                </select>
              </label>
              {prSelected && activeVariants.length === 0 && (
                <p className="muted small">Isi produk & jumlah dulu di atas, lalu kembali ke sini untuk mengaitkan baris.</p>
              )}
              {prSelected && activeVariants.length > 0 && (
                <div className="pr-link-map">
                  <p className="muted small" style={{margin: "4px 0"}}>
                    Pilih baris kebutuhan yang sesuai untuk tiap produk (konfirmasi eksplisit — tidak otomatis).
                  </p>
                  <ul>
                    {activeVariants.map(v => {
                      const currentLid = prLineByVid[v.id] || "";
                      const suggestedLid = suggestedLineByVid[v.id] || "";
                      // Pilihan: line yang category + unit match (allow override).
                      const options = (prSelected.items || []).filter(it =>
                        (it.category || "").toLowerCase() === (v.category || "").toLowerCase()
                        && (String(it.unit).toLowerCase() === String(v.unit).toLowerCase())
                      );
                      const needsExplicitConfirm = !currentLid && suggestedLid && options.length > 1;
                      return (
                        <li key={v.id} data-testid={`pr-link-row-${v.id}`}>
                          <span className="pr-link-vid-label">{describeVariant(v)}</span>
                          <span className="pr-link-arrow">→</span>
                          <select value={currentLid}
                            className={needsExplicitConfirm ? "pr-link-select-attention" : ""}
                            onChange={e => setPrLineByVid(prev => {
                              const next = { ...prev };
                              if (e.target.value) next[v.id] = e.target.value;
                              else delete next[v.id];
                              return next;
                            })}
                            data-testid={`pr-link-row-select-${v.id}`}>
                            <option value="">— pilih baris kebutuhan (opsional) —</option>
                            {options.map(it => {
                              const isSug = it.line_id === suggestedLid;
                              return (
                                <option key={it.line_id} value={it.line_id}>
                                  {isSug ? "⭐ " : ""}{describePrLine(it)}{isSug ? " · (saran)" : ""}
                                </option>
                              );
                            })}
                          </select>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="muted small" style={{margin: "6px 0 0", fontSize: 11.5, fontStyle: "italic"}}>
                    ⭐ Saran didasarkan pada atribut varian. Operator tetap wajib memilih — tidak ada tautan otomatis.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ============================================================
// PolosanGroup — cascading Produk → Lengan → Warna → tabel ukuran
// ============================================================
function PolosanGroup({ idx, group, eligible, kind, onPatch, onDelete }) {
  const pol = useMemo(() => eligible.filter(v => v.category === "polosan"), [eligible]);
  const products = useMemo(() => Array.from(new Set(pol.map(v => v.product_name))).sort(), [pol]);
  const sleeves = useMemo(() => Array.from(new Set(pol.filter(v => v.product_name === group.product_name).map(v => v.sleeve || ""))).sort(), [pol, group.product_name]);
  const colors = useMemo(() => Array.from(new Set(pol.filter(v => v.product_name === group.product_name && (v.sleeve||"") === group.sleeve).map(v => v.color))).sort(), [pol, group.product_name, group.sleeve]);
  const sizes = useMemo(() => pol
    .filter(v => v.product_name === group.product_name && (v.sleeve||"") === group.sleeve && v.color === group.color)
    .sort((a,b) => (a.size||"").localeCompare(b.size||""))
  , [pol, group.product_name, group.sleeve, group.color]);

  const hasInput = Object.values(group.qtyByVid||{}).some(q => String(q||"").trim() !== "");
  const confirmChange = (label) => {
    if (!hasInput) return true;
    return window.confirm(`Mengganti ${label} akan mengosongkan jumlah yang sudah diisi di kelompok ini. Lanjutkan?`);
  };

  const setPatch = (patch) => onPatch({ ...patch, qtyByVid: {} });

  const changeProduct = (v) => confirmChange("Produk") && setPatch({ product_name: v, sleeve: "", color: "" });
  const changeSleeve = (v) => confirmChange("Lengan") && setPatch({ sleeve: v, color: "" });
  const changeColor = (v) => confirmChange("Warna") && setPatch({ color: v });

  const setQty = (vid, raw) => onPatch({ qtyByVid: { ...(group.qtyByVid||{}), [vid]: raw } });

  return <div style={{border:"1px solid var(--line,#e2e8f0)", borderRadius:10, padding:10, display:"flex", flexDirection:"column", gap:8}} data-testid={`stock-txn-group-${idx}`}>
    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
      <b style={{fontSize:13}}>Produk #{idx+1}</b>
      <button type="button" className="soft-btn small-btn" onClick={onDelete} data-testid={`stock-txn-group-del-${idx}`} aria-label="hapus kelompok">× Hapus</button>
    </div>
    <label className="modal-field">Produk
      <select value={group.product_name} onChange={e=>changeProduct(e.target.value)} data-testid={`stock-txn-group-${idx}-product`}>
        <option value="">— pilih produk —</option>
        {products.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
    </label>
    {group.product_name && <label className="modal-field">Lengan
      <select value={group.sleeve} onChange={e=>changeSleeve(e.target.value)} data-testid={`stock-txn-group-${idx}-sleeve`}>
        <option value="">— pilih lengan —</option>
        {sleeves.map(s => <option key={s||"__none__"} value={s}>{s ? (SLEEVE_LABEL[s] || s) : "(belum diisi)"}</option>)}
      </select>
    </label>}
    {group.sleeve !== "" && group.product_name && <label className="modal-field">Warna
      <select value={group.color} onChange={e=>changeColor(e.target.value)} data-testid={`stock-txn-group-${idx}-color`}>
        <option value="">— pilih warna —</option>
        {colors.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </label>}
    {group.color && sizes.length > 0 && <div style={{overflowX:"auto"}}>
      <table style={{width:"100%", fontSize:13, borderCollapse:"collapse"}} data-testid={`stock-txn-group-${idx}-sizes`}>
        <thead>
          <tr style={{background:"var(--wash-2,#f4efdf)"}}>
            <th style={{textAlign:"left", padding:"6px 8px"}}>Ukuran</th>
            <th style={{textAlign:"right", padding:"6px 8px"}}>Stok Saat Ini</th>
            <th style={{textAlign:"right", padding:"6px 8px"}}>Jumlah {kind==="in"?"Masuk":"Keluar"}</th>
          </tr>
        </thead>
        <tbody>
          {sizes.map(v => {
            const cur = Number(v.current_qty||0);
            const raw = group.qtyByVid?.[v.id] ?? "";
            const q = parseInt(String(raw).replace(",","."), 10) || 0;
            const overflow = kind === "out" && q > cur;
            return <tr key={v.id} style={{borderTop:"1px solid var(--line,#e2e8f0)"}}>
              <td style={{padding:"6px 8px"}}>{v.size}</td>
              <td style={{padding:"6px 8px", textAlign:"right"}}>{cur} pcs</td>
              <td style={{padding:"6px 8px", textAlign:"right"}}>
                <input type="number" min={0} step={1} value={raw}
                  onChange={e => setQty(v.id, e.target.value)}
                  placeholder="0"
                  style={{width:80, textAlign:"right", borderColor: overflow ? "#b91c1c" : undefined}}
                  data-testid={`stock-txn-group-${idx}-qty-${v.size}`}/>
              </td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>}
    {group.color && sizes.length === 0 && <p className="muted small">Tidak ada varian aktif untuk kombinasi ini.</p>}
  </div>;
}

// ============================================================
// KainGroup — cascading Nama Kain → Merek → Warna → Varian/Satuan
// ============================================================
function KainGroup({ idx, group, eligible, kind, onPatch, onDelete, rollField }) {
  const kn = useMemo(() => eligible.filter(v => v.category === "kain"), [eligible]);
  const fabrics = useMemo(() => Array.from(new Set(kn.map(v => v.fabric_name))).sort(), [kn]);
  const brands = useMemo(() => Array.from(new Set(kn.filter(v => v.fabric_name === group.fabric_name).map(v => v.brand || ""))).sort(), [kn, group.fabric_name]);
  const colors = useMemo(() => Array.from(new Set(kn.filter(v => v.fabric_name === group.fabric_name && (v.brand||"") === (group.brand||"")).map(v => v.color))).sort(), [kn, group.fabric_name, group.brand]);
  const variants = useMemo(() => kn.filter(v => v.fabric_name === group.fabric_name && (v.brand||"") === (group.brand||"") && v.color === group.color), [kn, group.fabric_name, group.brand, group.color]);

  const brandRequired = brands.some(b => b);  // kalau ada brand valid, tampilkan step
  const hasInput = String(group.qty||"").trim() !== "" || String(group.roll_count||"").trim() !== "" || (group.roll_details||[]).length > 0;
  const confirmChange = (label) => {
    if (!hasInput) return true;
    return window.confirm(`Mengganti ${label} akan mengosongkan jumlah/roll di kelompok ini. Lanjutkan?`);
  };
  const clearInput = { qty: "", roll_count: "", roll_details: [] };
  const changeFabric = (v) => confirmChange("Nama Kain") && onPatch({ fabric_name: v, brand: "", color: "", variant_id: "", unit: "", ...clearInput });
  const changeBrand = (v) => confirmChange("Merek") && onPatch({ brand: v, color: "", variant_id: "", unit: "", ...clearInput });
  const changeColor = (v) => confirmChange("Warna") && onPatch({ color: v, variant_id: "", unit: "", ...clearInput });
  const changeVariant = (vid) => {
    const v = variants.find(x => x.id === vid);
    return confirmChange("Varian/Satuan") && onPatch({ variant_id: vid, unit: v?.unit || "", ...clearInput });
  };

  const selectedVariant = variants.find(v => v.id === group.variant_id);
  const vu = (selectedVariant?.unit || "").toLowerCase();
  const isFractional = vu === "meter" || vu === "kg";
  const step = isFractional ? "0.001" : "1";
  const showRollPanel = rollField && kind === "in" && selectedVariant && vu !== "roll";
  const cur = Number(selectedVariant?.current_qty || 0);
  const q = Number(String(group.qty||"").replace(",",".")) || 0;
  const overflow = kind === "out" && q > cur;

  return <div style={{border:"1px solid var(--line,#e2e8f0)", borderRadius:10, padding:10, display:"flex", flexDirection:"column", gap:8}} data-testid={`stock-txn-kain-group-${idx}`}>
    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
      <b style={{fontSize:13}}>Kain #{idx+1}</b>
      <button type="button" className="soft-btn small-btn" onClick={onDelete}>× Hapus</button>
    </div>
    <label className="modal-field">Nama Kain
      <select value={group.fabric_name} onChange={e=>changeFabric(e.target.value)}>
        <option value="">— pilih nama kain —</option>
        {fabrics.map(f => <option key={f} value={f}>{f}</option>)}
      </select>
    </label>
    {group.fabric_name && brandRequired && <label className="modal-field">Merek
      <select value={group.brand} onChange={e=>changeBrand(e.target.value)}>
        <option value="">— pilih merek —</option>
        {brands.map(b => <option key={b||"__none__"} value={b}>{b || "(tanpa merek)"}</option>)}
      </select>
    </label>}
    {group.fabric_name && (brandRequired ? group.brand !== "" : true) && <label className="modal-field">Warna
      <select value={group.color} onChange={e=>changeColor(e.target.value)}>
        <option value="">— pilih warna —</option>
        {colors.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </label>}
    {group.color && variants.length > 0 && <label className="modal-field">Varian / Satuan
      <select value={group.variant_id} onChange={e=>changeVariant(e.target.value)}>
        <option value="">— pilih satuan —</option>
        {variants.map(v => <option key={v.id} value={v.id}>{v.unit} · saldo {fmtQty(v.current_qty, v.unit)}</option>)}
      </select>
    </label>}
    {selectedVariant && <div style={{display:"grid", gridTemplateColumns:"1fr", gap:6}}>
      <div className="muted small">Stok saat ini: <b>{fmtQty(selectedVariant.current_qty, selectedVariant.unit)}</b></div>
      <label className="modal-field">Jumlah {kind==="in"?"Masuk":"Keluar"} ({selectedVariant.unit})
        <input type="number" min={0} step={step} value={group.qty}
          onChange={e => onPatch({ qty: e.target.value })}
          placeholder={`0 ${selectedVariant.unit}`}
          style={{borderColor: overflow ? "#b91c1c" : undefined}}/>
      </label>
      {showRollPanel && <RollDetailInput
        unit={selectedVariant.unit}
        rollCount={group.roll_count}
        rollDetails={group.roll_details}
        onChange={(rc, rd) => onPatch({ roll_count: rc, roll_details: rd })}
        testid={`stock-txn-kain-group-${idx}-roll`}
      />}
      {vu === "roll" && kind === "in" && <div className="muted small" style={{fontSize:11.5}}>
        Satuan utama <b>roll</b> — jumlah di atas sudah merupakan banyak roll. Panel kemasan roll dinonaktifkan agar tidak tercatat dua kali.
      </div>}
    </div>}
  </div>;
}

function RollDetailInput({ unit, rollCount, rollDetails, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const details = rollDetails || [];
  const setDetail = (idx, patch) => {
    const nd = details.map((r,i) => i===idx ? {...r, ...patch} : r);
    onChange(rollCount, nd);
  };
  const addDetail = () => onChange(rollCount, [...details, { qty: "", label: "" }]);
  const delDetail = (idx) => onChange(rollCount, details.filter((_,i)=>i!==idx));
  const total = details.reduce((s,r)=>s + Number(r.qty||0), 0);
  return <details style={{marginTop:2}} data-testid={testid}>
    <summary style={{cursor:"pointer", fontSize:12}} className="muted">Info kemasan roll (opsional) — {rollCount || details.length || 0} roll{details.length>0?` · rincian ${total} ${unit}`:""}</summary>
    <div style={{display:"flex", flexDirection:"column", gap:6, marginTop:6}}>
      <label className="modal-field" style={{maxWidth:200}}>Jumlah Roll (info kemasan)
        <input type="number" min={0} step={1} value={rollCount ?? ""} onChange={e=>onChange(e.target.value, details)} placeholder="mis. 3"/>
      </label>
      <div className="muted small">Rincian per roll (opsional — total harus sama dengan jumlah baris):</div>
      {details.map((r,idx) => (
        <div key={idx} style={{display:"grid", gridTemplateColumns:"100px 1fr auto", gap:6}}>
          <input type="number" min={0} step="0.001" placeholder={`${unit}`} value={r.qty} onChange={e=>setDetail(idx, {qty: e.target.value})} data-testid={`${testid}-qty-${idx}`}/>
          <input value={r.label||""} onChange={e=>setDetail(idx, {label: e.target.value})} placeholder="Label roll (opsional)"/>
          <button type="button" className="soft-btn small-btn" onClick={()=>delDetail(idx)}>×</button>
        </div>
      ))}
      <button type="button" className="soft-btn small-btn" onClick={addDetail}>+ Tambah rincian roll</button>
      <p className="muted small">Panjang/berat setiap roll boleh berbeda. Sisa yang sudah dibuka tetap dicatat sesuai satuan utama saat pengeluaran.</p>
    </div>
  </details>;
}

// ============================================================
// Stok Belum Terpotong: catatan ACC Adit yang tidak terpotong otomatis (read-only, tanpa membuat transaksi).
export function AccPendingCuts({ onOpenOrder }) {
  const [data, setData] = useState(null);
  const [grp, setGrp] = useState("all");
  const [openKey, setOpenKey] = useState(null);
  const load = () => axios.get(`${API}/acc-stock-cut/pending`).then(r => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="muted small" data-testid="acc-pending-loading">Memuat Stok Belum Terpotong…</p>;
  if (data.error) return <p className="muted small" data-testid="acc-pending-error">Gagal memuat Stok Belum Terpotong.</p>;
  const c = data.counts || {};
  const items = (data.items || []).filter(i => grp === "all" || i.group === grp);
  const stats = [["cut", c.cut, "Berhasil otomatis"], ["perlu_manual", c.perlu_manual, "Perlu manual"], ["stok_kurang", c.stok_kurang, "Stok tidak cukup"]];
  return <section className="acc-pend" data-testid="acc-pending">
    <h3 style={{margin:"0 0 4px"}}>Stok Belum Terpotong — ACC Adit</h3>
    <p className="muted small" style={{margin:"0 0 10px"}}>Catatan potong otomatis yang belum berhasil. Periksa dulu Barang Keluar manual yang sudah ada sebelum memotong.</p>
    <div className="acc-pend-stats">
      {stats.map(([k, n, l]) => <button key={k} type="button" disabled={k === "cut"} aria-pressed={grp === k}
        className={`acc-pend-stat${grp === k ? " is-active" : ""}${k === "cut" && !n ? " is-zero" : ""}`}
        onClick={() => setGrp(g => g === k ? "all" : k)} data-testid={`acc-pending-stat-${k}`}>
        <b>{n || 0}</b><span>{l}</span>
      </button>)}
    </div>
    {items.length === 0 && <p className="muted small" data-testid="acc-pending-empty">Tidak ada catatan.</p>}
    <ul className="acc-pend-list">{items.map(i => <AccPendingRow key={i.key} i={i} open={openKey === i.key}
      onToggle={() => setOpenKey(k => k === i.key ? null : i.key)} onOpenOrder={onOpenOrder} onDone={load}/>)}</ul>
  </section>;
}

function AccReadButton({ recKey, tid }) {
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/acc-stock-cut/request-read`, { key: recKey }); toast.success(r.data?.message || "AI diminta membaca"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal meminta AI membaca"); }
    finally { setBusy(false); }
  };
  return <button type="button" className="soft-btn small-btn" style={{marginTop:6, marginLeft:6}} disabled={busy}
    onClick={ask} data-testid={`acc-pending-read-${tid}`}>{busy ? "Meminta…" : "Minta AI Baca"}</button>;
}

const CUT_STATE = { cut: ["Berhasil Dipotong", "chip-safe"], manual: ["Sudah Keluar Manual", "chip-safe"], short: ["Stok Kurang", "chip-red"], pending: ["Belum Dipotong", "chip-amber"] };

function AccRetryButton({ recKey, tid, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/acc-stock-cut/retry`, { key: recKey }); toast.success(r.data?.message || "Sisa diproses"); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal memotong sisa"); }
    finally { setBusy(false); }
  };
  return <button type="button" className="soft-btn small-btn" style={{marginTop:6}} disabled={busy}
    onClick={go} data-testid={`acc-pending-retry-${tid}`}>{busy ? "Memproses…" : "Potong Sisa"}</button>;
}

function AccPendingRow({ i, open, onToggle, onOpenOrder, onDone }) {
  const vLabel = (v) => v ? [v.product_name, v.color, v.sleeve && v.sleeve !== "tidak_berlaku" ? `Lengan ${SLEEVE_LABEL[v.sleeve] || v.sleeve}` : "", v.size].filter(Boolean).join(" · ") : "Varian tidak ditemukan";
  const tid = (i.key || "").replace(/[^a-zA-Z0-9]+/g, "-");
  return <li className="acc-pend-row" data-testid={`acc-pending-row-${tid}`}>
    <button type="button" className="acc-pend-head" aria-expanded={open} onClick={onToggle} data-testid={`acc-pending-open-${tid}`}>
      <span style={{minWidth:0, flex:"1 1 auto", textAlign:"left"}}>
        <b className="acc-pend-no">{i.order_number}</b>
        <span className="muted small" style={{display:"block"}}>{i.kind === "offline" ? "Offline" : "Marketplace"} · ACC {i.actor || "—"} · {fmtDT(i.at)}</span>
      </span>
      <span className={`chip small ${i.group === "stok_kurang" ? "chip-red" : "chip-amber"}`}>{i.status === "partial" ? "Sebagian Dipotong" : i.group === "stok_kurang" ? "Stok tidak cukup" : "Perlu manual"}</span>
    </button>
    <div className="acc-pend-body">
      {i.lines.length === 0
        ? <div className="muted small" data-testid={`acc-pending-unverified-${tid}`}>Rincian belum terverifikasi</div>
        : i.lines.map((ln, k) => <div key={k} className="small" data-testid={`acc-pending-line-${tid}-${k}`}>
            {vLabel(ln.variant)} — <b>{ln.qty} pcs</b>{ln.variant ? <span className="muted"> (stok {fmtQty(ln.variant.current_qty, "pcs")})</span> : null}
            {CUT_STATE[ln.state] && <> <span className={`chip small ${CUT_STATE[ln.state][1]}`} data-testid={`acc-pending-line-state-${tid}-${k}`}>{CUT_STATE[ln.state][0]}</span>
              <span className="muted">{ln.doc_number ? ` ${ln.doc_number}` : ""}{ln.state === "short" ? ` · kurang ${ln.shortage} pcs` : ""}{ln.reason && ln.state !== "cut" ? ` · ${ln.reason}` : ""}</span></>}
          </div>)}
      {i.summary && <div className="small" style={{marginTop:4}} data-testid={`acc-pending-summary-${tid}`}>
        Berhasil Dipotong: <b>{i.summary.cut} varian ({i.summary.cut_pcs} pcs)</b> · Stok Kurang: <b>{i.summary.short} varian ({i.summary.short_pcs} pcs)</b> · Belum Dipotong: <b>{i.summary.pending} varian</b>
      </div>}
      <div className="small" style={{marginTop:4}}><span className="muted">Alasan:</span> {i.reason || "—"}</div>
      {i.manual_out.length > 0 && <div className="acc-pend-manual small" data-testid={`acc-pending-manual-${tid}`}>
        Sudah ada Barang Keluar manual untuk pesanan ini: {i.manual_out.map(t => `${t.doc_number} (${fmtQty(-t.delta, "pcs")}, ${t.actor_name})`).join(", ")} — periksa sebelum memotong.
      </div>}
      {open && <div className="acc-pend-more small" data-testid={`acc-pending-detail-${tid}`}>
        {i.message && <div className="muted">{i.message}</div>}
        {i.file_id && <div className="muted">Mockup: {i.file_id}</div>}
        {i.kind === "offline" && onOpenOrder && <button type="button" className="soft-btn small-btn" style={{marginTop:6}}
          onClick={() => onOpenOrder(i.order_number)} data-testid={`acc-pending-order-${tid}`}>Buka Nota</button>}
        {i.lines.length > 0 && <AccRetryButton recKey={i.key} tid={tid} onDone={onDone}/>}
        <AccReadButton recKey={i.key} tid={tid}/>
      </div>}
    </div>
  </li>;
}


function TabMutasi({ category, variants, isOwner, onDone }) {
  const [filter, setFilter] = useState({ variant_id:"", type:"", order_number:"", date_from:"", date_to:"" });
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { category };
      Object.entries(filter).forEach(([k,v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/stock/transactions`, { params });
      // Filter defensively: hanya tampilkan txn dari varian kategori yang sedang dipilih.
      const variantIds = new Set(variants.map(v => v.id));
      const list = (r.data || []).filter(t => t.category === category || variantIds.has(t.variant_id));
      setTxns(list);
    } catch { setTxns([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, category]);

  return <div style={{display:"flex", flexDirection:"column", gap:10}}>
    <h3 style={{margin:0}}>Riwayat Mutasi — {category === "kain" ? "Kain" : "Polosan"}</h3>
    <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:6}}>
      <select value={filter.variant_id} onChange={e=>setFilter(f=>({...f,variant_id:e.target.value}))} data-testid="stock-mutasi-variant">
        <option value="">Semua Varian</option>
        {variants.map(v => <option key={v.id} value={v.id}>{variantLabel(v)}</option>)}
      </select>
      <select value={filter.type} onChange={e=>setFilter(f=>({...f,type:e.target.value}))} data-testid="stock-mutasi-type">
        <option value="">Semua Jenis</option>
        <option value="opening">Saldo Awal</option>
        <option value="in">Masuk</option>
        <option value="out">Keluar</option>
        <option value="opname_adjust">Opname</option>
        <option value="reversal">Pembalikan</option>
      </select>
      <input placeholder="Nomor Nota" value={filter.order_number} onChange={e=>setFilter(f=>({...f,order_number:e.target.value}))}/>
      <input type="date" value={filter.date_from} onChange={e=>setFilter(f=>({...f,date_from:e.target.value}))}/>
      <input type="date" value={filter.date_to} onChange={e=>setFilter(f=>({...f,date_to:e.target.value}))}/>
    </div>
    {loading && <p className="muted">Memuat…</p>}
    {!loading && txns.length===0 && <p className="muted">Belum ada mutasi.</p>}
    <div style={{display:"flex", flexDirection:"column", gap:6}}>
      {txns.map(t => {
        const unit = t.unit || "";
        return <div key={t.id} style={{border:"1px solid var(--line,#e2e8f0)", borderRadius:8, padding:8, display:"flex", flexDirection:"column", gap:2, fontSize:13}}>
          <div style={{display:"flex", justifyContent:"space-between"}}>
            <span><b>{({opening:"Saldo Awal", in:"Masuk", out:"Keluar", opname_adjust:"Opname", reversal:"Pembalikan"}[t.type]||t.type)}</b> · {t.doc_number}</span>
            <span style={{color:t.delta>=0?"#166534":"#b91c1c", fontWeight:700}}>{t.delta>=0?"+":""}{fmtQty(t.delta, unit)}</span>
          </div>
          <div className="muted small">{fmtDT(t.recorded_at)} · oleh {t.actor_name}</div>
          <div className="muted small">Saldo: {fmtQty(t.balance_before, unit)} → {fmtQty(t.balance_after, unit)}</div>
          {t.ref?.roll_count && <div className="muted small">Kemasan: {t.ref.roll_count} roll</div>}
          {t.note && <div className="muted small" style={{fontStyle:"italic"}}>“{t.note}”</div>}
          {isOwner && !t.reversal_of && !t.reversed_by && <button type="button" className="soft-btn small-btn" onClick={()=>setReverseTarget(t)} data-testid={`stock-mutasi-reverse-${t.id}`}>Balikkan</button>}
          {t.reversed_by && <span className="chip chip-muted small">Sudah dibalik</span>}
        </div>;
      })}
    </div>
    {reverseTarget && <ReverseModal txn={reverseTarget} onClose={()=>setReverseTarget(null)} onDone={()=>{setReverseTarget(null); load(); onDone();}}/>}
  </div>;
}

function ReverseModal({ txn, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!reason.trim()) { setErr("Alasan wajib"); return; }
    setBusy(true); setErr("");
    try { await axios.post(`${API}/stock/transactions/${txn.id}/reverse`, { reason }); onDone(); }
    catch (e) { setErr(e?.response?.data?.detail || "Gagal balikkan"); setBusy(false); }
  };
  return <div className="modal-backdrop">
    <div className="modal-panel">
      <h3>Balikkan {txn.doc_number}</h3>
      <p className="muted small">Delta asli: {fmtQty(txn.delta, txn.unit||"")} → pembalikan akan menambah/mengurangi kebalikannya. Guard: tidak boleh membuat saldo negatif.</p>
      {err && <div className="warn-box small">{err}</div>}
      <label className="modal-field">Alasan (wajib)<textarea rows={3} value={reason} onChange={e=>setReason(e.target.value)} data-testid="stock-reverse-reason"/></label>
      <div className="modal-actions">
        <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
        <button type="button" className="primary warn" onClick={submit} disabled={busy} data-testid="stock-reverse-submit">{busy?"…":"Konfirmasi Pembalikan"}</button>
      </div>
    </div>
  </div>;
}

// ============================================================
function TabOpname({ category, variants, canMutasi, isOwner, onDone }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/stock/opname`); setSessions(r.data || []); }
    catch { setSessions([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Filter sesi: hanya tampilkan yang punya line kategori saat ini
  const sessionsInCat = useMemo(() => sessions.filter(s =>
    (s.lines || []).some(l => (l.category || "polosan") === category)
  ), [sessions, category]);

  return <div style={{display:"flex", flexDirection:"column", gap:10}}>
    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
      <h3 style={{margin:0}}>Stok Opname — {category === "kain" ? "Kain" : "Polosan"}</h3>
      {canMutasi && <button type="button" className="primary small-btn" onClick={()=>setShowNew(true)} data-testid="stock-opname-new">+ Sesi Baru</button>}
    </div>
    {loading && <p className="muted">Memuat…</p>}
    {!loading && sessionsInCat.length === 0 && <p className="muted">Belum ada sesi opname untuk kategori ini.</p>}
    {sessionsInCat.map(s => {
      const stat = summarizeOpname(s);
      return <article key={s.id} className="at-prod-card" style={{padding:10}} data-testid={`stock-opname-${s.id}`}>
        <div style={{display:"flex", justifyContent:"space-between"}}>
          <b>{s.session_number}</b>
          <span className="muted small">{fmtDT(s.created_at)}</span>
        </div>
        <div className="muted small">{s.lines?.length||0} varian · {stat.draft} draft · {stat.submitted} submitted · {stat.needs_recount} perlu recount · {stat.approved} approved · {stat.rejected} rejected</div>
        <button type="button" className="soft-btn small-btn" style={{marginTop:6}} onClick={()=>setDetail(s)} data-testid={`stock-opname-detail-${s.id}`}>Lihat Detail</button>
      </article>;
    })}
    {showNew && canMutasi && <OpnameNew category={category} variants={variants} onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false); load();}}/>}
    {detail && <OpnameDetail category={category} session={detail} canMutasi={canMutasi} isOwner={isOwner} onClose={()=>setDetail(null)} onChanged={()=>{load(); onDone();}}/>}
  </div>;
}

function summarizeOpname(s) {
  const r = { draft:0, submitted:0, needs_recount:0, approved:0, rejected:0 };
  for (const l of (s.lines||[])) r[l.status] = (r[l.status]||0) + 1;
  return r;
}

function OpnameNew({ category, variants, onClose, onSaved }) {
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const eligible = variants.filter(v => v.status !== "belum_dihitung" && v.uses_stock !== false && v.is_active !== false);
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/stock/opname`, { variant_ids: Array.from(sel) });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Gagal buat sesi"); }
    finally { setBusy(false); }
  };
  return <div className="modal-backdrop">
    <div className="modal-panel" style={{maxWidth:520}}>
      <h3>Sesi Opname Baru — {category === "kain" ? "Kain" : "Polosan"}</h3>
      {err && <div className="warn-box small">{err}</div>}
      <div style={{maxHeight:320, overflowY:"auto", display:"flex", flexDirection:"column", gap:4}}>
        {eligible.map(v => (
          <label key={v.id} className="modal-check">
            <input type="checkbox" checked={sel.has(v.id)} onChange={e=>{
              const n = new Set(sel);
              if (e.target.checked) n.add(v.id); else n.delete(v.id);
              setSel(n);
            }}/>
            <span>{variantLabel(v)} <span className="muted small">(saldo {fmtQty(v.current_qty, v.unit||"")})</span></span>
          </label>
        ))}
      </div>
      <div className="modal-actions">
        <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
        <button type="button" className="primary" onClick={submit} disabled={busy || sel.size===0} data-testid="stock-opname-new-submit">{busy?"…":`Buat Sesi (${sel.size})`}</button>
      </div>
    </div>
  </div>;
}

function OpnameDetail({ category, session, canMutasi, isOwner, onClose, onChanged }) {
  const [lines, setLines] = useState(session.lines || []);
  const [physical, setPhysical] = useState({});
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    try { const r = await axios.get(`${API}/stock/opname`, { params: {} });
      const upd = (r.data||[]).find(s => s.id === session.id);
      if (upd) setLines(upd.lines || []);
    } catch {}
  };

  const submit = async (variant_id) => {
    setBusy(variant_id);
    try {
      await axios.post(`${API}/stock/opname/${session.id}/submit-line`, { physical_qty: Number(physical[variant_id]||0), note: "" }, { params: { variant_id } });
      await refresh(); onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal submit"); }
    finally { setBusy(null); }
  };
  const recount = async (variant_id) => {
    setBusy(variant_id);
    try {
      await axios.post(`${API}/stock/opname/${session.id}/recount-line`, {}, { params: { variant_id } });
      await refresh(); onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal recount"); }
    finally { setBusy(null); }
  };
  const approve = async (variant_id, approveFlag) => {
    const reason = prompt(approveFlag ? "Alasan approve:" : "Alasan reject:");
    if (!reason) return;
    setBusy(variant_id);
    try {
      await axios.post(`${API}/stock/opname/${session.id}/approve-line`, { approve: approveFlag, reason }, { params: { variant_id } });
      await refresh(); onChanged();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal approve"); }
    finally { setBusy(null); }
  };

  return <div className="modal-backdrop">
    <div className="modal-panel" style={{maxWidth:720, maxHeight:"85vh", overflowY:"auto"}}>
      <h3>{session.session_number}</h3>
      <p className="muted small">Dibuat {fmtDT(session.created_at)} oleh {session.created_by}</p>
      <div style={{display:"flex", flexDirection:"column", gap:8, marginTop:8}}>
        {lines.map(l => {
          const lineCat = l.category || "polosan";
          const unit = l.unit || (lineCat === "kain" ? "" : "pcs");
          const uLow = (unit || "").toLowerCase();
          const isFractional = uLow === "meter" || uLow === "kg";
          const step = isFractional ? "0.001" : "1";
          const canSubmit = canMutasi && (l.status === "draft" || l.status === "needs_recount");
          const canApprove = isOwner && l.status === "submitted";
          const canRecount = canMutasi && l.status === "needs_recount";
          const diff = (l.physical_qty ?? 0) - (l.snapshot_qty ?? 0);
          const label = lineCat === "kain"
            ? [l.fabric_name || l.product_name, l.brand, l.color, l.unit].filter(Boolean).join(" · ")
            : [l.product_name, l.color, l.size].filter(Boolean).join(" · ");
          return <div key={l.variant_id} style={{border:"1px solid var(--line,#e2e8f0)", borderRadius:8, padding:8, fontSize:13, display:"flex", flexDirection:"column", gap:4}}>
            <div style={{display:"flex", justifyContent:"space-between"}}>
              <b>{label}</b>
              <span className={`chip ${l.status==="approved"?"chip-safe":l.status==="rejected"?"chip-danger":l.status==="needs_recount"?"chip-amber":"chip-muted"}`}>{l.status}</span>
            </div>
            <div className="muted small">Saldo pembanding: <b>{fmtQty(l.snapshot_qty, unit)}</b> · Snapshot {fmtDT(l.snapshot_at)}</div>
            {l.physical_qty!==null && l.physical_qty!==undefined && <div className="muted small">Fisik: <b>{fmtQty(l.physical_qty, unit)}</b> · Selisih: <b style={{color:diff>=0?"#166534":"#b91c1c"}}>{diff>=0?"+":""}{fmtQty(diff, unit)}</b></div>}
            {l.status === "needs_recount" && <div className="warn-box small">Ada mutasi baru sejak snapshot — silakan recount sebelum approval.</div>}
            <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
              {canSubmit && <>
                <input type="number" min={0} step={step} placeholder={`Jumlah fisik (${unit})`} style={{width:150}}
                  value={physical[l.variant_id] ?? ""} onChange={e=>setPhysical(p=>({...p, [l.variant_id]: e.target.value}))}
                  data-testid={`stock-opname-physical-${l.variant_id}`}
                />
                <button type="button" className="primary small-btn" onClick={()=>submit(l.variant_id)} disabled={busy===l.variant_id} data-testid={`stock-opname-submit-${l.variant_id}`}>{busy===l.variant_id?"…":"Submit"}</button>
              </>}
              {canRecount && <button type="button" className="soft-btn small-btn" onClick={()=>recount(l.variant_id)} disabled={busy===l.variant_id}>Recount (refresh snapshot)</button>}
              {canApprove && <>
                <button type="button" className="primary small-btn" onClick={()=>approve(l.variant_id, true)} disabled={busy===l.variant_id} data-testid={`stock-opname-approve-${l.variant_id}`}>Approve</button>
                <button type="button" className="soft-btn small-btn" onClick={()=>approve(l.variant_id, false)} disabled={busy===l.variant_id}>Reject</button>
              </>}
            </div>
          </div>;
        })}
      </div>
      <div className="modal-actions">
        <button type="button" className="primary" onClick={onClose}>Tutup</button>
      </div>
    </div>
  </div>;
}

// ============================================================
// TabPotong — Iter 284: Pengeluaran Kain ke Potong + Integrasi Jahit
// ============================================================
const CUT_STATUS_LABEL = {
  draft: {t: "Draft", c: "#a16207"},
  submitted: {t: "Terserah · Menunggu Jahit", c: "#0891b2"},
  submitting: {t: "Memproses…", c: "#a16207"},
  sent: {t: "Terkirim ke Jahit", c: "#166534"},
  needs_mapping: {t: "Perlu pemetaan di Jahit", c: "#a16207"},
  not_sent: {t: "Kain sudah diserahkan · Belum terkirim ke Jahit", c: "#b91c1c"},
  conflict: {t: "Konflik di Jahit — perbaiki di sisi Jahit", c: "#b91c1c"},
  cancel_pending: {t: "Menunggu konfirmasi batal Jahit", c: "#c2410c"},
  reversing: {t: "Mengembalikan stok…", c: "#c2410c"},
  cancelled: {t: "Dibatalkan", c: "#64748b"},
  cancel_rejected: {t: "Batal ditolak · Sudah dipotong", c: "#b91c1c"},
};

// Iter 398 — Filter grup untuk hierarki UI. "Perlu Tindakan" menggabungkan status
//   needs_mapping / not_sent / conflict / cancel_rejected / cancel_pending.
const CUT_ATTENTION_STATUSES = new Set(["needs_mapping", "not_sent", "conflict", "cancel_rejected", "cancel_pending"]);
const CUT_PRIMARY_FILTERS = [
  { key: "", label: "Semua" },
  { key: "attention", label: "Perlu Tindakan" },
  { key: "sent", label: "Terkirim" },
  { key: "cancelled", label: "Dibatalkan" },
];
const CUT_SECONDARY_FILTERS = [
  { key: "draft", label: "Draft" },
  { key: "needs_mapping", label: "Perlu Mapping" },
  { key: "not_sent", label: "Gagal Kirim" },
  { key: "conflict", label: "Konflik" },
  { key: "cancel_pending", label: "Batal Pending" },
  { key: "cancel_rejected", label: "Batal Ditolak" },
];

function TabPotong({ variants, canMutasi, isOwner, onDone, onOpenOrder }) {
  const [items, setItems] = useState([]);
  const [wip, setWip] = useState(null);
  const [filter, setFilter] = useState(""); // "" | "attention" | <raw status>
  const [integStatus, setIntegStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // "attention" = fetch semua lalu filter client-side (gabungan multi-status).
      const apiStatus = filter && filter !== "attention" ? filter : undefined;
      const [listR, integR] = await Promise.all([
        axios.get(`${API}/stock/cut-orders`, { params: apiStatus ? { status: apiStatus } : {} }),
        axios.get(`${API}/stock/cut-orders/integration-status`).catch(() => ({data: null})),
      ]);
      setItems(listR.data?.items || []);
      setIntegStatus(integR.data);
      if (isOwner) {
        const wipR = await axios.get(`${API}/stock/cut-orders/wip-value`).catch(()=>({data:null}));
        setWip(wipR.data);
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal memuat daftar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [filter]);

  const kainVariants = useMemo(() => (variants || []).filter(v => v.category === "kain" && (v.unit || "").toLowerCase() === "roll" && v.is_active), [variants]);

  // Client-side filter untuk "attention".
  const displayItems = useMemo(() => {
    if (filter !== "attention") return items;
    return items.filter(it => CUT_ATTENTION_STATUSES.has(it.status));
  }, [items, filter]);

  if (!canMutasi) {
    return <div className="warn-box small" data-testid="cut-nomutasi">
      Hanya Owner atau tim Gudang yang dapat mengelola Pengeluaran Kain ke Potong.
    </div>;
  }

  return <div data-testid="tab-potong" className="cut-page" style={{display:"flex", flexDirection:"column", gap:14}}>
    {/* Iter 398 — Hero card: judul + deskripsi + CTA full-width. */}
    <div className="cut-hero-card" data-testid="cut-hero-card">
      <div className="cut-hero-row">
        <div className="cut-hero-icon" aria-hidden="true">
          <Scissors size={22}/>
        </div>
        <div className="cut-hero-text">
          <h3 className="cut-hero-title">Pengeluaran Kain ke Potong</h3>
          <p className="cut-hero-sub">Stok berkurang setelah kain diserahkan ke bagian potong.</p>
        </div>
      </div>
      <button type="button" className="cut-hero-cta"
        onClick={()=>{ setEditing(null); setShowForm(true); }}
        data-testid="cut-new-btn">
        <PlusCircle size={16} aria-hidden="true"/> Buat Pengeluaran
      </button>
    </div>

    {/* Iter 398 — Status koneksi Jahit: 1 bar sederhana. Teknis masuk warning box bila belum ready. */}
    {integStatus && !integStatus.ready && <div className="warn-box small" style={{background:"#fef3c7", border:"1px solid #f59e0b"}} data-testid="cut-integ-notready">
      ⚠️ <b>Integrasi Jahit belum siap.</b> Konfigurasi <code>JAHIT_API_URL</code>{integStatus.url_set?"":" (kosong)"} & <code>JAHIT_API_SECRET</code>{integStatus.secret_set?"":" (kosong)"} di backend/.env.
    </div>}
    {integStatus && integStatus.ready && (
      <div className="cut-integ-bar" data-testid="cut-integ-ready" title="Terhubung ke aplikasi Jahit">
        <span className="cut-integ-dot" aria-hidden="true"/>
        <span className="cut-integ-text">Terhubung ke aplikasi Jahit</span>
      </div>
    )}

    {isOwner && wip && <div className="card" style={{padding:"10px 12px"}} data-testid="cut-wip-panel">
      <div style={{fontWeight:700, marginBottom:4}}>Bahan Dalam Produksi (Owner)</div>
      <div className="muted small">Bahan yang sudah diserahkan tapi belum kembali sebagai barang jadi.</div>
      <div style={{marginTop:6, display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8, fontSize:13}}>
        <div><b>{wip.wip_count}</b> dokumen</div>
        <div><b>{wip.wip_qty_roll_total}</b> roll</div>
        <div><b>Rp {money(wip.wip_value_total)}</b> nilai</div>
      </div>
    </div>}

    {/* Iter 398 — Filter utama 4 pil + "Filter lainnya" dropdown. */}
    <div className="cut-filter-row" data-testid="cut-filter-row">
      {CUT_PRIMARY_FILTERS.map(f => (
        <button key={f.key || "all"} type="button"
          className={`cut-filter-pill${filter === f.key ? " cut-filter-pill-active" : ""}`}
          onClick={()=>{ setFilter(f.key); setShowMoreFilters(false); }}
          data-testid={`cut-filter-${f.key || "all"}`}>{f.label}</button>
      ))}
    </div>
    <div style={{position:"relative"}}>
      <button type="button" className="cut-filter-more"
        onClick={()=>setShowMoreFilters(v => !v)}
        data-testid="cut-filter-more-btn"
        aria-expanded={showMoreFilters}>
        <SlidersHorizontal size={13} aria-hidden="true"/> Filter lainnya
        <ChevronDown size={13} aria-hidden="true" style={{transform: showMoreFilters ? "rotate(180deg)" : "none", transition:"transform .15s"}}/>
      </button>
      {showMoreFilters && (
        <div className="cut-filter-more-pop" data-testid="cut-filter-more-pop" role="menu">
          {CUT_SECONDARY_FILTERS.map(f => (
            <button key={f.key} type="button" role="menuitem"
              className={`cut-filter-more-item${filter === f.key ? " active" : ""}`}
              onClick={()=>{ setFilter(f.key); setShowMoreFilters(false); }}
              data-testid={`cut-filter-${f.key}`}>{f.label}</button>
          ))}
        </div>
      )}
    </div>

    {msg && <div className="warn-box small">{msg}</div>}
    {loading && <div className="muted small">Memuat…</div>}
    {!loading && displayItems.length === 0 && <div className="muted small" style={{padding:16, textAlign:"center", border:"1px dashed #e2e8f0", borderRadius:10}}>Belum ada perintah potong untuk filter ini.</div>}
    {!loading && displayItems.length > 0 && <div style={{display:"flex", flexDirection:"column", gap:10}}>
      {displayItems.map(it => <CutOrderCard key={it.id} it={it} isOwner={isOwner} integReady={integStatus?.ready}
        onOpenOrder={onOpenOrder}
        onEdit={()=>{ setEditing(it); setShowForm(true); }}
        onChanged={() => { load(); onDone && onDone(); }} />)}
    </div>}

    {showForm && <CutOrderForm
      kainVariants={kainVariants}
      editing={editing}
      onClose={()=>{setShowForm(false); setEditing(null);}}
      onSaved={()=>{setShowForm(false); setEditing(null); load(); onDone && onDone();}}/>}
  </div>;
}

function CutOrderCard({ it, isOwner, integReady, onOpenOrder, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const st = CUT_STATUS_LABEL[it.status] || {t: it.status, c: "#64748b"};
  const v = it.variant_snapshot || {};
  const isCancelled = it.status === "cancelled";
  const isCancelRejected = it.status === "cancel_rejected";
  const isCancelPending = it.status === "cancel_pending";
  const isReversing = it.status === "reversing";
  // Kondisi tombol Batalkan boleh muncul (draft atau doc yang sudah diserahkan/pending).
  const canCancel = ["draft","submitted","sent","needs_mapping","not_sent","conflict","cancel_pending"].includes(it.status);
  const needConfirmations = it.status !== "draft"; // untuk yang sudah diserahkan wajib checklist + alasan
  const doAction = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/stock/cut-orders/${it.id}/${action}`);
      onChanged();
    } catch (e) {
      setMsg(e?.response?.data?.detail || `Gagal ${action}`);
    } finally { setBusy(false); }
  };
  const doSubmit = async () => {
    if (busy || submitting) return;
    const ok = await notify.confirm({
      title: "Serahkan kain?",
      message: `Draft ${it.doc_number} akan diserahkan dan stok akan berkurang.`,
      details: (
        <div style={{fontSize:13, lineHeight:1.55, textAlign:"left"}}>
          <div><b>Kain:</b> {v.fabric_name}{v.brand?` · ${v.brand}`:""}{v.color?` · ${v.color}`:""}</div>
          <div><b>Jumlah:</b> {it.qty_roll} roll</div>
          <div><b>Tujuan:</b> {it.destination_product_name} <span className="muted">({it.destination_type === "order" ? "Pesanan" : "Stok Toko"})</span></div>
          {it.order_number && <div><b>No. Pesanan:</b> {it.order_number}</div>}
          <div><b>Penerima:</b> {it.receiver_name}</div>
          {it.note && <div><b>Catatan:</b> {it.note}</div>}
        </div>
      ),
      primaryLabel: `Serahkan ${it.qty_roll} roll`,
      secondaryLabel: "Kembali",
    });
    if (!ok) return;
    setSubmitting(true); setMsg("");
    try {
      await axios.post(`${API}/stock/cut-orders/${it.id}/submit`);
      onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Gagal menyerahkan kain. Coba lagi.";
      setMsg(detail);
      try { await notify.error({ title: "Gagal menyerahkan kain", message: detail }); } catch { /* noop */ }
    } finally { setSubmitting(false); }
  };
  const doDelete = async () => {
    if (!window.confirm("Hapus draft ini?")) return;
    setBusy(true); setMsg("");
    try {
      await axios.delete(`${API}/stock/cut-orders/${it.id}`);
      onChanged();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal menghapus");
    } finally { setBusy(false); }
  };
  // Iter 398 — Format tanggal pendek "DD/MM/YYYY · HH:mm" (hilangkan "oleh X" dari header; muncul di detail bila perlu).
  const fmtShort = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2,"0");
      const mm = String(d.getMonth()+1).padStart(2,"0");
      const hh = String(d.getHours()).padStart(2,"0");
      const mi = String(d.getMinutes()).padStart(2,"0");
      return `${dd}/${mm}/${d.getFullYear()} · ${hh}.${mi}`;
    } catch { return iso; }
  };
  const kainLabel = [v.fabric_name, v.brand, v.color].filter(Boolean).join(" · ");
  const needsResend = (it.status === "not_sent" || it.status === "needs_mapping" || it.status === "sent") && integReady;
  return <div className="cut-card" data-testid={`cut-card-${it.id}`} style={{opacity: isCancelled ? 0.78 : 1}}>
    {/* Header: nomor dokumen + status chip */}
    <div className="cut-card-head">
      <div className="cut-card-doc" data-testid={`cut-doc-${it.id}`}>{it.doc_number}</div>
      <span className="cut-card-status-chip" data-testid={`cut-status-${it.id}`}
        style={{background: st.c + "22", color: st.c, borderColor: st.c + "55"}}>
        {it.status === "sent" ? <CheckCircle2 size={12} aria-hidden="true"/> :
         CUT_ATTENTION_STATUSES.has(it.status) ? <AlertTriangle size={12} aria-hidden="true"/> :
         <Info size={12} aria-hidden="true"/>}
        {st.t}
      </span>
    </div>
    {/* Rows: ikon label-nilai */}
    <ul className="cut-card-rows">
      <li className="cut-card-row">
        <Target size={14} className="cut-card-row-icon" aria-hidden="true"/>
        <span className="cut-card-row-label">Tujuan</span>
        <span className="cut-card-row-value">
          <span className="cut-card-fabric-name">{it.destination_product_name}</span>
          {it.destination_type === "order" && it.order_number &&
            <> <button type="button" className="link-btn" onClick={()=>onOpenOrder && onOpenOrder(it.order_number)}
              style={{fontSize:12, marginLeft:4}}>{it.order_number}</button></>}
        </span>
      </li>
      <li className="cut-card-row">
        <Layers size={14} className="cut-card-row-icon" aria-hidden="true"/>
        <span className="cut-card-row-label">Kain</span>
        <span className="cut-card-row-value">
          <span className="cut-card-fabric-name">{v.fabric_name || "—"}</span>
          {(v.brand || v.color || it.qty_roll) && <span className="cut-card-fabric-meta">
            {v.brand && <> · {v.brand}</>}
            {v.color && <> · {v.color}</>}
            {it.qty_roll && <> · <b>{it.qty_roll} roll</b></>}
          </span>}
        </span>
      </li>
      <li className="cut-card-row">
        <User size={14} className="cut-card-row-icon" aria-hidden="true"/>
        <span className="cut-card-row-label">Penerima</span>
        <span className="cut-card-row-value">{it.receiver_name}</span>
      </li>
      <li className="cut-card-row">
        <Calendar size={14} className="cut-card-row-icon" aria-hidden="true"/>
        <span className="cut-card-row-label">Dibuat</span>
        <span className="cut-card-row-value">{fmtShort(it.created_at)}</span>
      </li>
    </ul>
    {/* Catatan / info kondisi khusus (dipertahankan). */}
    {it.note && <div className="cut-card-note muted small">Catatan: {it.note}</div>}
    {isOwner && it.value_transferred != null && !isCancelled && <div className="muted small" style={{color:"#166534", padding:"0 2px"}}>
      Nilai bahan: Rp {money(it.value_transferred)}
    </div>}
    {isCancelled && it.cancel_reason && <div className="cut-card-info" data-testid={`cut-cancelled-info-${it.id}`}>
      <b>Dibatalkan:</b> {it.cancel_reason}
      {it.cancelled_by && <> · oleh {it.cancelled_by}</>}
    </div>}
    {isCancelPending && <div className="cut-card-info cut-card-info-warn" data-testid={`cut-cancel-pending-info-${it.id}`}>
      <b>Menunggu konfirmasi batal dari Jahit.</b> Stok belum dikembalikan.
    </div>}
    {isCancelRejected && <div className="cut-card-info cut-card-info-danger" data-testid={`cut-cancel-rejected-info-${it.id}`}>
      <b>Batal ditolak Jahit:</b> {it.cancel_rejected_reason || "Kain sudah dipotong/dialokasikan"}
    </div>}
    {/* Rincian teknis (collapsible) — dipindah dari header atas supaya tidak membebani visual. */}
    {(it.jahit_last_status || it.submitted_at || it.cancel_attempts != null || it.created_by) && (
      <details className="cut-card-tech">
        <summary className="cut-card-tech-summary muted small">Rincian teknis</summary>
        <div className="cut-card-tech-body muted small">
          {it.created_by && <div>Dibuat oleh: {it.created_by}</div>}
          {it.jahit_last_status && <div>HTTP {it.jahit_last_status} · {it.jahit_attempts || 0}× kirim</div>}
          {it.submitted_at && <div>Terserah: {new Date(it.submitted_at).toLocaleString("id-ID")}</div>}
          {it.cancel_attempts != null && <div>Percobaan batal: {it.cancel_attempts}×</div>}
        </div>
      </details>
    )}
    {msg && <div className="warn-box small" style={{marginTop:6}} data-testid={`cut-msg-${it.id}`}>{msg}</div>}
    {/* Tombol aksi: primary emas + sekunder outline. */}
    <div className="cut-card-actions">
      {it.status === "draft" && <>
        <button type="button" className="cut-btn-secondary" onClick={onEdit} disabled={busy || submitting} data-testid={`cut-edit-${it.id}`}>Ubah</button>
        <button type="button" className="cut-btn-secondary" onClick={doDelete} disabled={busy || submitting} data-testid={`cut-delete-${it.id}`} style={{color:"#b91c1c"}}>Hapus</button>
        <button type="button" className="cut-btn-primary"
          onClick={doSubmit}
          disabled={busy || submitting || !integReady}
          title={!integReady ? "Integrasi Jahit belum siap" : ""}
          data-testid={`cut-submit-${it.id}`}>
          <Scissors size={14} aria-hidden="true"/> {submitting ? "Menyerahkan…" : "Serahkan Kain"}
        </button>
      </>}
      {needsResend && <button type="button" className="cut-btn-primary" onClick={()=>doAction("resend")}
        disabled={busy} data-testid={`cut-resend-${it.id}`}>
        <Send size={14} aria-hidden="true"/> Kirim Ulang
      </button>}
      {canCancel && !isReversing && <button type="button" className="cut-btn-secondary"
        onClick={()=>setShowCancel(true)}
        disabled={busy}
        data-testid={`cut-cancel-btn-${it.id}`}>
        <X size={14} aria-hidden="true"/> {isCancelPending ? "Coba Lagi Batalkan" : "Batalkan"}
      </button>}
    </div>
    {showCancel && <CancelCutModal it={it} needConfirmations={needConfirmations}
      onClose={()=>setShowCancel(false)}
      onDone={()=>{ setShowCancel(false); onChanged(); }}/>}
  </div>;
}

// Modal pembatalan Perintah Potong (Iter 286).
// - Draft: alasan opsional, konfirmasi tunggal.
// - Submitted/sent/dst: alasan WAJIB + 2 checkbox konfirmasi (kain belum keluar/potong, sudah kembali utuh).
// Backend memvalidasi ulang; UI hanya guard agar tidak submit kosong.
function CancelCutModal({ it, needConfirmations, onClose, onDone }) {
  const v = it.variant_snapshot || {};
  const [reason, setReason] = useState(it.cancel_reason || "");
  const [confirmNotCut, setConfirmNotCut] = useState(false);
  const [confirmReturned, setConfirmReturned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = !busy && (
    !needConfirmations ? true :
    (reason.trim().length >= 3 && confirmNotCut && confirmReturned)
  );

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = needConfirmations
        ? { reason: reason.trim(), confirm_not_cut: confirmNotCut, confirm_returned_intact: confirmReturned }
        : { reason: reason.trim() };
      const r = await axios.post(`${API}/stock/cut-orders/${it.id}/cancel`, payload);
      const st = r.data?.status;
      // Note: status cancel_pending juga counts sebagai "berhasil diajukan" — biarkan card refresh.
      onDone();
      if (st === "cancel_pending") {
        // Non-blocking hint via alert
        window.alert("Permintaan batal terkirim tapi Jahit belum konfirmasi. Stok belum dikembalikan. Coba lagi nanti.");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "string") setErr(d);
      else if (d && typeof d === "object") setErr(d.detail || d.status || JSON.stringify(d));
      else setErr(e?.message || "Gagal membatalkan");
    } finally { setBusy(false); }
  };

  return <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:12}} data-testid={`cut-cancel-modal-${it.id}`}>
    <div className="card" style={{maxWidth:520, width:"100%", padding:16, background:"#fff"}}>
      <div style={{fontWeight:700, fontSize:15, marginBottom:4}}>Batalkan Perintah Potong</div>
      <div className="muted small" style={{marginBottom:10}}>
        {it.doc_number} · {v.fabric_name} {v.color} · <b>{it.qty_roll} roll</b> · ke {it.receiver_name}
      </div>

      {!needConfirmations && <div className="muted small" style={{marginBottom:8}}>
        Draft belum diserahkan. Pembatalan tidak mengubah stok.
      </div>}
      {needConfirmations && <div style={{background:"#fef3c7", border:"1px solid #f59e0b", padding:"8px 10px", borderRadius:6, fontSize:12, marginBottom:10}}>
        <b>Perhatian:</b> Kain sudah tercatat keluar. Stok hanya dikembalikan setelah tim Jahit konfirmasi bahwa kain belum dipotong. Bila Jahit belum membalas, status jadi <b>Batal Pending</b> dan stok tetap tidak berubah.
      </div>}

      <label style={{display:"block", fontSize:12, fontWeight:600, marginBottom:4}}>Alasan pembatalan {needConfirmations && <span style={{color:"#b91c1c"}}>*</span>}</label>
      <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
        placeholder={needConfirmations ? "Contoh: salah tujuan / salah warna / order dibatalkan pelanggan" : "Opsional"}
        style={{width:"100%", padding:"8px 10px", border:"1px solid #cbd5e1", borderRadius:6, fontSize:13, resize:"vertical"}}
        data-testid={`cut-cancel-reason-${it.id}`}/>

      {needConfirmations && <div style={{marginTop:10, display:"flex", flexDirection:"column", gap:6}}>
        <label style={{display:"flex", gap:8, alignItems:"start", fontSize:12, cursor:"pointer"}}>
          <input type="checkbox" checked={confirmNotCut} onChange={e=>setConfirmNotCut(e.target.checked)}
            data-testid={`cut-cancel-confirm-notcut-${it.id}`}/>
          <span>Saya konfirmasi <b>kain belum dipotong / belum digunakan</b> di sisi Jahit.</span>
        </label>
        <label style={{display:"flex", gap:8, alignItems:"start", fontSize:12, cursor:"pointer"}}>
          <input type="checkbox" checked={confirmReturned} onChange={e=>setConfirmReturned(e.target.checked)}
            data-testid={`cut-cancel-confirm-returned-${it.id}`}/>
          <span>Saya konfirmasi <b>kain sudah kembali utuh</b> ke Gudang (atau siap kembali).</span>
        </label>
      </div>}

      {err && <div className="warn-box small" style={{marginTop:10}} data-testid={`cut-cancel-error-${it.id}`}>{err}</div>}

      <div style={{marginTop:14, display:"flex", gap:8, justifyContent:"flex-end", flexWrap:"wrap"}}>
        <button type="button" className="soft-btn small-btn" onClick={onClose} disabled={busy}
          data-testid={`cut-cancel-modal-close-${it.id}`}>Tutup</button>
        <button type="button" className="primary small-btn" onClick={submit} disabled={!canSubmit}
          style={{background: canSubmit ? "#b91c1c" : undefined, borderColor: canSubmit ? "#b91c1c" : undefined}}
          data-testid={`cut-cancel-modal-submit-${it.id}`}>
          {busy ? "Memproses…" : "Ya, Batalkan"}
        </button>
      </div>
    </div>
  </div>;
}

// Iter 287az — Bottom sheet picker varian kain: search + list clickable.
// Props:
//   • variants: list varian (fabric_name, brand, color, current_qty, unit, id, sku?/code?)
//   • selectedId: id varian yang sedang terpilih (highlight ✓)
//   • onPick(id): dipanggil saat user tap sebuah varian → close + commit.
//   • onClose(): dipanggil saat user tutup tanpa memilih → pilihan sebelumnya tetap.
// Catatan: hanya view/pilih — tidak menyimpan draft atau mengurangi stok.
function FabricVariantPicker({ variants, selectedId, onPick, onClose }) {
  const [q, setQ] = useState("");
  const searchRef = useRef(null);
  const listRef = useRef(null);
  // Fokus search otomatis saat buka (desktop); di iPhone biarkan user ketuk sendiri agar keyboard muncul karena gesture.
  useEffect(() => {
    const isTouch = typeof window !== "undefined" && ("ontouchstart" in window);
    if (!isTouch) {
      const t = setTimeout(() => searchRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, []);
  // Escape untuk tutup.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Scroll lock halaman belakang saat picker aktif.
  useEffect(() => lockBodyScroll(), []);
  // Scroll ke item terpilih setelah mount.
  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-variant-id="${selectedId}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return variants;
    return variants.filter(v => {
      const parts = [v.fabric_name, v.brand, v.color, v.sku, v.code]
        .filter(Boolean).map(s => String(s).toLowerCase());
      return parts.some(s => s.includes(needle));
    });
  }, [q, variants]);

  return (
    <div className="fv-picker-backdrop" role="presentation"
      data-testid="fabric-variant-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="fv-picker-sheet" role="dialog" aria-modal="true" aria-labelledby="fv-picker-title">
        <header className="fv-picker-head">
          <h4 id="fv-picker-title" className="fv-picker-title">Pilih Varian Kain</h4>
          <button type="button" className="fv-picker-close" onClick={onClose}
            aria-label="Tutup" data-testid="fabric-variant-picker-close">
            <X size={18}/>
          </button>
        </header>
        <div className="fv-picker-search-wrap">
          <Search size={16} className="fv-picker-search-icon" aria-hidden="true"/>
          <input ref={searchRef} type="search"
            className="fv-picker-search-input"
            placeholder="Cari kain, warna, atau kode…"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="fabric-variant-picker-search"
            aria-label="Cari varian kain"
            autoCapitalize="none" autoCorrect="off" spellCheck="false"/>
          {q && <button type="button" className="fv-picker-search-clear" onClick={() => setQ("")}
            aria-label="Bersihkan pencarian" data-testid="fabric-variant-picker-clear">
            <X size={14}/>
          </button>}
        </div>
        <div ref={listRef} className="fv-picker-list" data-testid="fabric-variant-picker-list">
          {filtered.length === 0 ? (
            <div className="fv-picker-empty" data-testid="fabric-variant-picker-empty">
              <p style={{margin:"0 0 4px", fontWeight:700}}>Varian tidak ditemukan</p>
              <p className="muted small" style={{margin:0}}>Coba kata kunci lain atau bersihkan pencarian.</p>
            </div>
          ) : filtered.map(v => {
            const isSel = v.id === selectedId;
            const code = v.sku || v.code || "";
            return (
              <button key={v.id} type="button"
                data-variant-id={v.id}
                className={`fv-picker-item ${isSel ? "selected" : ""}`}
                onClick={() => onPick(v.id)}
                data-testid={`fabric-variant-picker-item-${v.id}`}>
                <span className="fv-picker-item-main">
                  <strong className="fv-picker-item-name">{v.fabric_name}</strong>
                  <span className="fv-picker-item-meta">
                    {[v.color, v.brand, code].filter(Boolean).join(" · ") || "—"}
                  </span>
                </span>
                <span className="fv-picker-item-stock">
                  <span className="fv-picker-item-stock-qty">{v.current_qty}</span>
                  <span className="fv-picker-item-stock-unit">{v.unit || "roll"}</span>
                </span>
                {isSel && <Check size={16} className="fv-picker-item-check" aria-hidden="true"/>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function CutOrderForm({ kainVariants, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [destType, setDestType] = useState(editing?.destination_type || "stock");
  const [destName, setDestName] = useState(editing?.destination_product_name || "");
  const [orderNo, setOrderNo] = useState(editing?.order_number || "");
  const [variantId, setVariantId] = useState(editing?.variant_id || "");
  const [qtyRoll, setQtyRoll] = useState(editing?.qty_roll || 1);
  const [receiver, setReceiver] = useState(editing?.receiver_name || "");
  const [note, setNote] = useState(editing?.note || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  // Iter 287az — Bottom sheet picker varian kain.
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);

  const selVariant = useMemo(() => kainVariants.find(v => v.id === variantId), [variantId, kainVariants]);

  // Iter 287aq — Scroll lock halaman belakang selama modal terbuka.
  useEffect(() => lockBodyScroll(), []);

  const submit = async () => {
    if (!destName.trim()) { setMsg("Produk tujuan wajib diisi"); return; }
    if (!variantId) { setMsg("Pilih varian kain"); return; }
    if (!(qtyRoll > 0)) { setMsg("Jumlah roll harus > 0"); return; }
    if (!receiver.trim()) { setMsg("Nama penerima wajib diisi"); return; }
    if (destType === "order" && !orderNo.trim()) { setMsg("Nomor nota wajib untuk tujuan Pesanan"); return; }
    setBusy(true); setMsg("");
    try {
      const body = {
        destination_type: destType,
        destination_product_name: destName.trim(),
        order_number: destType === "order" ? orderNo.trim() : "",
        variant_id: variantId, qty_roll: Number(qtyRoll),
        receiver_name: receiver.trim(), note: note.trim(),
      };
      if (isEdit) {
        await axios.patch(`${API}/stock/cut-orders/${editing.id}`, body);
      } else {
        await axios.post(`${API}/stock/cut-orders`, body);
      }
      onSaved();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal menyimpan draft");
    } finally { setBusy(false); }
  };

  return <div className="modal-backdrop cut-form-backdrop" data-testid="cut-form-modal" onClick={(e)=>{ if (e.target === e.currentTarget && !busy) onClose(); }}>
    <div className="cut-form-panel" role="dialog" aria-modal="true" aria-labelledby="cut-form-title">
      <header className="cut-form-header">
        <div className="cut-form-titles">
          <h3 id="cut-form-title" className="cut-form-title">
            {isEdit ? `Ubah Draft ${editing.doc_number}` : "Pengeluaran Kain"}
          </h3>
          <p className="cut-form-subtitle">
            {isEdit ? "Perbarui informasi penyerahan kain" : "Buat draft untuk penyerahan kain"}
          </p>
        </div>
        <button type="button" className="cut-form-close" onClick={onClose} disabled={busy}
          aria-label="Tutup" data-testid="cut-form-close">
          <X size={18}/>
        </button>
      </header>

      <div className="cut-form-body">
        {msg && <div className="cut-form-alert" data-testid="cut-form-error">{msg}</div>}

        <div className="cut-form-segment" role="tablist" aria-label="Tujuan pengeluaran">
          {[["stock","Stok Toko"],["order","Pesanan Pelanggan"]].map(([k,l]) => (
            <button key={k} type="button" role="tab" aria-selected={destType===k}
              className={`cut-form-segment-btn ${destType===k ? "active" : ""}`}
              onClick={()=>setDestType(k)}
              data-testid={`cut-dest-${k}`}>
              {destType===k && <Check size={14} aria-hidden="true"/>} <span>{l}</span>
            </button>
          ))}
        </div>

        {destType === "order" && <label className="cut-form-field">
          <span className="cut-form-label">Nomor Nota</span>
          <input className="cut-form-input" value={orderNo} onChange={e=>setOrderNo(e.target.value.toUpperCase())}
            placeholder="misal: TF-010226-001" data-testid="cut-order-no"/>
        </label>}

        <label className="cut-form-field">
          <span className="cut-form-label">Produk Tujuan</span>
          <input className="cut-form-input" value={destName} onChange={e=>setDestName(e.target.value)}
            placeholder="Contoh: Kemeja PDL, Workshirt" data-testid="cut-dest-name"/>
        </label>

        <div className="cut-form-field">
          <span className="cut-form-label">Varian Kain</span>
          {/* Iter 287az — Trigger membuka FabricVariantPicker (bottom sheet dengan search). */}
          <button type="button"
            className={`cut-form-input cut-variant-trigger ${selVariant ? "has-value" : ""}`}
            onClick={() => setVariantPickerOpen(true)}
            data-testid="cut-variant-trigger"
            aria-haspopup="dialog" aria-expanded={variantPickerOpen}>
            {selVariant ? (
              <span className="cut-variant-trigger-inner">
                <span className="cut-variant-trigger-main">
                  <strong>{selVariant.fabric_name}</strong>
                  <span className="cut-variant-trigger-meta">
                    {[selVariant.color, selVariant.brand].filter(Boolean).join(" · ") || "—"}
                  </span>
                </span>
                <span className="cut-variant-trigger-stock">
                  Sisa <b>{selVariant.current_qty}</b> {selVariant.unit || "roll"}
                </span>
              </span>
            ) : (
              <span className="cut-variant-trigger-placeholder">Pilih varian kain</span>
            )}
            <ChevronDown size={18} className="cut-form-select-caret" aria-hidden="true"/>
          </button>
        </div>

        {variantPickerOpen && (
          <FabricVariantPicker
            variants={kainVariants}
            selectedId={variantId}
            onPick={(id) => { setVariantId(id); setVariantPickerOpen(false); }}
            onClose={() => setVariantPickerOpen(false)}
          />
        )}

        <label className="cut-form-field">
          <span className="cut-form-label">Jumlah Roll</span>
          <div className="cut-form-input-wrap">
            <input className="cut-form-input cut-form-input-with-suffix" type="number"
              min="0.01" step="0.01" value={qtyRoll} onChange={e=>setQtyRoll(e.target.value)}
              data-testid="cut-qty"/>
            <span className="cut-form-input-suffix" aria-hidden="true">roll</span>
          </div>
        </label>

        <label className="cut-form-field">
          <span className="cut-form-label">Penerima</span>
          <input className="cut-form-input" value={receiver} onChange={e=>setReceiver(e.target.value)}
            placeholder="Contoh: Rido, Tim Potong" data-testid="cut-receiver"/>
        </label>

        <label className="cut-form-field">
          <span className="cut-form-label">Catatan <span className="cut-form-label-muted">(opsional)</span></span>
          <textarea className="cut-form-input cut-form-textarea" rows={3} value={note}
            onChange={e=>setNote(e.target.value)}
            placeholder="Tambahkan keterangan bila diperlukan"
            data-testid="cut-note"/>
        </label>

        <div className="cut-form-note" data-testid="cut-form-stock-note">
          <Info size={16} className="cut-form-note-icon" aria-hidden="true"/>
          <div>
            Draft belum mengurangi stok.<br/>
            Stok berkurang setelah <b>Serahkan Kain</b> ditekan.
          </div>
        </div>
      </div>

      <footer className="cut-form-footer">
        <button type="button" className="cut-form-btn cut-form-btn-cancel"
          onClick={onClose} disabled={busy} data-testid="cut-form-cancel">Batal</button>
        <button type="button" className="cut-form-btn cut-form-btn-primary"
          onClick={submit} disabled={busy} data-testid="cut-save-btn">
          {busy ? "Menyimpan…" : (isEdit ? "Simpan" : "Simpan Draft")}
        </button>
      </footer>
    </div>
  </div>;
}

// ============================================================
// TabFabricRequests — Iter 285: Antrian Permintaan Kain dari Jahit
// ============================================================
const FABREQ_STATUS_LABEL = {
  pending: {t: "Menunggu Persetujuan", c: "#a16207"},
  approving: {t: "Memproses…", c: "#a16207"},
  approved: {t: "Disetujui & Diserahkan", c: "#166534"},
  rejected: {t: "Ditolak", c: "#b91c1c"},
};

function TabFabricRequests({ variants, canMutasi, isOwner, onDone, onOpenOrder }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [approveOf, setApproveOf] = useState(null);
  const [rejectOf, setRejectOf] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock/fabric-requests`, { params: status ? { status } : {} });
      setItems(r.data?.items || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal memuat");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [status]);

  const kainVariants = useMemo(() => (variants || []).filter(v =>
    v.category === "kain" && (v.unit || "").toLowerCase() === "roll" && v.is_active
  ), [variants]);

  if (!canMutasi) {
    return <div className="warn-box small">Hanya Owner atau Gudang yang dapat melihat antrean ini.</div>;
  }

  return <div data-testid="tab-fabreq" style={{display:"flex", flexDirection:"column", gap:10}}>
    <div className="warn-box small">
      <b>Permintaan Kain dari Jahit</b>
      <div className="muted small">Tim Produksi mengajukan dari aplikasi Jahit. Permintaan masuk sebagai "Menunggu Persetujuan". Rido cek fisik lalu setujui — stok baru berkurang saat "Setujui & Serahkan".</div>
    </div>
    <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
      {[["pending","Pending"],["approved","Disetujui"],["rejected","Ditolak"],["","Semua"]].map(([k,l]) => (
        <button key={k||"all"} type="button" className="soft-btn small-btn"
          style={{background: status===k ? "var(--accent, #2563eb)" : undefined, color: status===k ? "#fff" : undefined}}
          onClick={()=>setStatus(k)} data-testid={`fabreq-filter-${k||'all'}`}>{l}</button>
      ))}
    </div>
    {msg && <div className="warn-box small">{msg}</div>}
    {loading && <div className="muted small">Memuat…</div>}
    {!loading && items.length === 0 && <div className="muted small">Antrean kosong.</div>}
    {!loading && items.map(it => (
      <FabricRequestCard key={it.id} it={it} isOwner={isOwner}
        onApprove={()=>setApproveOf(it)} onReject={()=>setRejectOf(it)}
        onOpenOrder={onOpenOrder}/>
    ))}
    {approveOf && <FabricApproveModal req={approveOf} kainVariants={kainVariants}
      onClose={()=>setApproveOf(null)} onDone={()=>{setApproveOf(null); load(); onDone && onDone();}}/>}
    {rejectOf && <FabricRejectModal req={rejectOf}
      onClose={()=>setRejectOf(null)} onDone={()=>{setRejectOf(null); load();}}/>}
  </div>;
}

function FabricRequestCard({ it, isOwner, onApprove, onReject, onOpenOrder }) {
  const st = FABREQ_STATUS_LABEL[it.status] || {t: it.status, c: "#64748b"};
  return <div className="card" style={{padding:10, borderLeft:`4px solid ${st.c}`}} data-testid={`fabreq-card-${it.id}`}>
    <div style={{display:"flex", justifyContent:"space-between", gap:8, flexWrap:"wrap"}}>
      <div style={{minWidth:0, flex:1}}>
        <div style={{fontWeight:700, fontSize:14}}>
          {it.external_id} <span className="muted small">· dari {it.requested_by}</span>
        </div>
        <div className="muted small">{new Date(it.received_at).toLocaleString("id-ID")}</div>
        <div style={{marginTop:4, fontSize:13}}>
          <b>Kain:</b> {it.fabric_type} · {it.color} · <b>{it.requested_rolls} roll</b> diminta
        </div>
        <div style={{fontSize:13}}>
          <b>Untuk:</b> {it.product_name} ({it.destination})
          {it.nota_number && <> · <button type="button" className="link-btn" onClick={()=>onOpenOrder && onOpenOrder(it.nota_number)}>{it.nota_number}</button></>}
        </div>
        {it.note && <div className="muted small">Catatan Jahit: {it.note}</div>}
        {it.status === "approved" && <div className="muted small" style={{marginTop:4}}>
          Disetujui oleh <b>{it.approved_by}</b> · {new Date(it.approved_at).toLocaleString("id-ID")}
          {it.delivered_rolls !== it.requested_rolls && <> · Diserahkan: <b>{it.delivered_rolls} roll</b></>}
          {it.discrepancy_reason && <> · Alasan: <em>{it.discrepancy_reason}</em></>}
          {it.cut_order_doc_number && <> · Perintah Potong: <b>{it.cut_order_doc_number}</b></>}
        </div>}
        {it.status === "rejected" && <div className="muted small" style={{color:"#b91c1c", marginTop:4}}>
          Ditolak oleh {it.rejected_by} — {it.rejection_reason}
        </div>}
      </div>
      <div style={{textAlign:"right"}}>
        <div style={{fontWeight:700, color:st.c, fontSize:13}} data-testid={`fabreq-status-${it.id}`}>{st.t}</div>
      </div>
    </div>
    {it.status === "pending" && <div style={{marginTop:8, display:"flex", gap:6, flexWrap:"wrap"}}>
      <button type="button" className="primary small-btn" onClick={onApprove} data-testid={`fabreq-approve-${it.id}`}>
        <Scissors size={14}/> Setujui & Serahkan
      </button>
      <button type="button" className="soft-btn small-btn" onClick={onReject}
        style={{color:"#b91c1c"}} data-testid={`fabreq-reject-${it.id}`}>Tolak</button>
    </div>}
  </div>;
}

function FabricApproveModal({ req, kainVariants, onClose, onDone }) {
  const [variantId, setVariantId] = useState("");
  const [delivered, setDelivered] = useState(req.requested_rolls);
  const [reason, setReason] = useState("");
  const [receiver, setReceiver] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // Filter varian yang cocok dgn fabric_type / color untuk mempermudah — tetap Rido pilih eksplisit.
  const suggested = useMemo(() => kainVariants.filter(v => {
    const ft = (req.fabric_type || "").toLowerCase();
    const co = (req.color || "").toLowerCase();
    return (v.fabric_name || "").toLowerCase().includes(ft.split(" ")[0]) ||
           (v.color || "").toLowerCase().includes(co.split(" ")[0]);
  }), [kainVariants, req]);
  const others = useMemo(() => kainVariants.filter(v => !suggested.includes(v)), [kainVariants, suggested]);

  const selVariant = kainVariants.find(v => v.id === variantId);
  const isDiff = Number(delivered) !== Number(req.requested_rolls);

  const submit = async () => {
    if (!variantId) { setMsg("Pilih varian kain dulu"); return; }
    if (!(delivered > 0)) { setMsg("delivered_rolls harus > 0"); return; }
    if (isDiff && !reason.trim()) { setMsg("Alasan wajib karena jumlah berbeda dari permintaan"); return; }
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/stock/fabric-requests/${req.id}/approve`, {
        variant_id: variantId,
        delivered_rolls: Number(delivered),
        discrepancy_reason: reason.trim() || undefined,
        receiver_name: receiver.trim() || undefined,
        note: note.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal menyetujui");
    } finally { setBusy(false); }
  };

  return <div className="modal-backdrop" data-testid="fabreq-approve-modal">
    <div className="modal-panel" style={{maxWidth: 560}}>
      <h3 style={{margin:"0 0 6px"}}>Setujui & Serahkan Kain — {req.external_id}</h3>
      <div className="muted small">Kain: <b>{req.fabric_type} · {req.color}</b> · diminta <b>{req.requested_rolls} roll</b> · untuk {req.product_name}</div>
      {msg && <div className="warn-box small">{msg}</div>}
      <label className="modal-field">Cocokkan Varian Stok
        <select value={variantId} onChange={e=>setVariantId(e.target.value)} data-testid="fabreq-approve-variant">
          <option value="">— pilih varian —</option>
          {suggested.length > 0 && <optgroup label="Saran match">
            {suggested.map(v => <option key={v.id} value={v.id}>
              {v.fabric_name}{v.brand?` · ${v.brand}`:""} · {v.color} · sisa {v.current_qty} roll
            </option>)}
          </optgroup>}
          <optgroup label="Semua varian kain">
            {others.map(v => <option key={v.id} value={v.id}>
              {v.fabric_name}{v.brand?` · ${v.brand}`:""} · {v.color} · sisa {v.current_qty} roll
            </option>)}
          </optgroup>
        </select>
      </label>
      {selVariant && <div className="muted small">Sisa stok: <b>{selVariant.current_qty} roll</b></div>}
      <label className="modal-field">Jumlah Diserahkan (roll)
        <input type="number" min="0.01" step="0.01" value={delivered}
          onChange={e=>setDelivered(e.target.value)} data-testid="fabreq-approve-delivered"/>
      </label>
      {isDiff && <label className="modal-field">Alasan berbeda dari permintaan <span style={{color:"#b91c1c"}}>*</span>
        <input value={reason} onChange={e=>setReason(e.target.value)}
          placeholder="mis: sisa roll rusak, ada penyusutan"
          data-testid="fabreq-approve-reason"/>
      </label>}
      <label className="modal-field">Nama Penerima (opsional, default: nama Anda)
        <input value={receiver} onChange={e=>setReceiver(e.target.value)} placeholder="mis: Rido"/>
      </label>
      <label className="modal-field">Catatan tambahan
        <textarea rows={2} value={note} onChange={e=>setNote(e.target.value)}/>
      </label>
      <div className="modal-actions">
        <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
        <button type="button" className="primary" onClick={submit} disabled={busy || !variantId}
          data-testid="fabreq-approve-confirm">
          {busy ? "Memproses…" : `Setujui & Serahkan (${delivered} roll)`}
        </button>
      </div>
      <p className="muted small" style={{marginTop:6}}>Stok akan berkurang <b>tepat sekali</b> saat tombol ini ditekan, dan Perintah Potong otomatis dikirim ke Jahit dengan <code>request_id={req.external_id}</code>.</p>
    </div>
  </div>;
}

function FabricRejectModal({ req, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async () => {
    if (!reason.trim()) { setMsg("Alasan wajib"); return; }
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/stock/fabric-requests/${req.id}/reject`, { reason: reason.trim() });
      onDone();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal menolak");
    } finally { setBusy(false); }
  };
  return <div className="modal-backdrop" data-testid="fabreq-reject-modal">
    <div className="modal-panel" style={{maxWidth: 460}}>
      <h3 style={{margin:"0 0 6px"}}>Tolak Permintaan — {req.external_id}</h3>
      {msg && <div className="warn-box small">{msg}</div>}
      <label className="modal-field">Alasan Penolakan
        <textarea rows={3} value={reason} onChange={e=>setReason(e.target.value)}
          placeholder="mis: bahan sedang habis, warna beda dari permintaan"
          data-testid="fabreq-reject-reason"/>
      </label>
      <div className="modal-actions">
        <button type="button" className="soft-btn" onClick={onClose} disabled={busy}>Batal</button>
        <button type="button" className="primary" onClick={submit} disabled={busy}
          style={{background:"#b91c1c"}} data-testid="fabreq-reject-confirm">
          {busy ? "Menolak…" : "Tolak Permintaan"}
        </button>
      </div>
    </div>
  </div>;
}
